/* =========================================================================
   export-excel.ts — Investment-grade Excel workbook generation (exceljs)

   Every non-input cell is a formula. Cross-sheet refs link Assumptions
   to all calculation sheets. Output sheets (Cover, KPIs) reference calcs.

   Colour convention (investment banking standard):
     • Blue font  = hardcoded input / assumption
     • Black font = formula / calculated
     • Green font = cross-sheet link
     • Yellow fill = key driver assumption
     • Navy fill   = section header
     • Grey fill   = subtotal / total row
   ========================================================================= */

import type { Workbook } from "exceljs";
import type {
  Scenario,
  ModelResult,
  Year,
  Assumptions,
  Building,
  KPIs,
  Developer,
  MonthlyResult,
  CheckResult,
} from "./types";
import { Model, exitLeaseYearFor } from "./model";
import {
  renderSparklineSvg,
  svgElementToPngDataUrl,
  renderCashflowSvg,
  renderPnLSvg,
  renderTornadoSvg,
} from "./chart-export";
import * as Census from "./census";
import {
  GEARED_REFERENCE_DISCLOSURE,
  getOperatorDealRecommendation,
  getOperatorManagementPosture,
} from "./decision-metrics";
import { OPERATOR_AFFORDABILITY_CEILING } from "./decision-thresholds";
import { resolveCostAllowance } from "./developer-cost-basis";
import {
  BOARD_COST_BASIS_LABELS,
  BOARD_COST_STATUS_LABELS,
  buildBoardCostViewModel,
} from "./presentation/board-cost-view-model";
import {
  CAPACITY_LABELS,
  resolveEnrolmentCapacityBasis,
  scheduledClassroomSeatValue,
  scheduledClassroomSeatsForYear,
} from "./presentation/capacity-view-model";
import { sameProjectScenarios } from "./presentation/scenario-identity";
import {
  gearedRefinanceExitLabel,
  INPUT_DEBT_CAP_LABEL,
  CONSTRUCTION_DEBT_CAP_INPUT_FOOTNOTE,
  OPERATOR_CONTRIBUTION_BOARD_NOTE,
  GROSS_DEBT_CAPACITY_LABEL,
  ACTUAL_CONSTRUCTION_DEBT_LABEL,
  grossDebtCapacityFootnote,
  actualConstructionDebtFootnote,
  rentBridgeExitBasisLabel,
  PEAK_CONSTRUCTION_DEBT_LABEL,
  DEVELOPER_EQUITY_LABEL,
  TERM_LOAN_LIMIT_LABEL,
  TERM_LOAN_SUFFICIENCY_LABEL,
  TERM_LOAN_SUFFICIENCY_PASS_LABEL,
  TERM_LOAN_SUFFICIENCY_WARN_LABEL,
  TERM_LOAN_SUFFICIENT_FOOTNOTE,
  termLoanLimitFootnote,
  termLoanShortfallFootnote,
  DEVELOPER_TERM_LOAN_DSCR_LABEL,
  DSCR_NOT_APPLICABLE_LABEL,
  dscrDebtFacilityFootnote,
  EQUITY_REQUIRED_LABEL,
  matchedUngearedReturnLabel,
  matchedGearedReferenceLabel,
  ALL_IN_COST_LABEL,
  ALL_IN_COST_SHORT_LABEL,
  NOI_ON_DEVELOPER_EQUITY_LABEL,
  NOI_ON_DEVELOPER_EQUITY_DEFINITION,
  INSPIRED_REDDAM_CONTRIBUTION_LABEL,
  BEFORE_INSPIRED_REDDAM_CONTRIBUTION_FOOTNOTE,
  TOTAL_FUNDING_SOURCES_LABEL,
  SOURCES_LESS_USES_DIFFERENCE_LABEL,
  STABILISED_YEAR1_NOI_LABEL,
  CONSTRUCTION_INTEREST_LABEL,
  LINE_FEE_ONE_OFF_LABEL,
  GROSS_TOTAL_DEVELOPMENT_COST_LABEL,
  GROSS_TDC_LABEL,
  GROSS_TDC_USES_LABEL,
  YIELD_ON_COST_LABEL,
  EXIT_CAP_RATE_LABEL,
  DEVELOPMENT_SPREAD_LABEL,
  DEVELOPER_BOARD_YIELD_ON_GROSS_TDC_FOOTNOTE,
  RENT_PCT_REVENUE_SENSITIVITY_HEADER,
  UNGEARED_RETURN_SENSITIVITY_HEADER,
  GEARED_RETURN_SENSITIVITY_HEADER,
  RAMP_RESERVES_REQUIRED_LABEL,
  MARGIN_ON_COST_LABEL,
  DEVELOPER_PROFIT_LABEL,
  OPERATOR_NPV_LABEL,
  OPERATOR_IRR_LABEL,
  OPERATOR_PAYBACK_LABEL,
  RENT_PCT_REVENUE_Y1_LABEL,
  RENT_PCT_REVENUE_AVG_LABEL,
  RENT_PCT_REVENUE_STEADY_LABEL,
  Y1_EBITDA_LABEL,
  AVG_EBITDA_LABEL,
  BREAK_EVEN_RENT_Y1_LABEL,
  EFFECTIVE_OCCUPANCY_COST_Y1_ROW_LABEL,
  EFFECTIVE_OCCUPANCY_COST_RATIO_EXCEL_LABEL,
  EFFECTIVE_OCCUPANCY_COST_BASIS_NOTE,
} from "./presentation/return-labels";
import { buildDealRisks } from "./deal-risks";
import {
  C,
  NF,
  styleHeader,
  styleInput,
  styleFormula,
  styleLink,
  styleLabel,
  styleNote,
  stylePass,
  styleWarn,
  styleFail,
  styleTotal,
  thinBorder,
  thinBorderRow,
  autoNumFmt,
  setFormula,
  normalizeFormulaExpression,
  fmtCompactMoney,
  fmtPct,
  addr,
  AU_DATE,
} from "./excel/shared";

function formatSubtitleCurrency(value: number): string {
  return `$${Math.round(value).toLocaleString("en-AU")}`;
}

function formatSubtitlePercent(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}
export {
  C,
  NF,
  styleHeader,
  styleInput,
  styleFormula,
  styleLink,
  styleLabel,
  styleNote,
  stylePass,
  styleWarn,
  styleFail,
  styleTotal,
  thinBorder,
  thinBorderRow,
  autoNumFmt,
  setFormula,
  fmtCompactMoney,
  fmtMoney,
  fmtPct,
  addr,
  AU_DATE,
} from "./excel/shared";

/** A5: single exit predicate — developerExitYear is PROJECT years from T0, and an early exit is
 *  one before the model horizon. Previously re-derived in four sheet builders. */
export function exitBasisFor(
  a: Pick<Assumptions, "developerExitYear" | "modelYears">,
): {
  exitYear: number;
  earlyExit: boolean;
} {
  const exitYear = a.developerExitYear ?? a.modelYears;
  return { exitYear, earlyExit: exitYear < a.modelYears };
}

export function buildCostAndYield(
  wb: Workbook,
  scenario: Scenario,
  result: ModelResult,
  refs: Map<string, string>,
  devCost: ReturnType<typeof buildDevCost>,
) {
  const ws = wb.addWorksheet("Cost & Yield");
  const board = buildBoardCostViewModel(scenario, result);
  const d = result.developer;
  const grossDebtShare =
    d.totalCost > 0 ? d.grossFacilityLimitDev / d.totalCost : 0;
  const actualDebtShare =
    d.totalCost > 0 ? d.facilityLimitDev / d.totalCost : 0;
  const constructionLvrRef = refs.get("constructionLvrRate");
  ws.views = [{ state: "frozen", xSplit: 1, ySplit: 3 }];

  ws.getCell(1, 1).value = "DEVELOPMENT COST & YIELD";
  styleHeader(ws.getCell(1, 1));
  ws.mergeCells("A1:F1");
  ws.getCell(2, 1).value =
    "Board presentation - model totals and live formula links remain distinct from comparison evidence.";
  styleNote(ws.getCell(2, 1));
  ws.mergeCells("A2:F2");

  const headers = [
    "Scope / building",
    "Area sqm",
    "Base cost",
    "Loading basis",
    "Scope state",
    "Source / as at",
  ];
  headers.forEach((label, index) => {
    const cell = ws.getCell(3, index + 1);
    cell.value = label;
    styleHeader(cell);
    cell.alignment = { vertical: "middle", wrapText: true };
  });
  ws.getRow(3).height = 30;
  let row = 4;
  for (const item of board.costSchedule) {
    const values = [
      item.label,
      item.areaSqm ?? null,
      item.amount,
      BOARD_COST_BASIS_LABELS[item.basis],
      BOARD_COST_STATUS_LABELS[item.status],
      `${item.source ?? "Source not available"}${item.asAt ? ` / ${item.asAt}` : ""}`,
    ];
    values.forEach((value, index) => {
      const cell = ws.getCell(row, index + 1);
      cell.value = value;
      if (index === 2 && typeof value === "number") cell.numFmt = NF.currency;
      if (index === 1 && typeof value === "number") cell.numFmt = NF.num;
      if ([0, 3, 4, 5].includes(index)) {
        cell.alignment = { vertical: "top", wrapText: true };
      }
    });
    ws.getRow(row).height = 30;
    row++;
  }

  const section = (label: string) => {
    ws.getCell(row, 1).value = label;
    styleHeader(ws.getCell(row, 1));
    ws.mergeCells(`A${row}:F${row}`);
    row++;
  };
  const linked = (
    label: string,
    formula: string,
    resultValue: number,
    numFmt: string = NF.currency,
    note?: string,
  ) => {
    ws.getCell(row, 1).value = label;
    styleLabel(ws.getCell(row, 1));
    setFormula(ws.getCell(row, 3), formula, resultValue);
    styleLink(ws.getCell(row, 3));
    ws.getCell(row, 3).numFmt = numFmt;
    if (note) {
      ws.getCell(row, 4).value = note;
      styleNote(ws.getCell(row, 4));
    }
    row++;
    return row - 1;
  };

  if (board.sourceBasis && board.constructionBridge) {
    section("ADOPTED SOURCE COST");
    const sourceRows: Array<[string, string | number, string]> = [
      [
        "Evidence source",
        board.sourceBasis.sourceName,
        board.sourceBasis.sourceDate,
      ],
      [
        "Loaded building works",
        board.sourceBasis.buildingWorks,
        `${board.sourceBasis.sourceBuildingAreaSqm.toLocaleString("en-AU")} sqm source area`,
      ],
      [
        "Loaded site / outdoor works",
        board.sourceBasis.siteWorks,
        "First-class absolute cost",
      ],
      [
        "Adopted construction",
        board.sourceBasis.adoptedConstruction,
        "Fixed source total",
      ],
      [
        "Scenario building area",
        board.sourceBasis.scenarioBuildingAreaSqm,
        "Used provisionally for phasing allocation only",
      ],
      [
        "GST / escalation",
        `${board.sourceBasis.gstStatus} / ${board.sourceBasis.escalationStatus}`,
        "GST remains a disclosed residual",
      ],
    ];
    for (const [label, value, note] of sourceRows) {
      ws.getCell(row, 1).value = label;
      styleLabel(ws.getCell(row, 1));
      ws.getCell(row, 3).value = value;
      if (typeof value === "number") {
        ws.getCell(row, 3).numFmt = /area/i.test(label) ? NF.num : NF.currency;
      }
      ws.getCell(row, 4).value = note;
      styleNote(ws.getCell(row, 4));
      row++;
    }
    for (const loading of board.sourceBasis.embeddedLoadings) {
      ws.getCell(row, 1).value = `${loading.label} (embedded)`;
      styleLabel(ws.getCell(row, 1));
      ws.getCell(row, 3).value = loading.rate;
      ws.getCell(row, 3).numFmt = NF.pct2;
      ws.getCell(row, 4).value = "Disclosure only - not added again";
      styleNote(ws.getCell(row, 4));
      row++;
    }

    section("CONSTRUCTION COST BRIDGE");
    const bridgeRows = [
      {
        label: "Adopted construction cost",
        amount: board.constructionBridge.adoptedTotal,
      },
      ...board.constructionBridge.rows,
      {
        label: "Upper-bound comparator",
        amount: board.constructionBridge.upperBoundTotal,
      },
    ];
    for (const bridge of bridgeRows) {
      ws.getCell(row, 1).value = bridge.label;
      styleLabel(ws.getCell(row, 1));
      ws.getCell(row, 3).value = bridge.amount;
      ws.getCell(row, 3).numFmt = NF.currency;
      row++;
    }

    section("SCOPE / DOUBLE-COUNT REGISTER");
    ["Scope", "Classification", "Model mapping", "Disclosure"].forEach(
      (label, index) => {
        ws.getCell(row, index + 1).value = label;
        styleHeader(ws.getCell(row, index + 1));
      },
    );
    row++;
    for (const item of board.scopeRegister) {
      ws.getCell(row, 1).value = item.label;
      ws.getCell(row, 2).value = item.classification;
      ws.getCell(row, 3).value = item.mapping;
      ws.getCell(row, 4).value = item.note;
      for (let column = 1; column <= 4; column++) {
        ws.getCell(row, column).alignment = {
          vertical: "top",
          wrapText: true,
        };
      }
      row++;
    }
  }

  section("HEADLINE ECONOMICS");
  const grossTdcRow = linked(
    GROSS_TOTAL_DEVELOPMENT_COST_LABEL,
    `='Dev Cost'!B${devCost.totalCostRow}`,
    board.headline.grossTdc,
    NF.currency,
    BEFORE_INSPIRED_REDDAM_CONTRIBUTION_FOOTNOTE,
  );
  const noiRow = linked(
    STABILISED_YEAR1_NOI_LABEL,
    `='Dev Cost'!B${devCost.noiRow}`,
    board.headline.stabilisedYear1Noi,
  );
  const yieldOnCostRow = linked(
    YIELD_ON_COST_LABEL,
    `=C${noiRow}/C${grossTdcRow}`,
    board.headline.yieldOnCost,
    NF.pct2,
    DEVELOPER_BOARD_YIELD_ON_GROSS_TDC_FOOTNOTE,
  );
  const capRateRef = refs.get("capRate");
  const exitCapRow = linked(
    EXIT_CAP_RATE_LABEL,
    capRateRef ? `=${capRateRef}` : `=${board.headline.exitCapRate}`,
    board.headline.exitCapRate,
    NF.pct2,
  );
  linked(
    DEVELOPMENT_SPREAD_LABEL,
    `=C${yieldOnCostRow}-C${exitCapRow}`,
    board.headline.developmentSpread,
    NF.pct2,
  );

  section("SOURCES AND USES");
  linked(GROSS_TDC_USES_LABEL, `=C${grossTdcRow}`, board.funding.grossTdc);
  const contributionRef = refs.get("reddamCapexContribution");
  const contributionRow = linked(
    INSPIRED_REDDAM_CONTRIBUTION_LABEL,
    contributionRef ? `=${contributionRef}` : `=${board.funding.contribution}`,
    board.funding.contribution,
    NF.currency,
    OPERATOR_CONTRIBUTION_BOARD_NOTE,
  );
  linked(
    INPUT_DEBT_CAP_LABEL,
    constructionLvrRef
      ? `=${constructionLvrRef}`
      : `=${scenario.assumptions.constructionLvrRate}`,
    scenario.assumptions.constructionLvrRate,
    NF.pct,
    CONSTRUCTION_DEBT_CAP_INPUT_FOOTNOTE,
  );
  linked(
    GROSS_DEBT_CAPACITY_LABEL,
    `='Dev Cost'!B${devCost.grossFacilityLimitRow}`,
    board.funding.grossDebtCapacity,
    NF.currency,
    grossDebtCapacityFootnote(fmtPct(grossDebtShare)),
  );
  const debtRow = linked(
    ACTUAL_CONSTRUCTION_DEBT_LABEL,
    `='Dev Cost'!B${devCost.facilityLimitRow}`,
    board.funding.constructionDebt,
    NF.currency,
    actualConstructionDebtFootnote(fmtPct(actualDebtShare)),
  );
  linked(
    PEAK_CONSTRUCTION_DEBT_LABEL,
    `='Dev Cost'!B${devCost.peakDebtRow}`,
    board.funding.peakDebt,
  );
  const equityRow = linked(
    DEVELOPER_EQUITY_LABEL,
    `='Dev Cost'!B${devCost.equityReqRow}`,
    board.funding.developerEquity,
  );
  const sourcesRow = linked(
    TOTAL_FUNDING_SOURCES_LABEL,
    `=C${contributionRow}+C${debtRow}+C${equityRow}`,
    board.funding.totalSources,
  );
  linked(
    SOURCES_LESS_USES_DIFFERENCE_LABEL,
    `=C${sourcesRow}-C${grossTdcRow}`,
    board.funding.difference,
    // loop#003: the funding check fails at |difference| > $0.01 — show cents on this row.
    NF.currencyCents,
  );
  linked(
    ALL_IN_COST_LABEL,
    `='Dev Cost'!B${devCost.allInCostRow}`,
    board.funding.allInCost,
  );
  const facilityBalanceFormula = `('Dev Cost'!B${devCost.facilityLimitRow}+'Dev Cost'!B${devCost.lineFeeRow}+'Dev Cost'!B${devCost.constructionInterestRow})`;
  const termLoanSufficient =
    d.termLoanLimit >= d.facilityLimitDev + d.debtCostDev;
  linked(
    TERM_LOAN_LIMIT_LABEL,
    `='Dev Cost'!B${devCost.termLoanLimitRow}`,
    d.termLoanLimit,
    NF.currency,
    termLoanLimitFootnote(
      `${(scenario.assumptions.termLoanLvrRate * 100).toFixed(0)}%`,
    ),
  );
  ws.getCell(row, 1).value = TERM_LOAN_SUFFICIENCY_LABEL;
  styleLabel(ws.getCell(row, 1));
  setFormula(
    ws.getCell(row, 3),
    `=IF('Dev Cost'!B${devCost.termLoanLimitRow}>=${facilityBalanceFormula},"${TERM_LOAN_SUFFICIENCY_PASS_LABEL}","${TERM_LOAN_SUFFICIENCY_WARN_LABEL}")`,
    termLoanSufficient
      ? TERM_LOAN_SUFFICIENCY_PASS_LABEL
      : TERM_LOAN_SUFFICIENCY_WARN_LABEL,
  );
  styleLink(ws.getCell(row, 3));
  ws.getCell(row, 4).value = termLoanSufficient
    ? TERM_LOAN_SUFFICIENT_FOOTNOTE
    : termLoanShortfallFootnote(
        fmtCompactMoney(d.facilityLimitDev + d.debtCostDev - d.termLoanLimit),
      );
  styleNote(ws.getCell(row, 4));
  row++;

  if (d.dscrStatus === "na") {
    ws.getCell(row, 1).value = DEVELOPER_TERM_LOAN_DSCR_LABEL;
    styleLabel(ws.getCell(row, 1));
    ws.getCell(row, 3).value = "—";
    styleLink(ws.getCell(row, 3));
    ws.getCell(row, 4).value = DSCR_NOT_APPLICABLE_LABEL;
    styleNote(ws.getCell(row, 4));
    row++;
  } else {
    linked(
      DEVELOPER_TERM_LOAN_DSCR_LABEL,
      `=IFERROR('Dev Cost'!B${devCost.noiRow}/'Dev Cost'!B${devCost.termLoanAnnualServiceRow},0)`,
      d.dscr,
      NF.multiple,
      dscrDebtFacilityFootnote(
        fmtCompactMoney(d.noi),
        fmtCompactMoney(d.termLoanAnnualService),
      ),
    );
  }

  if (board.sensitivities.length > 0) {
    section("FIXED COST & CONTRIBUTION SENSITIVITIES - DECISION METRICS");
    const decisionHeaders = [
      "Case",
      "Construction",
      "Timing",
      "Rent Y1",
      RENT_PCT_REVENUE_SENSITIVITY_HEADER,
      OPERATOR_NPV_LABEL,
      UNGEARED_RETURN_SENSITIVITY_HEADER,
      GEARED_RETURN_SENSITIVITY_HEADER,
      YIELD_ON_COST_LABEL,
    ];
    decisionHeaders.forEach((label, index) => {
      ws.getCell(row, index + 1).value = label;
      styleHeader(ws.getCell(row, index + 1));
    });
    row++;
    for (const item of board.sensitivities) {
      const values = [
        item.label,
        item.constructionCost,
        item.contributionTiming,
        item.rentYear1,
        item.rentPctRevenueYear1,
        item.operatorNpv,
        item.developerUngearedReturn,
        item.developerGearedReturn,
        item.yieldOnCost,
      ];
      values.forEach((value, index) => {
        ws.getCell(row, index + 1).value = value;
        if ([1, 3, 5].includes(index)) {
          ws.getCell(row, index + 1).numFmt = NF.currency;
        }
        if ([4, 6, 7, 8].includes(index)) {
          ws.getCell(row, index + 1).numFmt = NF.pct2;
        }
      });
      row++;
    }

    section("FIXED COST & CONTRIBUTION SENSITIVITIES - FUNDING");
    const fundingHeaders = [
      "Case",
      DEVELOPER_EQUITY_LABEL,
      GROSS_DEBT_CAPACITY_LABEL,
      ACTUAL_CONSTRUCTION_DEBT_LABEL,
      PEAK_CONSTRUCTION_DEBT_LABEL,
      CONSTRUCTION_INTEREST_LABEL,
      LINE_FEE_ONE_OFF_LABEL,
      GROSS_TDC_LABEL,
      ALL_IN_COST_SHORT_LABEL,
    ];
    fundingHeaders.forEach((label, index) => {
      ws.getCell(row, index + 1).value = label;
      styleHeader(ws.getCell(row, index + 1));
    });
    row++;
    for (const item of board.sensitivities) {
      const values = [
        item.label,
        item.developerEquity,
        item.grossDebtCapacity,
        item.actualDebt,
        item.peakDebt,
        item.constructionInterest,
        item.lineFee,
        item.grossTdc,
        item.allInCost,
      ];
      values.forEach((value, index) => {
        ws.getCell(row, index + 1).value = value;
        if (index > 0) ws.getCell(row, index + 1).numFmt = NF.currency;
      });
      row++;
    }
    ws.getCell(row, 1).value =
      "Developer returns are counterparty diagnostics only. Rent is held at the scenario ask in every case.";
    styleNote(ws.getCell(row, 1));
    ws.mergeCells(`A${row}:I${row}`);
    row++;
  }

  section("DEVELOPER COUNTERPARTY DIAGNOSTICS");
  linked(
    matchedUngearedReturnLabel(board.diagnostics.horizon.label),
    `='Dev Cost'!B${devCost.primaryIrrRow}`,
    board.diagnostics.matchedUngearedReturn,
    NF.pct2,
  );
  const matchedGearedRow =
    devCost.gearedEarlyExitIrrRow > 0
      ? devCost.gearedEarlyExitIrrRow
      : devCost.gearedIrrRow;
  linked(
    matchedGearedReferenceLabel(board.diagnostics.horizon.label),
    `='Dev Cost'!B${matchedGearedRow}`,
    board.diagnostics.matchedGearedReturn,
    NF.pct2,
  );
  linked(
    ACTUAL_CONSTRUCTION_DEBT_LABEL,
    `='Dev Cost'!B${devCost.facilityLimitRow}`,
    board.diagnostics.developerDebt,
  );
  linked(
    DEVELOPER_EQUITY_LABEL,
    `='Dev Cost'!B${devCost.equityReqRow}`,
    board.diagnostics.developerEquity,
  );
  linked(
    "Term loan annual service",
    `='Dev Cost'!B${devCost.termLoanAnnualServiceRow}`,
    result.developer.termLoanAnnualService,
  );
  linked(
    DEVELOPER_PROFIT_LABEL,
    `='Dev Cost'!B${devCost.profitRow}`,
    board.diagnostics.profit,
  );
  linked(
    MARGIN_ON_COST_LABEL,
    `='Dev Cost'!B${devCost.marginRow}`,
    board.diagnostics.marginOnCost,
    NF.pct2,
  );
  linked(
    NOI_ON_DEVELOPER_EQUITY_LABEL,
    `='Dev Cost'!B${devCost.yieldCashRow}`,
    board.diagnostics.noiOnDeveloperEquity.value,
    NF.pct2,
    NOI_ON_DEVELOPER_EQUITY_DEFINITION,
  );

  ws.columns = [
    { width: 32 },
    { width: 14 },
    { width: 18 },
    { width: 26 },
    { width: 16 },
    { width: 38 },
    { width: 16 },
    { width: 16 },
    { width: 16 },
  ];
  for (let sheetRow = 3; sheetRow < row; sheetRow++) {
    for (const column of [1, 4, 5, 6]) {
      const cell = ws.getCell(sheetRow, column);
      cell.alignment = {
        ...cell.alignment,
        vertical: cell.alignment?.vertical ?? "top",
        wrapText: true,
      };
    }
  }
  const lastWrittenRow = row - 1;
  ws.pageSetup = {
    orientation: "landscape",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    printArea: `A1:I${lastWrittenRow}`,
    printTitlesRow: "3:3",
    horizontalCentered: true,
    margins: {
      left: 0.25,
      right: 0.25,
      top: 0.5,
      bottom: 0.5,
      header: 0.2,
      footer: 0.2,
    },
  };
  return { grossTdcRow, noiRow, sourcesRow };
}

/* ── Assumptions sheet builder ──
   Returns a map: assumptionKey -> "'Assumptions'!$B$4" for cross-sheet refs */
export function buildAssumptions(
  wb: Workbook,
  a: Assumptions,
): Map<string, string> {
  const ws = wb.addWorksheet("Assumptions");
  ws.views = [{ state: "frozen", ySplit: 1 }];

  const refs = new Map<string, string>();
  let r = 1;
  const percentageKeys = new Set([
    "holdingInterestRate",
    "discountRate",
    "developerHurdleIRR",
    "capRate",
    "stampDutyRate",
    "acquisitionFeeRate",
    "preliminariesRate",
    "builderMarginRate",
    "consultantCostRate",
    "statutoryCostRate",
    "developerContingencyRate",
    "agentsFeeRate",
    "devManagementFeeRate",
    "earlyLearningShare",
    "primaryShare",
    "secondaryShare",
    "seniorShare",
    "tuitionGrowthPct",
    "foundingFamilyDiscount",
    "siblingDiscount",
    "startingUtilisation",
    "steadyStateUtilisation",
    "rentIndexationPct",
    "cpiAssumption",
    "marketReviewPct",
    "fixedOpexGrowth",
    "ancillaryGrowthPct",
    "constructionLvrRate",
    "constructionMarginRate",
    "constructionLineFeeRate",
    "termLoanLvrRate",
    "termLoanMarginRate",
    "performanceBondPct",
    "externalWorksPct",
    "approvalCostReddamSharePct",
    "corporateTaxRate",
    "contingencyInputRate",
    "professionalFeesInputRate",
    "developmentFeeInputRate",
  ]);
  const listValues = new Map<string, string>([
    ["rentEscalationMethod", "fixed,cpi,market"],
    ["useScurveDrawdown", "Enabled,Disabled (50% avg drawn)"],
    ["equityReturnFormulation", "perpetuity,amortised"],
    ["siteDealAllocationStatus", "unverified,negotiating,agreed"],
    ["developerCostBasisMode", "legacy_net_trade,loaded_aggregate"],
    ["loadedCostGstStatus", "unverified,inclusive,exclusive"],
    ["contingencyInputMode", "rate,amount"],
    ["professionalFeesInputMode", "rate,amount"],
    ["developmentFeeInputMode", "rate,amount"],
    ["contributionTiming", "upfront,progressive,completion"],
  ]);

  const title = (t: string) => {
    ws.getCell(r, 1).value = t;
    ws.getCell(r, 1).font = {
      bold: true,
      size: 14,
      color: { argb: C.headerBg },
      name: "Arial",
    };
    r++;
  };

  const section = (t: string) => {
    ws.getCell(r, 1).value = t;
    ws.getCell(r, 1).font = {
      bold: true,
      color: { argb: C.headerText },
      name: "Arial",
      size: 10,
    };
    ws.getCell(r, 1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.headerBg },
    };
    ws.getCell(r, 2).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.headerBg },
    };
    thinBorderRow(ws, r, 1, 2);
    r++;
  };

  const kv = (
    label: string,
    value: string | number | boolean,
    key: string,
    isKey = false,
  ) => {
    ws.getCell(r, 1).value = label;
    styleLabel(ws.getCell(r, 1));
    ws.getCell(r, 2).value = value;
    styleInput(ws.getCell(r, 2), isKey);
    thinBorderRow(ws, r, 1, 2);
    const nf = autoNumFmt(label, value, key);
    if (nf && typeof value === "number") ws.getCell(r, 2).numFmt = nf;
    ws.getCell(r, 2).alignment = { horizontal: "right" };
    const list = listValues.get(key);
    if (list) {
      ws.getCell(r, 2).dataValidation = {
        type: "list",
        allowBlank: false,
        formulae: [`"${list}"`],
        showErrorMessage: true,
        errorTitle: "Select a listed value",
        error: "Choose one of the permitted model inputs.",
      };
    } else if (percentageKeys.has(key) && typeof value === "number") {
      ws.getCell(r, 2).dataValidation = {
        type: "decimal",
        operator: "between",
        allowBlank: false,
        formulae: [0, 1],
        showErrorMessage: true,
        errorTitle: "Enter a percentage",
        error: "Enter a value between 0% and 100%.",
      };
    }
    refs.set(key, `'Assumptions'!$B$${r}`);
    r++;
  };

  const kvFormula = (
    label: string,
    formula: string,
    result: number,
    key: string,
    numFmt: string,
  ) => {
    ws.getCell(r, 1).value = label;
    styleLabel(ws.getCell(r, 1));
    setFormula(ws.getCell(r, 2), formula, result, { numFmt });
    ws.getCell(r, 2).alignment = { horizontal: "right" };
    refs.set(key, `'Assumptions'!$B$${r}`);
    r++;
  };

  const blank = () => {
    r++;
  };

  title("MODEL ASSUMPTIONS");
  blank();

  section("PROJECT");
  kv("Project name", a.projectName, "projectName");
  kv("Operator name", a.operatorName, "operatorName");
  kv("Campus name", a.campusName, "campusName");
  kv("Address", a.address, "address");
  blank();

  section("TERM & FINANCING");
  kv("Lease term (years)", a.leaseTermYears, "leaseTermYears", true);
  kv("Model years", a.modelYears, "modelYears");
  kv("Development months", a.developmentMonths, "developmentMonths", true);
  kv(
    "Holding interest rate",
    a.holdingInterestRate,
    "holdingInterestRate",
    true,
  );
  kv("Operator discount rate", a.discountRate, "discountRate", true);
  kv("Developer hurdle IRR", a.developerHurdleIRR, "developerHurdleIRR", true);
  kv("Cap rate (exit)", a.capRate, "capRate", true);
  kv(
    "Minimum cash reserve (months)",
    a.minimumCashReserveMonths,
    "minimumCashReserveMonths",
  );
  blank();

  section("ACQUISITION & DEVELOPMENT");
  kv("Purchase price", a.purchasePrice, "purchasePrice", true);
  kv("Full site price", a.fullSitePrice ?? 113_000_000, "fullSitePrice");
  kv(
    "Campus parcel allocation (low)",
    a.campusParcelAllocationLow ?? 113_000_000,
    "campusParcelAllocationLow",
  );
  kv(
    "Campus parcel allocation (high)",
    a.campusParcelAllocationHigh ?? 113_000_000,
    "campusParcelAllocationHigh",
  );
  kv("Stamp duty rate", a.stampDutyRate, "stampDutyRate");
  kv("Acquisition fee rate", a.acquisitionFeeRate, "acquisitionFeeRate");
  kv("Preliminaries rate", a.preliminariesRate, "preliminariesRate");
  kv("Builder margin rate", a.builderMarginRate, "builderMarginRate");
  kv("Consultant cost rate", a.consultantCostRate, "consultantCostRate");
  kv("Statutory cost rate", a.statutoryCostRate, "statutoryCostRate");
  kv(
    "Planning approval costs",
    a.planningApprovalCosts,
    "planningApprovalCosts",
  );
  kv(
    "Developer contingency rate",
    a.developerContingencyRate,
    "developerContingencyRate",
  );
  kv("Agents fee rate", a.agentsFeeRate, "agentsFeeRate");
  kv("Dev management fee rate", a.devManagementFeeRate, "devManagementFeeRate");
  kv("Other DD costs", a.otherDdCosts, "otherDdCosts");
  kv(
    "Tenant relocation costs",
    a.tenantRelocationCosts,
    "tenantRelocationCosts",
  );
  blank();

  section("OPERATOR REVENUE");
  kv("Fee per student Y1", a.feePerStudentYear1, "feePerStudentYear1", true);
  kv("Early learning fee Y1", a.earlyLearningFeeYear1, "earlyLearningFeeYear1");
  kv("Primary fee Y1", a.primaryFeeYear1, "primaryFeeYear1");
  kv("Secondary fee Y1", a.secondaryFeeYear1, "secondaryFeeYear1");
  kv("Senior fee Y1", a.seniorFeeYear1, "seniorFeeYear1");
  kv("Early learning share", a.earlyLearningShare, "earlyLearningShare");
  kv("Primary share", a.primaryShare, "primaryShare");
  kv("Secondary share", a.secondaryShare, "secondaryShare");
  kv("Senior share", a.seniorShare, "seniorShare");
  kv("Tuition growth p.a.", a.tuitionGrowthPct, "tuitionGrowthPct", true);
  kv(
    "Founding family discount",
    a.foundingFamilyDiscount,
    "foundingFamilyDiscount",
  );
  kv("Sibling discount", a.siblingDiscount, "siblingDiscount");
  kv("Discount decay years", a.discountDecayYears, "discountDecayYears");
  kv("Ramp years", a.rampYears, "rampYears", true);
  kv(
    "Starting utilisation",
    a.startingUtilisation,
    "startingUtilisation",
    true,
  );
  kv(
    "Steady-state utilisation",
    a.steadyStateUtilisation,
    "steadyStateUtilisation",
    true,
  );
  kv("Rent indexation p.a.", a.rentIndexationPct, "rentIndexationPct");
  kv(
    "Rent escalation method",
    a.rentEscalationMethod ?? "fixed",
    "rentEscalationMethod",
  );
  kv("CPI assumption (rent method)", a.cpiAssumption ?? 0.03, "cpiAssumption");
  kv("Market review assumption", a.marketReviewPct ?? 0.04, "marketReviewPct");
  blank();

  section("OPERATING COSTS");
  kv(
    "Variable cost / student",
    a.variableCostPerStudent,
    "variableCostPerStudent",
  );
  kv("Fixed opex Y1", a.fixedOpex, "fixedOpex");
  kv("Fixed opex growth", a.fixedOpexGrowth, "fixedOpexGrowth");
  kv(
    "Reddam fitout allowance",
    a.reddamFitoutAllowance,
    "reddamFitoutAllowance",
  );
  kv(
    // A6: mirror compute()'s guard — a negative contribution is clamped to 0 by the engine.
    "Reddam capex contribution",
    Math.max(
      0,
      Number.isFinite(a.reddamCapexContribution)
        ? a.reddamCapexContribution
        : 0,
    ),
    "reddamCapexContribution",
  );
  kv(
    "Fitout amortisation years",
    a.fitoutAmortisationYears,
    "fitoutAmortisationYears",
  );
  blank();

  section("ANCILLARY INCOME & AMENITIES");
  kv("Sports field area (sqm)", a.sportsFieldAreaSqm, "sportsFieldAreaSqm");
  kv(
    "Sports field income / sqm",
    a.sportsFieldIncomePerSqm,
    "sportsFieldIncomePerSqm",
  );
  kv(
    "Sports field LL rent / sqm",
    a.sportsFieldRentPerSqm,
    "sportsFieldRentPerSqm",
  );
  kv(
    "Sports field build cost / sqm",
    a.sportsFieldConstructionPerSqm,
    "sportsFieldConstructionPerSqm",
  );
  kv("Parking bays", a.parkingBays, "parkingBays");
  kv("Parking income / bay", a.parkingIncomePerBay, "parkingIncomePerBay");
  kv("Parking LL rent / bay", a.parkingRentPerBay, "parkingRentPerBay");
  kv(
    "Parking build cost / bay",
    a.parkingConstructionCostPerBay,
    "parkingConstructionCostPerBay",
  );
  kv(
    "Covered play area ground rent (annual lump sum)",
    a.coveredPlayAreaGroundRent,
    "coveredPlayAreaGroundRent",
  );
  kv(
    "Covered play area build cost",
    a.coveredPlayAreaConstructionCost,
    "coveredPlayAreaConstructionCost",
  );
  kv(
    "Ancillary income growth (CPI)",
    a.ancillaryGrowthPct,
    "ancillaryGrowthPct",
  );
  kv(
    "Weekday parent parking bays",
    a.weekdayParentParkingBays,
    "weekdayParentParkingBays",
  );
  kv(
    "Weekday parent parking fee",
    a.weekdayParentParkingFee,
    "weekdayParentParkingFee",
  );
  kv(
    "Weekday parent parking days",
    a.weekdayParentParkingDays,
    "weekdayParentParkingDays",
  );
  kv(
    "Weekend sports parking bays",
    a.weekendSportsParkingBays,
    "weekendSportsParkingBays",
  );
  kv(
    "Weekend sports parking fee",
    a.weekendSportsParkingFee,
    "weekendSportsParkingFee",
  );
  kv(
    "Weekend sports parking days",
    a.weekendSportsParkingDays,
    "weekendSportsParkingDays",
  );
  kv(
    "Staff reserved parking bays",
    a.staffReservedParkingBays,
    "staffReservedParkingBays",
  );
  blank();

  section("DEBT & FINANCE");
  kv("Construction LVR", a.constructionLvrRate, "constructionLvrRate", true);
  kv(
    "Construction margin rate",
    a.constructionMarginRate,
    "constructionMarginRate",
    true,
  );
  kv(
    "Construction line fee rate",
    a.constructionLineFeeRate,
    "constructionLineFeeRate",
  );
  kv("Term loan LVR", a.termLoanLvrRate, "termLoanLvrRate", true);
  kv("Term loan margin rate", a.termLoanMarginRate, "termLoanMarginRate");
  kv("Term loan hold (years)", a.termLoanHoldYears, "termLoanHoldYears", true);
  kv(
    "S-curve drawdown",
    a.useScurveDrawdown ? "Enabled" : "Disabled (50% avg drawn)",
    "useScurveDrawdown",
  );
  blank();

  section("CONSTRUCTION RISK");
  kv("Performance bond %", a.performanceBondPct, "performanceBondPct");
  kv(
    "Defects liability (months)",
    a.defectsLiabilityMonths,
    "defectsLiabilityMonths",
  );
  kv(
    "Bank guarantee (months gross rent)",
    a.bankGuaranteeMonths,
    "bankGuaranteeMonths",
  );
  kv("External works %", a.externalWorksPct, "externalWorksPct");
  blank();

  section("APPROVAL COSTS & SPLIT");
  kv("SSDA cost", a.ssdaCost ?? 0, "ssdaCost");
  kv("Change-of-use approval", a.changeOfUseCost ?? 0, "changeOfUseCost");
  kv("Other approvals", a.otherApprovalCosts ?? 0, "otherApprovalCosts");
  kv(
    "Reddam share of approvals",
    a.approvalCostReddamSharePct ?? 0.5,
    "approvalCostReddamSharePct",
  );
  blank();

  section("RENT ECONOMICS");
  kv(
    "Equity return formulation",
    a.equityReturnFormulation ?? "perpetuity",
    "equityReturnFormulation",
  );
  kv(
    "Site deal allocation status",
    a.siteDealAllocationStatus ?? "unverified",
    "siteDealAllocationStatus",
  );
  // P8-R1 (#19): the resolved rent-escalation rate the engine actually uses (fixed→rentIndexationPct,
  // cpi→cpiAssumption, market→marketReviewPct). The rent rows escalate by THIS, so cpi/market workbooks
  // reconcile to compute() (they previously silently used rentIndexationPct).
  kvFormula(
    "Rent escalation (resolved)",
    `IF(${refs.get("rentEscalationMethod")}="cpi",${refs.get("cpiAssumption")},IF(${refs.get("rentEscalationMethod")}="market",${refs.get("marketReviewPct")},${refs.get("rentIndexationPct")}))`,
    Model.effectiveRentGrowth(a),
    "effectiveRentGrowthResolved",
    NF.pct2,
  );
  blank();

  section("LEASE INCENTIVE & SCHOOL WACC (W2/W3)");
  kv(
    "Landlord contribution (lump sum)",
    a.leaseIncentiveLumpSum ?? 0,
    "leaseIncentiveLumpSum",
  );
  kv("Rent-free period (months)", a.rentFreeMonths ?? 0, "rentFreeMonths");
  kv(
    "Incentive amortisation (years)",
    a.incentiveAmortisationYears ?? a.leaseTermYears,
    "incentiveAmortisationYears",
  );
  // A6: mirror compute()'s guard (finite && > −1, else the 8.5% default).
  kv(
    "School WACC (lease-vs-own NPV)",
    Number.isFinite(a.schoolWacc) && (a.schoolWacc ?? 0) > -1
      ? a.schoolWacc
      : 0.085,
    "schoolWacc",
  );
  blank();

  section("TAX");
  kv("Corporate tax rate", a.corporateTaxRate, "corporateTaxRate");
  blank();

  section("CAPACITY DISCLOSURE");
  kv(
    "Planning enrolment target",
    a.targetEnrolment ?? 0,
    "targetEnrolment",
    true,
  );
  blank();

  const loadedCost = a.developerCostBasis;
  const allowances = a.developerCostAllowances;
  section("LOADED COST EVIDENCE & FUNDING");
  kv(
    "Cost basis mode",
    loadedCost?.mode ?? "legacy_net_trade",
    "developerCostBasisMode",
  );
  // Loaded bases are written pre-clamped (finite >= 0) exactly as compute() resolves
  // them — every loadedWorksRef/loadedSiteRef consumer reads what the engine used.
  const clampLoadedBasis = (v: number | undefined) =>
    Number.isFinite(v) && (v as number) >= 0 ? (v as number) : 0;
  kv(
    "Loaded building works",
    clampLoadedBasis(loadedCost?.buildingWorks),
    "loadedBuildingWorks",
  );
  kv(
    "Loaded site/outdoor works",
    clampLoadedBasis(loadedCost?.siteWorks),
    "loadedSiteWorks",
  );
  kv(
    "Cost evidence source",
    loadedCost?.sourceName ?? "Not loaded",
    "loadedCostSourceName",
  );
  kv(
    "Cost evidence date",
    loadedCost?.sourceDate ?? "Not loaded",
    "loadedCostSourceDate",
  );
  kv(
    "Cost evidence GST treatment",
    loadedCost?.gstStatus ?? "unverified",
    "loadedCostGstStatus",
  );
  kv(
    "Contingency input mode",
    allowances?.contingency?.mode ?? "rate",
    "contingencyInputMode",
  );
  kv(
    "Contingency amount input",
    allowances?.contingency?.mode === "amount"
      ? allowances.contingency.amount
      : 0,
    "contingencyInputAmount",
  );
  kv(
    "Contingency rate input",
    allowances?.contingency?.mode === "rate"
      ? allowances.contingency.rate
      : a.developerContingencyRate,
    "contingencyInputRate",
  );
  kv(
    "Professional fees input mode",
    allowances?.professionalFees?.mode ?? "rate",
    "professionalFeesInputMode",
  );
  kv(
    "Professional fees amount input",
    allowances?.professionalFees?.mode === "amount"
      ? allowances.professionalFees.amount
      : 0,
    "professionalFeesInputAmount",
  );
  kv(
    "Professional fees rate input",
    allowances?.professionalFees?.mode === "rate"
      ? allowances.professionalFees.rate
      : a.consultantCostRate,
    "professionalFeesInputRate",
  );
  kv(
    "Development fee input mode",
    allowances?.developmentFee?.mode ?? "rate",
    "developmentFeeInputMode",
  );
  kv(
    "Development fee amount input",
    allowances?.developmentFee?.mode === "amount"
      ? allowances.developmentFee.amount
      : 0,
    "developmentFeeInputAmount",
  );
  kv(
    "Development fee rate input",
    allowances?.developmentFee?.mode === "rate"
      ? allowances.developmentFee.rate
      : a.devManagementFeeRate,
    "developmentFeeInputRate",
  );
  // Written as 0 when the allowance is absent; validate() requires a finite >= 0 consents
  // amount in loaded mode, and the cost formulas' ISNUMBER/>=0 guard mirrors the engine's
  // hasLoadedConsents fallback for any state that slips through.
  kv("Consents amount", allowances?.consentsAmount ?? 0, "consentsAmount");
  kv(
    "Contribution timing",
    a.contributionTiming ?? "progressive",
    "contributionTiming",
  );

  ws.columns = [{ width: 38 }, { width: 22 }];

  // Register named ranges for all assumption keys
  for (const [key, ref] of refs) {
    const match = ref.match(/'Assumptions'!\$B\$(\d+)/);
    if (match) {
      const row = parseInt(match[1], 10);
      try {
        wb.definedNames.add(`Assumptions!$B$${row}`, key);
      } catch {
        // Named range may already exist; skip
      }
    }
  }

  return refs;
}

/* ── Building Schedule sheet (NEW) ──
   Per-building calculations. Building physical data = inputs (blue).
   Derived cols (construction cost, rent, outgoings) = formulas. */
export function buildBuildingSchedule(
  wb: Workbook,
  buildings: Building[],
  a: Assumptions,
  refs: Map<string, string>,
) {
  const ws = wb.addWorksheet("Building Schedule");
  ws.views = [{ state: "frozen", ySplit: 1 }];

  const headers = [
    "Building",
    "NLA (sqm)",
    "GFA (sqm)",
    "Capacity",
    "Constr. $/sqm GFA",
    "Construction cost",
    "Reddam capex",
    "Net rent $/sqm",
    "Net rent Y1",
    "Outgoings $/sqm",
    "Outgoings Y1",
    "Online (year)",
    "Constr. start (mo)",
    "Constr. duration (mo)",
    "Bldg holding interest",
  ];
  const headerRow = ws.addRow(headers);
  headerRow.eachCell((cell) => styleHeader(cell));

  // Column indices (1-based)
  const COL = {
    name: 1,
    nla: 2,
    gfa: 3,
    capacity: 4,
    constrRate: 5,
    constrCost: 6,
    reddamCapex: 7,
    netRentRate: 8,
    netRentY1: 9,
    outgoingsRate: 10,
    outgoingsY1: 11,
    online: 12,
    startMonth: 13,
    duration: 14,
    bldgHolding: 15,
  };

  const holdingRateRef = refs.get("holdingInterestRate") || "";
  const lvrRef = refs.get("constructionLvrRate") || "0";
  // A0 (#72 review): the per-building DD base must mirror compute()'s buildingData in BOTH cost-basis
  // modes. Legacy: soft lines are hard × rate (consultant/statutory/contingency/prelims/builder margin),
  // honouring the amount-mode allowances the way resolveCostAllowance does. Loaded: prelims and builder
  // margin are zero, consultant/statutory/contingency are totals on the loaded bases, and both hard and
  // soft are allocated to buildings by GFA share. The live formulas use the same IF shapes as the Dev
  // Cost HARD & SOFT rows so sheet recalculation reconciles to compute() in either mode.
  const ref = (key: string) => refs.get(key) || "0";
  const loadedCost =
    a.developerCostBasis?.mode === "loaded_aggregate"
      ? a.developerCostBasis
      : undefined;
  const loadedWorksRef = ref("loadedBuildingWorks");
  const loadedSiteRef = ref("loadedSiteWorks");
  const profModeRef = ref("professionalFeesInputMode");
  const profAmountRef = ref("professionalFeesInputAmount");
  const profRateRef = ref("professionalFeesInputRate");
  const contModeRef = ref("contingencyInputMode");
  const contAmountRef = ref("contingencyInputAmount");
  const contRateRef = ref("contingencyInputRate");
  const consentsRef = ref("consentsAmount");
  const statutoryRateRef = ref("statutoryCostRate");
  const prelimRateRef = ref("preliminariesRate");
  const builderMarginRateRef = ref("builderMarginRate");
  const consultantRateRef = ref("consultantCostRate");
  const contingencyRateRef = ref("developerContingencyRate");
  // Loaded soft totals as assumption-cell expressions, mirroring resolveCostAllowance's fallback
  // chain: amount mode wins ONLY for a number >= 0 (loop#019 — same guard as the Dev Cost rows;
  // a negative/blank amount falls back to the rate path); rate mode uses the allowance rate;
  // otherwise the base rate applies.
  const consultTotalExpr = `IF(AND(${profModeRef}="amount",ISNUMBER(${profAmountRef}),${profAmountRef}>=0),${profAmountRef},MAX(0,IF(${profModeRef}="rate",${profRateRef},${consultantRateRef}))*(${loadedWorksRef}+${loadedSiteRef}))`;
  const contTotalExpr = `IF(AND(${contModeRef}="amount",ISNUMBER(${contAmountRef}),${contAmountRef}>=0),${contAmountRef},MAX(0,IF(${contModeRef}="rate",${contRateRef},${contingencyRateRef}))*(${loadedWorksRef}+${loadedSiteRef}))`;
  // Total row is buildings.length + 2 (written after the loop, but known here).
  const totalRowNumber = buildings.length + 2;
  // GFA share with compute()'s fallback: totalGfa=0 → equal split across buildings.
  const gfaShareExpr = (row: number) =>
    `IF($C$${totalRowNumber}>0,C${row}/$C$${totalRowNumber},1/${buildings.length})`;
  // Cached-value mirror of compute(): soft-cost totals resolved the way model.ts resolves them.
  const safeLoaded = (v: number | undefined) =>
    Number.isFinite(v) && (v as number) >= 0 ? (v as number) : 0;
  const loadedBuildingWorks = loadedCost
    ? safeLoaded(loadedCost.buildingWorks)
    : 0;
  const loadedSiteWorks = loadedCost ? safeLoaded(loadedCost.siteWorks) : 0;
  const hardConstructionTotal = loadedCost
    ? loadedBuildingWorks
    : buildings.reduce((sum, b) => sum + b.gfaSqm * b.constructionPerSqmGfa, 0);
  const allowanceBase = loadedCost
    ? loadedBuildingWorks + loadedSiteWorks
    : hardConstructionTotal;
  const consultantTotal = resolveCostAllowance(
    a.developerCostAllowances?.professionalFees,
    a.consultantCostRate,
    allowanceBase,
  ).amount;
  const consentsAmount = a.developerCostAllowances?.consentsAmount;
  const statutoryTotal =
    loadedCost && Number.isFinite(consentsAmount) && (consentsAmount ?? 0) >= 0
      ? (consentsAmount as number)
      : hardConstructionTotal * a.statutoryCostRate;
  const contingencyTotal = resolveCostAllowance(
    a.developerCostAllowances?.contingency,
    a.developerContingencyRate,
    allowanceBase,
  ).amount;
  const equityFraction = 1 - Math.min(1, Math.max(0, a.constructionLvrRate));
  const totalGfa = buildings.reduce((sum, b) => sum + b.gfaSqm, 0);
  const cachedByColumn = new Map<number, number>();
  const addCached = (column: number, value: number) => {
    cachedByColumn.set(column, (cachedByColumn.get(column) ?? 0) + value);
  };

  buildings.forEach((b, idx) => {
    const row = idx + 2; // Excel row number
    // Mirrors compute(): NaN/negative/fractional start months normalise the same way on the sheet.
    const startMonth = Math.max(
      0,
      Math.round(
        Number.isFinite(b.constructionStartMonth)
          ? b.constructionStartMonth!
          : 0,
      ),
    );
    // Mirrors compute(): an unset per-building duration falls back to the campus-wide developmentMonths.
    // (Was `?? 0` → 1 month, which understated the sheet's per-building holding interest ~developmentMonths×.)
    const duration = Math.max(
      1,
      Math.round(
        Number.isFinite(b.constructionMonths)
          ? b.constructionMonths!
          : a.developmentMonths,
      ),
    );
    // Mirror compute(): loaded mode prorates the loaded building works by GFA share; legacy uses the
    // per-building rate. The DD base adds each soft line the way compute() allocates it (hard share).
    const gfaShare =
      totalGfa > 0
        ? b.gfaSqm / totalGfa
        : buildings.length > 0
          ? 1 / buildings.length
          : 0;
    const hard = loadedCost
      ? hardConstructionTotal * gfaShare
      : b.gfaSqm * b.constructionPerSqmGfa;
    const hardShare =
      hardConstructionTotal > 0 ? hard / hardConstructionTotal : 0;
    const bldDd =
      hard +
      consultantTotal * hardShare +
      statutoryTotal * hardShare +
      contingencyTotal * hardShare +
      (loadedCost ? 0 : hard * a.preliminariesRate) +
      (loadedCost ? 0 : hard * a.builderMarginRate);
    const constructionCost = Math.round(hard);
    const reddamCapex = Math.round(b.reddamCapex);
    const netRentY1 = Math.round(b.nlaSqm * b.netRentPerSqm);
    const outgoingsY1 = Math.round(b.nlaSqm * b.outgoingsPerSqm);
    // Same operation order as compute()'s buildingHolding reduction.
    const buildingHoldingInterest =
      0.5 * bldDd * equityFraction * a.holdingInterestRate * (duration / 12);
    const scheduledSeats = scheduledClassroomSeatValue(b.enrolmentCapacity);
    const r = ws.addRow([
      b.name,
      b.nlaSqm,
      b.gfaSqm,
      scheduledSeats,
      b.constructionPerSqmGfa,
      0, // placeholder — formula set below
      reddamCapex,
      b.netRentPerSqm,
      0, // placeholder
      b.outgoingsPerSqm,
      0, // placeholder
      b.comesOnlineYear,
      startMonth,
      duration,
      0, // placeholder — formula set below
    ]);

    r.eachCell((cell, colNumber) => {
      thinBorder(cell);
      if (colNumber === COL.name) {
        styleLabel(cell, false);
      } else if (
        [
          COL.nla,
          COL.gfa,
          COL.capacity,
          COL.constrRate,
          COL.reddamCapex,
          COL.netRentRate,
          COL.outgoingsRate,
          COL.online,
          COL.startMonth,
          COL.duration,
        ].includes(colNumber)
      ) {
        // Inputs from building data
        styleInput(cell);
        if (
          [
            COL.nla,
            COL.gfa,
            COL.capacity,
            COL.online,
            COL.startMonth,
            COL.duration,
          ].includes(colNumber)
        )
          cell.numFmt = NF.num;
        else if (
          [COL.constrRate, COL.netRentRate, COL.outgoingsRate].includes(
            colNumber,
          )
        )
          cell.numFmt = NF.currency;
        else if (colNumber === COL.reddamCapex) cell.numFmt = NF.currency;
      }
    });

    // Construction cost: legacy = GFA × $/sqm; loaded = loaded building works × GFA share (A0).
    setFormula(
      r.getCell(COL.constrCost),
      loadedCost
        ? `${loadedWorksRef}*${gfaShareExpr(row)}`
        : `${addr(row - 1, COL.gfa - 1)}*${addr(row - 1, COL.constrRate - 1)}`,
      constructionCost,
      { numFmt: NF.currency },
    );

    // Net rent Y1 = NLA × $/sqm
    setFormula(
      r.getCell(COL.netRentY1),
      `${addr(row - 1, COL.nla - 1)}*${addr(row - 1, COL.netRentRate - 1)}`,
      netRentY1,
      { numFmt: NF.currency },
    );

    // Outgoings Y1 = NLA × $/sqm
    setFormula(
      r.getCell(COL.outgoingsY1),
      `${addr(row - 1, COL.nla - 1)}*${addr(row - 1, COL.outgoingsRate - 1)}`,
      outgoingsY1,
      { numFmt: NF.currency },
    );

    // Building holding interest = 0.5 × building DD base × rate × duration/12, charged on the EQUITY
    // share only (× (1 − construction LVR)) — P5-R1/D1. The DD base is mode-gated (A0): legacy builds
    // it from this row's construction cost and the amount-aware soft allocations; loaded takes the
    // loaded works plus the loaded soft totals, both × GFA share.
    const bldgHoldingFormula = loadedCost
      ? `0.5*${gfaShareExpr(row)}*(${loadedWorksRef}+${consultTotalExpr}+IF(AND(ISNUMBER(${consentsRef}),${consentsRef}>=0),${consentsRef},${loadedWorksRef}*${statutoryRateRef})+${contTotalExpr})*(1-MIN(1,MAX(0,${lvrRef})))*${holdingRateRef}*${addr(row - 1, COL.duration - 1)}/12`
      : `0.5*(${addr(row - 1, COL.constrCost - 1)}*(1+${prelimRateRef}+${builderMarginRateRef}+${statutoryRateRef})+IF(AND(${profModeRef}="amount",ISNUMBER(${profAmountRef}),${profAmountRef}>=0),${profAmountRef}*${addr(row - 1, COL.constrCost - 1)}/$F$${totalRowNumber},MAX(0,IF(${profModeRef}="rate",${profRateRef},${consultantRateRef}))*${addr(row - 1, COL.constrCost - 1)})+IF(AND(${contModeRef}="amount",ISNUMBER(${contAmountRef}),${contAmountRef}>=0),${contAmountRef}*${addr(row - 1, COL.constrCost - 1)}/$F$${totalRowNumber},MAX(0,IF(${contModeRef}="rate",${contRateRef},${contingencyRateRef}))*${addr(row - 1, COL.constrCost - 1)}))*(1-MIN(1,MAX(0,${lvrRef})))*${holdingRateRef}*${addr(row - 1, COL.duration - 1)}/12`;
    setFormula(
      r.getCell(COL.bldgHolding),
      bldgHoldingFormula,
      buildingHoldingInterest,
      {
        numFmt: NF.currency,
      },
    );

    addCached(COL.nla, b.nlaSqm);
    addCached(COL.gfa, b.gfaSqm);
    addCached(COL.capacity, scheduledSeats ?? 0);
    addCached(COL.constrCost, constructionCost);
    addCached(COL.reddamCapex, reddamCapex);
    addCached(COL.netRentY1, netRentY1);
    addCached(COL.outgoingsY1, outgoingsY1);
    addCached(COL.bldgHolding, buildingHoldingInterest);
  });

  // Totals row
  const n = buildings.length;
  const totalRow = ws.addRow([
    "TOTAL",
    "",
    "",
    "",
    "",
    0,
    0,
    "",
    0,
    "",
    0,
    "",
    "",
    "",
    0,
  ]);
  const tr = n + 2;
  styleTotal(totalRow.getCell(COL.name));
  thinBorder(totalRow.getCell(COL.name));

  // SUM formulas for totals
  const sumCols = [
    COL.nla,
    COL.gfa,
    COL.capacity,
    COL.constrCost,
    COL.reddamCapex,
    COL.netRentY1,
    COL.outgoingsY1,
    COL.bldgHolding,
  ];
  sumCols.forEach((col) => {
    const cell = totalRow.getCell(col);
    if (n > 0) {
      const numFmt = [COL.nla, COL.gfa, COL.capacity].includes(col)
        ? NF.num
        : NF.currency;
      setFormula(
        cell,
        `SUM(${addr(1, col - 1)}:${addr(n, col - 1)})`,
        cachedByColumn.get(col) ?? 0,
        {
          numFmt,
          bold: true,
        },
      );
    } else {
      cell.value = 0;
      cell.numFmt = [COL.nla, COL.gfa, COL.capacity].includes(col)
        ? NF.num
        : NF.currency;
      cell.font = { bold: true, name: "Arial", size: 10 };
      thinBorder(cell);
    }
  });

  // Fill empty total cells with borders
  [
    COL.constrRate,
    COL.netRentRate,
    COL.outgoingsRate,
    COL.online,
    COL.startMonth,
    COL.duration,
  ].forEach((col) => {
    thinBorder(totalRow.getCell(col));
  });

  ws.columns = [
    { width: 22 },
    { width: 10 },
    { width: 10 },
    { width: 10 },
    { width: 16 },
    { width: 16 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 12 },
    { width: 14 },
    { width: 16 },
    { width: 18 },
  ];

  // Return cell references for cross-sheet linking
  return {
    totalConstrCost: `'Building Schedule'!$F$${tr}`,
    totalReddamCapex: `'Building Schedule'!$G$${tr}`,
    totalReddamCapexResult: buildings.reduce(
      (total, building) => total + building.reddamCapex,
      0,
    ),
    totalNetRentY1: `'Building Schedule'!$I$${tr}`,
    totalOutgoingsY1: `'Building Schedule'!$K$${tr}`,
    totalNla: `'Building Schedule'!$B$${tr}`,
    totalGfa: `'Building Schedule'!$C$${tr}`,
    totalCapacity: `'Building Schedule'!$D$${tr}`,
    livePhysicalCapacityAtYear: (year: number) =>
      scheduledClassroomSeatsForYear(buildings, year),
    totalBuildingHolding: `'Building Schedule'!$O$${tr}`,
    nlaRange: `'Building Schedule'!$B$2:$B$${tr - 1}`,
    netRentRateRange: `'Building Schedule'!$H$2:$H$${tr - 1}`,
    onlineYearRange: `'Building Schedule'!$L$2:$L$${tr - 1}`,
  };
}

/* ── Cover sheet ──
   All values are formulas linking to Headline KPIs sheet.
   No hardcoded values. */
export function buildCover(
  wb: Workbook,
  scenario: Scenario,
  result: ModelResult,
  refs: Map<string, string>,
  bldgRefs: ReturnType<typeof buildBuildingSchedule>,
  _devCost: ReturnType<typeof buildDevCost>,
  kpiRefs: Map<string, number>,
  _opPl: ReturnType<typeof buildOpPL>,
) {
  const ws = wb.addWorksheet("Cover");
  const a = scenario.assumptions;
  let r = 1;

  const big = (text: string, size = 14) => {
    ws.getCell(r, 1).value = text;
    ws.getCell(r, 1).font = {
      bold: true,
      size,
      name: "Arial",
      color: { argb: C.headerBg },
    };
    r++;
  };
  const line = (
    label: string,
    formula: string,
    result: number | string,
    opts?: { numFmt?: string },
  ) => {
    const labelCell = ws.getCell(r, 1);
    labelCell.value = label;
    styleLabel(labelCell, true);
    labelCell.alignment = { vertical: "middle", wrapText: true };
    if (label.length > 42) ws.getRow(r).height = 28;
    const cell = ws.getCell(r, 2);
    if (typeof result === "number") {
      setFormula(cell, formula, result, { numFmt: opts?.numFmt, isLink: true });
    } else {
      cell.value = result;
      styleLink(cell);
    }
    r++;
  };
  const textLine = (label: string, value: string) => {
    ws.getCell(r, 1).value = label;
    styleLabel(ws.getCell(r, 1), true);
    ws.getCell(r, 2).value = value;
    ws.getCell(r, 2).font = {
      name: "Arial",
      size: 10,
      color: { argb: C.formulaBlack },
    };
    ws.getCell(r, 2).alignment = { vertical: "middle", wrapText: true };
    if (value.length > 58) {
      ws.getRow(r).height = Math.max(42, Math.ceil(value.length / 42) * 14);
    }
    r++;
  };

  big("Campus Lease Model — Board Pack Export", 14);
  ws.mergeCells("A1:B1");
  r++;
  textLine("Generated", AU_DATE());
  textLine("Project", a.projectName);
  textLine("Operator", a.operatorName);
  textLine("Campus", a.campusName);
  textLine("Address", a.address);
  textLine("Scenario", scenario.name);
  textLine("Description", scenario.description || "");
  const managementPosture = getOperatorManagementPosture(
    getOperatorDealRecommendation(scenario, result),
  );
  textLine(
    "Management recommendation",
    managementPosture ?? "Required before Board issue",
  );
  r++;

  const kpi = (label: string) => kpiRefs.get(label) ?? 0;

  const k = result.kpis;
  const exitYear = a.developerExitYear ?? a.modelYears;
  const primaryGearedLabel =
    exitYear < a.modelYears
      ? `IRR (geared, project Y${exitYear} early exit)`
      : "IRR (geared, sale at completion)";
  const primaryUngearedLabel = `Primary IRR (ungeared, ${exitYear < a.modelYears ? `Y${exitYear}` : "PC"})`;
  const refinanceExitLabel = gearedRefinanceExitLabel(a.termLoanHoldYears);

  big("OPERATOR DECISION METRICS", 12);
  line(
    RENT_PCT_REVENUE_Y1_LABEL,
    `='Headline KPIs'!$B$${kpi(RENT_PCT_REVENUE_Y1_LABEL)}`,
    k.rentPctRevenueYear1,
    { numFmt: NF.pct2 },
  );
  line(
    Y1_EBITDA_LABEL,
    `='Headline KPIs'!$B$${kpi(Y1_EBITDA_LABEL)}`,
    k.yearOneEbitda,
    { numFmt: NF.currency },
  );
  line(
    OPERATOR_NPV_LABEL,
    `='Headline KPIs'!$B$${kpi(OPERATOR_NPV_LABEL)}`,
    k.reddamNpv,
    {
      numFmt: NF.currency,
    },
  );
  // Operator IRR: keep the live link to Headline KPIs (which is IFERROR→"n/a" when the operator never
  // recovers capex), but cache "n/a" rather than 0 so the static Cover value matches Headline/Op P&L.
  {
    ws.getCell(r, 1).value = OPERATOR_IRR_LABEL;
    styleLabel(ws.getCell(r, 1), true);
    const irrCached: number | string =
      k.reddamIrr == null ? "n/a" : k.reddamIrr;
    setFormula(
      ws.getCell(r, 2),
      `='Headline KPIs'!$B$${kpi(OPERATOR_IRR_LABEL)}`,
      irrCached,
      { numFmt: NF.pct2, isLink: true },
    );
    r++;
  }
  line(
    OPERATOR_PAYBACK_LABEL,
    `='Headline KPIs'!$B$${kpi(OPERATOR_PAYBACK_LABEL)}`,
    k.paybackYear ?? "—",
    { numFmt: NF.num },
  );
  line(
    RAMP_RESERVES_REQUIRED_LABEL,
    `='Headline KPIs'!$B$${kpi(RAMP_RESERVES_REQUIRED_LABEL)}`,
    k.reservesRequiredToSurvive,
    { numFmt: NF.currency },
  );
  r++;

  big("DEVELOPER COUNTERPARTY DIAGNOSTICS", 12);
  line(
    `Developer ${primaryUngearedLabel}`,
    `='Headline KPIs'!$B$${kpi(primaryUngearedLabel)}`,
    k.developerPrimaryIrr,
    { numFmt: NF.pct2 },
  );
  line(
    `Developer ${primaryGearedLabel}`,
    `='Headline KPIs'!$B$${kpi(primaryGearedLabel)}`,
    k.developerPrimaryGearedIrr,
    { numFmt: NF.pct2 },
  );
  line(
    `Developer ${refinanceExitLabel}`,
    `='Headline KPIs'!$B$${kpi(refinanceExitLabel)}`,
    k.developerGearedWholeOfLifeIrr,
    { numFmt: NF.pct2 },
  );
  line(
    "Stated developer hurdle",
    refs.get("developerHurdleIRR") || "",
    a.developerHurdleIRR,
    { numFmt: NF.pct2 },
  );
  line(
    EQUITY_REQUIRED_LABEL,
    `='Headline KPIs'!$B$${kpi(EQUITY_REQUIRED_LABEL)}`,
    k.developerEquityRequired,
    { numFmt: NF.currency },
  );
  line(
    NOI_ON_DEVELOPER_EQUITY_LABEL,
    `='Headline KPIs'!$B$${kpi(NOI_ON_DEVELOPER_EQUITY_LABEL)}`,
    k.developerYieldOnCash,
    { numFmt: NF.pct2 },
  );
  r++;

  big("CAMPUS DELIVERY BASIS", 12);
  const totalCapacity = scenario.buildings.reduce(
    (sum, building) =>
      sum + (scheduledClassroomSeatValue(building.enrolmentCapacity) ?? 0),
    0,
  );
  const planningTarget = resolveEnrolmentCapacityBasis(
    a.targetEnrolment,
    totalCapacity,
  );
  const targetRef = refs.get("targetEnrolment") || "0";
  line(
    CAPACITY_LABELS.planningTarget,
    `IF(${targetRef}>0,${targetRef},${bldgRefs.totalCapacity})`,
    planningTarget,
    { numFmt: NF.num },
  );
  line(
    CAPACITY_LABELS.scheduledClassroomSeats,
    bldgRefs.totalCapacity,
    totalCapacity,
    { numFmt: NF.num },
  );
  line(
    "Physical delivery bridge",
    `MAX(0,IF(${targetRef}>0,${targetRef},${bldgRefs.totalCapacity})-${bldgRefs.totalCapacity})`,
    Math.max(0, planningTarget - totalCapacity),
    { numFmt: NF.num },
  );

  ws.columns = [{ width: 34 }, { width: 24 }];
}

/* ── Buildings sheet ──
   Simple data table of raw building inputs. All static (inputs). */
export function buildBuildings(wb: Workbook, buildings: Building[]) {
  const ws = wb.addWorksheet("Buildings");
  ws.views = [{ state: "frozen", ySplit: 2 }];

  // Raw-input clarity note (row 1) — pushes headers to row 2, data to rows 3..n+2.
  ws.getCell(1, 1).value =
    "Raw per-building input register. Derived figures (construction cost = GFA × $/sqm, net rent Y1 = NLA × $/sqm, outgoings Y1 = NLA × $/sqm) are computed as live formulas on the 'Building Schedule' sheet.";
  styleNote(ws.getCell(1, 1));
  ws.mergeCells("A1:J1");

  const headers = [
    "ID",
    "Name",
    "NLA (sqm)",
    "GFA (sqm)",
    "Construction $/sqm GFA",
    "Reddam capex",
    "Net rent $/sqm",
    "Outgoings $/sqm",
    "Capacity",
    "Online (year)",
  ];
  const headerRow = ws.addRow(headers);
  headerRow.eachCell((cell) => styleHeader(cell));

  buildings.forEach((b) => {
    const row = ws.addRow([
      b.id,
      b.name,
      b.nlaSqm,
      b.gfaSqm,
      b.constructionPerSqmGfa,
      Math.round(b.reddamCapex),
      b.netRentPerSqm,
      b.outgoingsPerSqm,
      scheduledClassroomSeatValue(b.enrolmentCapacity),
      b.comesOnlineYear,
    ]);
    row.eachCell((cell, colNumber) => {
      thinBorder(cell);
      if (colNumber >= 3) {
        const nf = autoNumFmt(String(headers[colNumber - 1]), cell.value);
        if (nf) cell.numFmt = nf;
      }
    });
  });

  // Totals row with SUM formulas
  const n = buildings.length;
  // Data rows are 3..n+2 (row 1 = note, row 2 = headers), so SUM ranges run C3:C{n+2}.
  const totals = [
    "",
    "TOTAL",
    n > 0
      ? {
          formula: `=SUM(C3:C${n + 2})`,
          result: buildings.reduce((s, b) => s + b.nlaSqm, 0),
        }
      : 0,
    n > 0
      ? {
          formula: `=SUM(D3:D${n + 2})`,
          result: buildings.reduce((s, b) => s + b.gfaSqm, 0),
        }
      : 0,
    "",
    n > 0
      ? {
          formula: `=SUM(F3:F${n + 2})`,
          result: buildings.reduce((s, b) => s + b.reddamCapex, 0),
        }
      : 0,
    "",
    "",
    n > 0
      ? {
          formula: `=SUM(I3:I${n + 2})`,
          result: scheduledClassroomSeatsForYear(buildings),
        }
      : 0,
    "",
  ];
  const totalRow = ws.addRow(totals);
  totalRow.eachCell((cell, colNumber) => {
    styleTotal(cell);
    thinBorder(cell);
    if (colNumber >= 3 && [3, 4, 6, 9].includes(colNumber)) {
      const nf = autoNumFmt(String(headers[colNumber - 1]), cell.value);
      if (nf) cell.numFmt = nf;
    }
  });

  ws.columns = [
    { width: 10 },
    { width: 26 },
    { width: 12 },
    { width: 12 },
    { width: 18 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 10 },
    { width: 14 },
  ];
}

/* ── Dev Cost sheet (full formulas) ── */
export function buildDevCost(
  wb: Workbook,
  d: Developer,
  a: Assumptions,
  refs: Map<string, string>,
  bldgRefs: ReturnType<typeof buildBuildingSchedule>,
  cf: ReturnType<typeof buildDevCashflow>,
  k: KPIs,
) {
  const ws = wb.addWorksheet("Dev Cost");
  ws.views = [{ state: "frozen", ySplit: 1 }];

  const ref = (key: string) => refs.get(key) ?? "";
  // P8-R2 (#23/#25/#29): when the developer flips before the model horizon, the sale realisation is the
  // EXIT-YEAR valuation, not the at-completion Y1-NOI formula. Stamp the exit-year gross realisation + the
  // (at-completion) term-loan limit as input-styled model-output literals so the realisation/profit/margin/
  // money-multiple chain + the debt facility reconcile to compute(); the default export keeps live formulas.
  const { exitYear: exitYr, earlyExit: useExitYear } = exitBasisFor(a);
  const exitNote = (row: number, text: string) => {
    ws.getCell(row, 3).value = text;
    styleNote(ws.getCell(row, 3));
  };

  let r = 1;
  const section = (title: string) => {
    ws.getCell(r, 1).value = title;
    ws.getCell(r, 1).font = {
      bold: true,
      color: { argb: C.headerText },
      name: "Arial",
      size: 10,
    };
    ws.getCell(r, 1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.headerBg },
    };
    ws.getCell(r, 2).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.headerBg },
    };
    thinBorderRow(ws, r, 1, 2);
    r++;
  };

  // kv = key-value row; returns Excel row number for formula referencing
  const kv = (
    label: string,
    value: string | number | { formula: string; result: number | string },
    isFormula = false,
    numFmt?: string,
  ) => {
    ws.getCell(r, 1).value = label;
    styleLabel(ws.getCell(r, 1));
    const cell = ws.getCell(r, 2);
    if (typeof value === "object" && "formula" in value) {
      const normalized = normalizeFormulaExpression(value.formula);
      cell.value = { formula: normalized, result: value.result };
      styleFormula(cell);
    } else {
      cell.value = value;
      if (isFormula) styleFormula(cell);
      else styleInput(cell);
    }
    if (numFmt) cell.numFmt = numFmt;
    const nf = autoNumFmt(
      label,
      typeof value === "object" ? value.result : value,
    );
    if (nf && !numFmt) cell.numFmt = nf;
    thinBorderRow(ws, r, 1, 2);
    r++;
    return r - 1;
  };

  const kvf = (
    label: string,
    formula: string,
    result: number | string,
    numFmt?: string,
  ) => {
    return kv(label, { formula, result }, true, numFmt);
  };
  const loadedMode = d.costBasisMode === "loaded_aggregate";
  const costBasisModeRef = ref("developerCostBasisMode");

  // ── LAND & ACQUISITION ──
  section("LAND & ACQUISITION");
  const purchasePriceRow = kv(
    "Purchase price",
    { formula: "=" + ref("purchasePrice"), result: d.purchasePrice },
    true,
    NF.currency,
  );
  const stampDutyRow = kvf(
    "Stamp duty",
    `B${purchasePriceRow}*${ref("stampDutyRate")}`,
    d.stampDuty,
    NF.currency,
  );
  const acqFeeRow = kvf(
    "Acquisition fee",
    `B${purchasePriceRow}*${ref("acquisitionFeeRate")}`,
    d.acquisitionFee,
    NF.currency,
  );
  const landAcqRow = kvf(
    "Total land & acquisition",
    `B${purchasePriceRow}+B${stampDutyRow}+B${acqFeeRow}`,
    d.purchasePrice + d.stampDuty + d.acquisitionFee,
    NF.currency,
  );
  r++;

  // ── HARD & SOFT COSTS ──
  section("HARD & SOFT COSTS");
  const hardConstrRow = kv(
    loadedMode
      ? "Hard construction (loaded building works)"
      : "Hard construction (net trade)",
    {
      formula: `=IF(${costBasisModeRef}="loaded_aggregate",${ref("loadedBuildingWorks")},${bldgRefs.totalConstrCost})`,
      result: d.hardConstruction,
    },
    true,
    NF.currency,
  );
  const siteWorksRow = kvf(
    "Site/outdoor works (loaded)",
    `IF(${costBasisModeRef}="loaded_aggregate",${ref("loadedSiteWorks")},0)`,
    d.siteWorksConstruction,
    NF.currency,
  );
  const prelimRow = kvf(
    "Preliminaries",
    `IF(${costBasisModeRef}="loaded_aggregate",0,B${hardConstrRow}*${ref("preliminariesRate")})`,
    d.preliminaries,
    NF.currency,
  );
  const builderMarginRow = kvf(
    "Builder margin",
    `IF(${costBasisModeRef}="loaded_aggregate",0,B${hardConstrRow}*${ref("builderMarginRate")})`,
    d.builderMargin,
    NF.currency,
  );
  const consultantRow = kvf(
    loadedMode ? "Professional fees / consultants" : "Consultant costs",
    // resolveCostAllowance() honors the amount only when finite >= 0; a negative or
    // blank amount falls back to the rate path — mirror so the live sheet cannot
    // diverge from the engine (same guard the Consents row carries).
    `IF(AND(${ref("professionalFeesInputMode")}="amount",ISNUMBER(${ref("professionalFeesInputAmount")}),${ref("professionalFeesInputAmount")}>=0),${ref("professionalFeesInputAmount")},(B${hardConstrRow}+B${siteWorksRow})*MAX(0,${ref("professionalFeesInputRate")}))`,
    d.consultantCosts,
    NF.currency,
  );
  const statutoryRow = kvf(
    loadedMode ? "Consents" : "Statutory costs",
    // compute() (hasLoadedConsents) only takes the consents amount when it is >= 0; a missing
    // or negative allowance falls back to hard construction × statutory rate. Mirror that here
    // so the live sheet cannot diverge from the engine for loaded scenarios without consents.
    `IF(${costBasisModeRef}="loaded_aggregate",IF(AND(ISNUMBER(${ref("consentsAmount")}),${ref("consentsAmount")}>=0),${ref("consentsAmount")},B${hardConstrRow}*${ref("statutoryCostRate")}),B${hardConstrRow}*${ref("statutoryCostRate")})`,
    d.statutoryCosts,
    NF.currency,
  );
  const planningRow = kvf(
    "Planning approval costs",
    `IF(${costBasisModeRef}="loaded_aggregate",0,${ref("planningApprovalCosts")})`,
    d.planningApprovalCosts,
    NF.currency,
  );
  const contingencyRow = kvf(
    "Construction contingency",
    // Same negative/blank amount guard as resolveCostAllowance() — see Consents row.
    `IF(AND(${ref("contingencyInputMode")}="amount",ISNUMBER(${ref("contingencyInputAmount")}),${ref("contingencyInputAmount")}>=0),${ref("contingencyInputAmount")},(B${hardConstrRow}+B${siteWorksRow})*MAX(0,${ref("contingencyInputRate")}))`,
    d.constructionContingency,
    NF.currency,
  );
  // Developer share of split approvals (SSDA + change-of-use + other) × (1 − Reddam share).
  // Deterministic from assumption inputs, so it's a live formula like its sibling cost lines;
  // MIN/MAX replicates the model's clamp of the Reddam-share pct to [0,1] (model.ts:309).
  const approvalShareRow = kv(
    "Approval costs (developer share)",
    {
      formula: `=IF(${costBasisModeRef}="loaded_aggregate",0,(${ref("ssdaCost")}+${ref("changeOfUseCost")}+${ref("otherApprovalCosts")})*(1-MIN(1,MAX(0,${ref("approvalCostReddamSharePct")}))))`,
      result: d.developerApprovalShare,
    },
    true,
    NF.currency,
  );
  const ddTotalRow = kvf(
    "Total hard & soft costs",
    `B${hardConstrRow}+B${siteWorksRow}+B${prelimRow}+B${builderMarginRow}+B${consultantRow}+B${statutoryRow}+B${planningRow}+B${contingencyRow}+B${approvalShareRow}`,
    d.hardConstruction +
      d.siteWorksConstruction +
      d.preliminaries +
      d.builderMargin +
      d.consultantCosts +
      d.statutoryCosts +
      d.planningApprovalCosts +
      d.constructionContingency +
      d.developerApprovalShare,
    NF.currency,
  );
  // P4-R2: developer builds the amenities (sports field + parking + covered play area) and leases them to
  // the operator. Build cost sits in TDC, attracts holding interest + DM fee. Rates are flagged for
  // construction-cre-panel.
  const amenityConstrRow = kvf(
    loadedMode
      ? "Amenity construction (separately added)"
      : "Amenity construction (sports field + parking + covered play area)",
    `IF(${costBasisModeRef}="loaded_aggregate",0,${ref("sportsFieldAreaSqm")}*${ref("sportsFieldConstructionPerSqm")}+${ref("parkingBays")}*${ref("parkingConstructionCostPerBay")}+${ref("coveredPlayAreaConstructionCost")})`,
    d.amenityConstruction,
    NF.currency,
  );
  // A5: per-item amenity construction disclosure — the total above is what the cost stack uses;
  // these lines just show the three components it sums (all zero in loaded mode, like the total).
  const amenityItem = (label: string, expr: string, cached: number) =>
    kvf(
      `— of which ${label}`,
      `IF(${costBasisModeRef}="loaded_aggregate",0,${expr})`,
      cached,
      NF.currency,
    );
  amenityItem(
    "sports field",
    `${ref("sportsFieldAreaSqm")}*${ref("sportsFieldConstructionPerSqm")}`,
    loadedMode ? 0 : a.sportsFieldAreaSqm * a.sportsFieldConstructionPerSqm,
  );
  amenityItem(
    "parking",
    `${ref("parkingBays")}*${ref("parkingConstructionCostPerBay")}`,
    loadedMode ? 0 : a.parkingBays * a.parkingConstructionCostPerBay,
  );
  amenityItem(
    "covered play area",
    ref("coveredPlayAreaConstructionCost"),
    loadedMode ? 0 : a.coveredPlayAreaConstructionCost,
  );
  r++;

  // ── FINANCE & FEES ──
  section("FINANCE & FEES");
  // P5-R1/D1: holding interest is charged on the EQUITY-funded share only — each carry term is scaled by
  // the equity fraction (1 − construction LVR); the debt-funded carry lives in the construction facility
  // (line fee + interest on the Debt Schedule), not here, so it is not double-charged.
  const equityFraction = 1 - Math.min(1, Math.max(0, a.constructionLvrRate));
  // E.1: Holding interest split — site-wide (until last completion) + building-specific (50% per building duration), each on the equity share
  const siteWideCostsFormula = `B${landAcqRow}+B${planningRow}+B${approvalShareRow}+${ref("otherDdCosts")}+${ref("tenantRelocationCosts")}+B${siteWorksRow}+B${amenityConstrRow}`;
  const siteWideHoldingResult =
    (d.purchasePrice +
      d.stampDuty +
      d.acquisitionFee +
      d.planningApprovalCosts +
      d.developerApprovalShare +
      d.otherDdCosts +
      d.tenantRelocationCosts +
      d.siteWorksConstruction +
      d.amenityConstruction) *
    equityFraction *
    a.holdingInterestRate *
    (d.lastCompletionMonth / 12);
  // Parenthesise the cost base so the rate × time × equity-fraction applies to the WHOLE sum, not just
  // the last term (without the parens Excel recalcs this high — precedence; cached value is always correct).
  const siteWideHoldingRow = kvf(
    "Holding interest (site-wide, equity share)",
    `(${siteWideCostsFormula})*(1-MIN(1,MAX(0,${ref("constructionLvrRate")})))*${ref("holdingInterestRate")}*${d.lastCompletionMonth}/12`,
    siteWideHoldingResult,
    NF.currency,
  );
  // A0: the Building Schedule column-O formulas now mirror compute() in loaded mode too, so the
  // building-specific holding is a live sheet ref in both modes (was a numeric literal in loaded mode).
  const buildingHoldingRow = kvf(
    "Holding interest (building-specific)",
    bldgRefs.totalBuildingHolding,
    d.holdingInterest - siteWideHoldingResult,
    NF.currency,
  );
  const holdingInterestRow = kvf(
    "Total holding interest",
    `B${siteWideHoldingRow}+B${buildingHoldingRow}`,
    d.holdingInterest,
    NF.currency,
  );

  const otherDdRow = kv(
    "Other DD costs",
    { formula: "=" + ref("otherDdCosts"), result: d.otherDdCosts },
    true,
    NF.currency,
  );
  const tenantRow = kv(
    "Tenant relocation costs",
    {
      formula: "=" + ref("tenantRelocationCosts"),
      result: d.tenantRelocationCosts,
    },
    true,
    NF.currency,
  );
  const preMgmtRow = kvf(
    "Pre-management total",
    `B${landAcqRow}+B${ddTotalRow}+B${amenityConstrRow}+B${holdingInterestRow}+B${otherDdRow}+B${tenantRow}`,
    d.purchasePrice +
      d.stampDuty +
      d.acquisitionFee +
      d.hardConstruction +
      d.siteWorksConstruction + // in B{ddTotalRow}'s formula — omitting it here made the cached total drift on F9
      d.preliminaries +
      d.builderMargin +
      d.consultantCosts +
      d.statutoryCosts +
      d.planningApprovalCosts +
      d.constructionContingency +
      d.developerApprovalShare +
      d.amenityConstruction +
      d.holdingInterest +
      d.otherDdCosts +
      d.tenantRelocationCosts,
    NF.currency,
  );
  // DM fee base = development costs + optionally land / finance (mirrors model.ts dmFeeBase).
  // Default excludes land + all finance, so the base is just the dev-cost rows (NOT the full pre-mgmt total).
  const dmFeeBaseTerms = [
    `B${ddTotalRow}`,
    `B${amenityConstrRow}`,
    `B${otherDdRow}`,
    `B${tenantRow}`,
  ];
  if (a.dmFeeIncludeLand) dmFeeBaseTerms.push(`B${landAcqRow}`);
  if (a.dmFeeIncludeHoldingInterest)
    dmFeeBaseTerms.push(`B${holdingInterestRow}`);
  // line fee / construction interest live on the Debt Schedule sheet → fold the $ in as a literal when included
  const dmFeeFinanceLiteral =
    (a.dmFeeIncludeLineFee ? d.lineFee : 0) +
    (a.dmFeeIncludeConstructionInterest ? d.debtCostDev - d.lineFee : 0);
  const dmFeeBaseFormula =
    dmFeeBaseTerms.join("+") +
    (dmFeeFinanceLiteral > 0 ? `+${Math.round(dmFeeFinanceLiteral)}` : "");
  const dmFeeBaseRow = kvf(
    "DM fee base",
    dmFeeBaseFormula,
    d.dmFeeBase,
    NF.currency,
  );
  const devMgmtFeeRow = kvf(
    "Development management fee",
    // Same negative/blank amount guard as resolveCostAllowance() — see Consents row.
    `IF(AND(${ref("developmentFeeInputMode")}="amount",ISNUMBER(${ref("developmentFeeInputAmount")}),${ref("developmentFeeInputAmount")}>=0),${ref("developmentFeeInputAmount")},B${dmFeeBaseRow}*MAX(0,IF(${costBasisModeRef}="loaded_aggregate",${ref("developmentFeeInputRate")},${ref("devManagementFeeRate")})))`,
    d.developmentManagementFee,
    NF.currency,
  );
  r++;

  // ── TOTALS ──
  section("TOTALS");
  const totalCostRow = kvf(
    "Total developer cost",
    `B${preMgmtRow}+B${devMgmtFeeRow}`,
    d.totalCost,
    NF.currency,
  );
  // W2: Lease incentive — landlord contribution + rent-free equivalent. Displayed as a memo item
  // (not included in totalCost to preserve all existing IRR/margin/yield model invariants).
  const leaseIncentiveCostRow = kv(
    "Landlord incentive PV (memo; not in TDC)",
    d.leaseIncentiveCost,
    false,
    NF.currency,
  );
  ws.getCell(r - 1, 3).value =
    "Lump-sum contribution + rent-free equivalent at commencement. Developer cost; not in TDC — see Rent Bridge for effective-rent impact.";
  styleNote(ws.getCell(r - 1, 3));
  r++;

  // ── EXIT ──
  section("EXIT");
  // P4-R2/#18: NOI components are the developer's LL RENT on the amenities (operator hire/sublet income is
  // NO LONGER cross-booked into landlord NOI — it stays operator revenue on the Op P&L sheet).
  const sportsRentRow = kvf(
    "Sports field LL rent",
    `${ref("sportsFieldAreaSqm")}*${ref("sportsFieldRentPerSqm")}`,
    d.sportsFieldRent,
    NF.currency,
  );
  const parkingRentRow = kvf(
    "Parking LL rent",
    `${ref("parkingBays")}*${ref("parkingRentPerBay")}`,
    d.parkingRent,
    NF.currency,
  );
  const coveredPlayAreaRentRow = kvf(
    "Covered play area ground rent",
    `${ref("coveredPlayAreaGroundRent")}`,
    d.coveredPlayAreaRent,
    NF.currency,
  );

  const netRentY1Row = kv(
    "Net rent Y1",
    { formula: "=" + bldgRefs.totalNetRentY1, result: d.netRentYear1 },
    true,
    NF.currency,
  );
  const noiRow = kvf(
    "NOI",
    `B${netRentY1Row}+B${sportsRentRow}+B${parkingRentRow}+B${coveredPlayAreaRentRow}`,
    d.noi,
    NF.currency,
  );
  // Exit valuation capitalises the run-rate of the LEASE year containing the exit month (project year
  // exitYr × 12 months, less the construction period) — mirrors compute()'s exitLeaseYear.
  const exitLeaseYear = useExitYear
    ? exitLeaseYearFor(exitYr, d.lastCompletionMonth)
    : 0;
  const grossRealRow = useExitYear
    ? kvf(
        "Gross realisation",
        `IFERROR((SUMPRODUCT(--(${bldgRefs.onlineYearRange}<=${exitLeaseYear}),${bldgRefs.nlaRange},${bldgRefs.netRentRateRange})+B${sportsRentRow}+B${parkingRentRow}+B${coveredPlayAreaRentRow})*POWER(1+${ref("effectiveRentGrowthResolved")},${exitLeaseYear - 1})/${ref("capRate")},0)`,
        d.grossRealisation,
        NF.currency,
      )
    : kvf(
        "Gross realisation",
        `IFERROR(B${noiRow}/${ref("capRate")},0)`,
        d.grossRealisation,
        NF.currency,
      );
  if (useExitYear)
    exitNote(
      grossRealRow,
      `Live exit valuation: project year Y${a.developerExitYear} (month ${exitYr * 12}) is lease year ${exitLeaseYear} after PC at month ${Math.round(d.lastCompletionMonth)} — landlord NOI of that lease year (building online schedule + resolved rent growth) ÷ cap rate.`,
    );
  const sellingCostsRow = kvf(
    "Selling costs",
    `B${grossRealRow}*${ref("agentsFeeRate")}`,
    d.sellingCosts,
    NF.currency,
  );
  const netRealRow = kvf(
    "Net realisation",
    `B${grossRealRow}-B${sellingCostsRow}`,
    d.netRealisation,
    NF.currency,
  );
  const profitRow = kvf(
    "Profit",
    `B${netRealRow}-B${totalCostRow}`,
    d.profit,
    NF.currency,
  );
  const marginRow = kvf(
    MARGIN_ON_COST_LABEL,
    `B${profitRow}/B${totalCostRow}`,
    d.marginOnCost,
    NF.pct2,
  );
  const yieldOnCostRow = kvf(
    YIELD_ON_COST_LABEL,
    `B${noiRow}/B${totalCostRow}`,
    d.yieldOnCost,
    NF.pct2,
  );
  r++;

  // ── DEBT FACILITY ──
  section("DEBT FACILITY");
  // P5-R1/D1: the facility is sized on the EX-HOLDING cost base (it funds construction + the non-finance
  // DM fee, not the equity-funded holding carry). For the default fee toggles that base is TDC − holding.
  const grossFacilityLimitRow = kvf(
    "Gross debt capacity",
    `(B${totalCostRow}-B${holdingInterestRow})*${ref("constructionLvrRate")}`,
    d.grossFacilityLimitDev,
    NF.currency,
  );
  const contributionAppliedRow = kvf(
    "Operator contribution applied to debt",
    `MIN(${ref("reddamCapexContribution")},B${grossFacilityLimitRow})`,
    d.operatorContributionApplied,
    NF.currency,
  );
  const facilityLimitRow = kvf(
    "Construction facility limit",
    `MAX(0,B${grossFacilityLimitRow}-B${contributionAppliedRow})`,
    d.facilityLimitDev,
    NF.currency,
  );
  const equityReqRow = kvf(
    EQUITY_REQUIRED_LABEL,
    `MAX(0,B${totalCostRow}-B${grossFacilityLimitRow})`,
    d.equityRequired,
    NF.currency,
  );
  const peakDebtRow = kvf(
    PEAK_CONSTRUCTION_DEBT_LABEL,
    `IF(${ref("contributionTiming")}="completion",B${grossFacilityLimitRow},B${facilityLimitRow})`,
    d.peakDebtDev,
    NF.currency,
  );
  const lineFeeRow = kvf(
    LINE_FEE_ONE_OFF_LABEL,
    `IF(${ref("contributionTiming")}="completion",B${grossFacilityLimitRow},B${facilityLimitRow})*${ref("constructionLineFeeRate")}`,
    d.lineFee,
    NF.currency,
  );
  const constructionInterestRow = kvf(
    CONSTRUCTION_INTEREST_LABEL,
    `SUM('Debt Schedule'!F5:F${4 + Math.max(1, Math.round(d.lastCompletionMonth))})`,
    d.constructionInterest,
    NF.currency,
  );
  const allInCostRow = kvf(
    ALL_IN_COST_LABEL,
    `B${totalCostRow}+B${lineFeeRow}+B${constructionInterestRow}`,
    d.allInCost,
    NF.currency,
  );
  // Term loan is sized on the AT-COMPLETION valuation (base gross), independent of the exit year — so
  // when the gross-realisation cell above holds the exit-year value, stamp this as a model literal too.
  const termLoanLimitRow = useExitYear
    ? kvf(
        "Term loan limit",
        `IFERROR(B${noiRow}/${ref("capRate")}*${ref("termLoanLvrRate")},0)`,
        d.termLoanLimit,
        NF.currency,
      )
    : kvf(
        "Term loan limit",
        `B${grossRealRow}*${ref("termLoanLvrRate")}`,
        d.termLoanLimit,
        NF.currency,
      );
  if (useExitYear)
    exitNote(
      termLoanLimitRow,
      "Live formula — sized on the at-completion valuation (independent of the exit year).",
    );
  const termLoanAnnualServiceRow = kvf(
    "Term loan annual service",
    `B${termLoanLimitRow}*(${ref("holdingInterestRate")}+${ref("termLoanMarginRate")})`,
    d.termLoanAnnualService,
    NF.currency,
  );
  r++;

  // ── RETURNS ──
  section("RETURNS");
  const irrRow = kvf(
    "IRR (ungeared, sale at completion)",
    `IRR(${cf.ungearedRange})`,
    d.irr,
    NF.pct2,
  );
  // Σcf-scope disclosure (loop#016, review-corrected): the identity holds only at 0%
  // construction LVR — this vector books FULL holding interest while total cost carries the
  // equity-scaled share. State it so it is not over-applied to the geared or hold-and-sell streams.
  exitNote(
    irrRow,
    "Cashflow basis: the ungeared CF books the FULL holding interest with no debt, so Σcf equals net realisation − total cost only at 0% construction LVR (total cost carries the equity-scaled holding share); the geared CF nets debt draws/repayment; the hold-and-sell CF swaps the sale-at-completion for exit-basis proceeds plus hold-period landlord NOI.",
  );
  let ungearedEarlyExitIrrRow = 0;
  if (useExitYear) {
    ungearedEarlyExitIrrRow = kvf(
      `IRR (ungeared, project Y${exitYr} hold-and-sell)`,
      `IRR(${cf.ungearedEarlyExitRange})`,
      d.ungearedEarlyExitIrr,
      NF.pct2,
    );
  }
  const gearedIrrRow = kvf(
    "IRR (geared, sale at completion)",
    `IRR(${cf.gearedDevExitRange})`,
    d.gearedDevelopmentIrr,
    NF.pct2,
  );
  // Y5-primary follow-on: when the developer exits before the model horizon the PRIMARY geared IRR is
  // the hold-to-exit figure. Surface it as a live =IRR() over the early-exit geared CF (column E) so the
  // export agrees with the in-app headline. Gated on useExitYear so the default at-PC export is
  // byte-identical (no row, no row-shift); cf.gearedEarlyExitRange is "" when not early-exit.
  let gearedEarlyExitIrrRow = 0;
  if (useExitYear) {
    gearedEarlyExitIrrRow = kvf(
      `IRR (geared, project Y${exitYr} early exit)`,
      `IRR(${cf.gearedEarlyExitRange})`,
      d.gearedEarlyExitIrr,
      NF.pct2,
    );
  }
  const gearedWholeLifeIrrRow = kvf(
    gearedRefinanceExitLabel(a.termLoanHoldYears),
    `IRR(${cf.gearedWholeOfLifeRange})`,
    d.gearedWholeOfLifeIrr,
    NF.pct2,
  );
  const primaryHorizon = useExitYear ? `Y${exitYr}` : "PC";
  const primaryIrrSourceRow = useExitYear ? ungearedEarlyExitIrrRow : irrRow;
  const primaryIrrRow = kvf(
    `Primary IRR (ungeared, ${primaryHorizon})`,
    `B${primaryIrrSourceRow}`,
    k.developerPrimaryIrr,
    NF.pct2,
  );
  const yieldCashRow = kvf(
    NOI_ON_DEVELOPER_EQUITY_LABEL,
    `IFERROR(B${noiRow}/B${equityReqRow},0)`,
    d.yieldOnCash,
    NF.pct2,
  );
  // P5-R2/D3: TRUE equity multiple — total equity distributions of the SELECTED strategy ÷ equity.
  // Sell-at-PC: PC net realisation − facility − construction debt costs (interest + line fee, both live
  // from the monthly debt schedule above). Early exit: the PC refinance leg (term loan − facility − debt
  // costs) plus the hold-period entries of the early-exit geared cashflow on Dev Cashflow (column E,
  // months PC+1 .. exit — each already net of term-loan service; the exit month carries the sale net of
  // selling costs and the term-loan repayment). Mirrors compute()'s equityDistributions.
  const moneyMultipleRow = useExitYear
    ? (() => {
        const firstDataRow = 3; // Dev Cashflow: row 1 note, row 2 headers, row 3 = month 0
        const months = d.lastCompletionMonth;
        const exitMonths = exitYr * 12;
        // Exit sale net of selling costs and the term-loan repayment — booked as its own live row so the
        // exit month's interim leg can be separated from it (Dev Cashflow column E carries both in one
        // cell), and so the formula stays correct when the exit month IS the PC month (no hold period,
        // and the PC row also carries construction equity).
        const exitLegRow = kvf(
          `Exit sale net of selling costs and term loan (Y${exitYr})`,
          `B${grossRealRow}*(1-${ref("agentsFeeRate")})-B${termLoanLimitRow}`,
          d.grossRealisation * (1 - a.agentsFeeRate) - d.termLoanLimit,
          NF.currency,
        );
        const refinanceLeg = `(B${termLoanLimitRow}-B${facilityLimitRow}-B${lineFeeRow}-B${constructionInterestRow})`;
        const exitLeg = `B${exitLegRow}`;
        // Pure interim months (NOI − term service): PC+1 .. exit−1. The exit month's interim leg is the
        // exit row less the exit sale leg; it is zero when the exit month is the PC month.
        const hasInterim = exitMonths - 1 >= months + 1;
        const interim = `'Dev Cashflow'!$E$${firstDataRow + months + 1}:$E$${firstDataRow + exitMonths - 1}`;
        const exitInterim =
          exitMonths > months
            ? `('Dev Cashflow'!$E$${firstDataRow + exitMonths}-${exitLeg})`
            : "0";
        // MOIC: positive legs are distributions; negative legs (PC top-up, uncovered term service, a
        // loss on exit) are further equity contributions — never netted against distributions.
        const positive =
          `MAX(0,${refinanceLeg})+MAX(0,${exitInterim})+MAX(0,${exitLeg})` +
          (hasInterim ? `+SUMIF(${interim},">0")` : "");
        const negative =
          `MAX(0,-${refinanceLeg})+MAX(0,-${exitInterim})+MAX(0,-${exitLeg})` +
          (hasInterim ? `-SUMIF(${interim},"<0")` : "");
        return kvf(
          `Money multiple (equity, project Y${exitYr} exit)`,
          `IFERROR((${positive})/(B${equityReqRow}+${negative}),0)`,
          d.moneyMultiple,
          NF.multiple,
        );
      })()
    : kvf(
        "Money multiple (equity)",
        `IFERROR(MAX(0,B${netRealRow}-B${facilityLimitRow}-B${lineFeeRow}-B${constructionInterestRow})/(B${equityReqRow}+MAX(0,-(B${netRealRow}-B${facilityLimitRow}-B${lineFeeRow}-B${constructionInterestRow}))),0)`,
        d.moneyMultiple,
        NF.multiple,
      );
  kvf(
    `Primary hurdle met (${primaryHorizon})`,
    `IF(B${primaryIrrRow}>=${ref("developerHurdleIRR")},"Yes","No")`,
    k.developerPrimaryHurdleMet ? "Yes" : "No",
  );
  kvf(
    `Primary hurdle status (${primaryHorizon})`,
    `IF(B${primaryIrrRow}>=${ref("developerHurdleIRR")}+0.005,"met",IF(B${primaryIrrRow}>=${ref("developerHurdleIRR")}-0.005,"marginal","below"))`,
    k.developerPrimaryHurdleStatus,
  );

  ws.columns = [{ width: 36 }, { width: 20 }];

  // Return key row references for cross-sheet linking
  return {
    totalCostRow,
    netRealRow,
    profitRow,
    marginRow,
    noiRow,
    grossRealRow,
    facilityLimitRow,
    grossFacilityLimitRow,
    contributionAppliedRow,
    peakDebtRow,
    hardConstrRow,
    siteWorksRow,
    prelimRow,
    builderMarginRow,
    amenityConstrRow,
    constructionInterestRow,
    allInCostRow,
    termLoanLimitRow,
    termLoanAnnualServiceRow,
    equityReqRow,
    yieldOnCostRow,
    yieldCashRow,
    moneyMultipleRow,
    netRentY1Row,
    sportsRentRow,
    parkingRentRow,
    coveredPlayAreaRentRow,
    preMgmtRow,
    devMgmtFeeRow,
    irrRow,
    ungearedEarlyExitIrrRow,
    gearedIrrRow,
    gearedEarlyExitIrrRow,
    gearedWholeLifeIrrRow,
    primaryIrrRow,
    // Rent Bridge cost build-up references (C-redesign)
    landAcqRow,
    holdingInterestRow,
    lineFeeRow,
    consultantRow,
    statutoryRow,
    planningRow,
    contingencyRow,
    otherDdRow,
    tenantRow,
    approvalShareRow,
    leaseIncentiveCostRow,
  };
}

/* ── Dev Cashflow sheet ──
   Monthly cashflow vectors used by =IRR() formulas in Dev Cost.
   Values are model outputs (blue = input), IRRs are formulas. */
export function buildDevCashflow(wb: Workbook, d: Developer, a: Assumptions) {
  const ws = wb.addWorksheet("Dev Cashflow");
  ws.views = [{ state: "frozen", ySplit: 1 }];
  const { exitYear: exitYr, earlyExit } = exitBasisFor(a);

  // Header note
  ws.getCell(1, 1).value =
    "Model inputs — monthly cashflow vectors output by the model (not Excel formulas). The IRR cells on the Dev Cost sheet read these ranges via =IRR(); edit a cell here to re-run those IRRs.";
  styleNote(ws.getCell(1, 1));
  ws.mergeCells(earlyExit ? "A1:F1" : "A1:E1");

  const headers = [
    "Month",
    "Ungeared CF",
    "Geared sale-at-completion CF",
    `Geared refi/hold ${a.termLoanHoldYears}y/exit CF`,
    earlyExit
      ? `Early-exit geared CF (Y${exitYr})`
      : "Early-exit geared CF (exit at horizon)",
    earlyExit
      ? `Ungeared hold-and-sell CF (project Y${exitYr})`
      : "Ungeared hold-and-sell CF (exit at horizon)",
  ];
  const headerRow = ws.addRow(headers);
  headerRow.eachCell((cell) => styleHeader(cell));

  const maxLen = Math.max(
    d.cashflowUngeared.length,
    d.cashflowGearedDevExit.length,
    d.cashflowGearedWholeOfLife.length,
    d.cashflowGearedEarlyExit.length,
    d.cashflowUngearedEarlyExit.length,
  );

  for (let i = 0; i < maxLen; i++) {
    const row = ws.addRow([
      i,
      i < d.cashflowUngeared.length ? Math.round(d.cashflowUngeared[i]) : null,
      i < d.cashflowGearedDevExit.length
        ? Math.round(d.cashflowGearedDevExit[i])
        : null,
      i < d.cashflowGearedWholeOfLife.length
        ? Math.round(d.cashflowGearedWholeOfLife[i])
        : null,
      i < d.cashflowGearedEarlyExit.length
        ? Math.round(d.cashflowGearedEarlyExit[i])
        : null,
      i < d.cashflowUngearedEarlyExit.length
        ? Math.round(d.cashflowUngearedEarlyExit[i])
        : null,
    ]);
    row.eachCell((cell, colNumber) => {
      thinBorder(cell);
      if (colNumber === 1) {
        styleLabel(cell);
        cell.numFmt = NF.num;
      } else if (colNumber > 1) {
        styleFormula(cell);
        cell.numFmt = NF.currency;
        cell.alignment = { horizontal: "right" };
      }
    });
  }

  ws.columns = [
    { width: 10 },
    { width: 18 },
    { width: 20 },
    { width: 24 },
    { width: 24 },
    { width: 24 },
  ];

  // Return row references for IRR formulas
  // Row 1 = note, Row 2 = headers, Row 3+ = data
  const firstDataRow = 3;
  const range = (column: string, length: number) =>
    `'Dev Cashflow'!$${column}$${firstDataRow}:$${column}$${2 + length}`;
  return {
    ungearedRange: range("B", d.cashflowUngeared.length),
    gearedDevExitRange: range("C", d.cashflowGearedDevExit.length),
    gearedWholeOfLifeRange: range("D", d.cashflowGearedWholeOfLife.length),
    gearedEarlyExitRange: earlyExit
      ? range("E", d.cashflowGearedEarlyExit.length)
      : "",
    ungearedEarlyExitRange: earlyExit
      ? range("F", d.cashflowUngearedEarlyExit.length)
      : "",
    maxLen,
  };
}

/* ── Op P&L sheet (full formulas) ──
   Year-by-year operating model. Every data cell is a formula.
   References: Assumptions sheet + Building Schedule sheet. */
export function buildOpPL(
  wb: Workbook,
  years: Year[],
  a: Assumptions,
  refs: Map<string, string>,
  bldgRefs: ReturnType<typeof buildBuildingSchedule>,
  kpis: Pick<KPIs, "reddamIrr" | "reddamNpv">,
  totalReddamCapex: number,
) {
  // P8-R1 (#19): the engine escalates rent by effectiveRentGrowth (method-resolved), NOT rentIndexationPct;
  // the cached back-outs + the live formulas below must use the same resolved rate to reconcile.
  const effRentGrowth = Model.effectiveRentGrowth(a);
  const ws = wb.addWorksheet("Op P&L");
  ws.views = [{ state: "frozen", ySplit: 3, xSplit: 1 }];

  const ref = (key: string) => refs.get(key) ?? "";
  const ny = years.length;
  const reddamIrr = kpis.reddamIrr;

  // ── Intermediate calcs (rows above the main table) ──
  let r = 1;

  // Row 1: Blended tuition fee Y1
  ws.getCell(r, 1).value = "Blended tuition fee Y1";
  styleLabel(ws.getCell(r, 1));
  // loop#012: mirror blendedTuitionFee exactly — per-stage MAX(0, fee)×MAX(0, share),
  // shares SUM-normalised (stage mixes summing to ≠1 are valid, info-flagged input
  // space), falling back to feePerStudentYear1 when the blended fee is 0. The old raw
  // `fee×share` weighted sum re-summed shares as if they totalled 1 and drifted ~5%
  // through every downstream KPI on a 0.95 stage mix.
  const stagePairs: Array<[string, string]> = [
    ["earlyLearningFeeYear1", "earlyLearningShare"],
    ["primaryFeeYear1", "primaryShare"],
    ["secondaryFeeYear1", "secondaryShare"],
    ["seniorFeeYear1", "seniorShare"],
  ];
  const shareDen = stagePairs
    .map(([, share]) => `MAX(0,${ref(share)})`)
    .join("+");
  const weightedSum = stagePairs
    .map(([fee, share]) => `MAX(0,${ref(fee)})*MAX(0,${ref(share)})`)
    .join("+");
  const blendedFeeFormula =
    `IF((${shareDen})=0,${ref("feePerStudentYear1")},` +
    `IF((${weightedSum})>0,(${weightedSum})/(${shareDen}),${ref("feePerStudentYear1")}))`;
  // Simplify: we compute the blended fee result in JS and use it
  const blendedFeeY1 = years[0]?.headlineFee ?? a.feePerStudentYear1;
  setFormula(ws.getCell(r, 2), blendedFeeFormula, Math.round(blendedFeeY1), {
    numFmt: NF.currency,
  });
  const blendedFeeCell = addr(r - 1, 1); // B1
  r++;

  // Row 2: Total operator capex exactly mirrors model totalOpCapex: global fitout + per-building
  // Reddam capex + contribution + Reddam's share of split approvals. This is the period-0 outflow
  // used by the operator NPV/IRR and the fitout-amortisation base.
  ws.getCell(r, 1).value =
    "Total operator capex (fitout + building capex + contribution + Reddam approval share)";
  styleLabel(ws.getCell(r, 1));
  const reddamApprovalShareOp =
    ((a.ssdaCost ?? 0) +
      (a.changeOfUseCost ?? 0) +
      (a.otherApprovalCosts ?? 0)) *
    Math.min(1, Math.max(0, a.approvalCostReddamSharePct ?? 0.5));
  const totalOpCapexResult =
    a.reddamFitoutAllowance +
    bldgRefs.totalReddamCapexResult +
    // A6: same clamp as compute() so the cached total never disagrees with the engine.
    Math.max(
      0,
      Number.isFinite(a.reddamCapexContribution)
        ? a.reddamCapexContribution
        : 0,
    ) +
    reddamApprovalShareOp;
  setFormula(
    ws.getCell(r, 2),
    `${ref("reddamFitoutAllowance")}+${bldgRefs.totalReddamCapex}+${ref("reddamCapexContribution")}+(${ref("ssdaCost")}+${ref("changeOfUseCost")}+${ref("otherApprovalCosts")})*MIN(1,MAX(0,${ref("approvalCostReddamSharePct")}))`,
    Math.round(totalOpCapexResult),
    { numFmt: NF.currency },
  );
  const fitoutCapexCell = addr(r - 1, 1); // B2
  r++;

  // Row 3: Sports income (annual)
  ws.getCell(r, 1).value = "Sports income (annual)";
  styleLabel(ws.getCell(r, 1));
  const sportsIncomeResult = a.sportsFieldAreaSqm * a.sportsFieldIncomePerSqm;
  setFormula(
    ws.getCell(r, 2),
    `${ref("sportsFieldAreaSqm")}*${ref("sportsFieldIncomePerSqm")}`,
    sportsIncomeResult,
    { numFmt: NF.currency },
  );
  const sportsIncomeCell = addr(r - 1, 1); // B3
  r++;

  // Row 4: Parking income (annual). D3 (#20): the flat per-bay base applies ONLY to non-event, non-staff
  // bays (event bays earn their own fees; staff-reserved bays aren't casually let) — MAX(0,…) guards a
  // negative bay count when allocations exceed total bays.
  const flatParkingBays = Math.max(
    0,
    a.parkingBays -
      a.weekdayParentParkingBays -
      a.weekendSportsParkingBays -
      a.staffReservedParkingBays,
  );
  const parkingIncomeResult =
    flatParkingBays * a.parkingIncomePerBay +
    a.weekdayParentParkingBays *
      a.weekdayParentParkingFee *
      a.weekdayParentParkingDays +
    a.weekendSportsParkingBays *
      a.weekendSportsParkingFee *
      a.weekendSportsParkingDays;
  const parkingIncomeFormula = `MAX(0,${ref("parkingBays")}-${ref("weekdayParentParkingBays")}-${ref("weekendSportsParkingBays")}-${ref("staffReservedParkingBays")})*${ref("parkingIncomePerBay")}+${ref("weekdayParentParkingBays")}*${ref("weekdayParentParkingFee")}*${ref("weekdayParentParkingDays")}+${ref("weekendSportsParkingBays")}*${ref("weekendSportsParkingFee")}*${ref("weekendSportsParkingDays")}`;
  setFormula(ws.getCell(r, 2), parkingIncomeFormula, parkingIncomeResult, {
    numFmt: NF.currency,
  });
  const parkingIncomeCell = addr(r - 1, 1); // B4
  r++;

  // blank row
  ws.getCell(r, 1).value = "";
  r++;

  // ── Main table header ──
  const header = ["Line item", ...years.map((y) => `Y${y.year}`)];
  const headerRow = ws.addRow(header);
  headerRow.eachCell((cell) => styleHeader(cell));
  r++;

  const tableStartRow = r; // First data row after header

  // Build static value arrays for each row
  const rows = [
    {
      key: "capacityBasis",
      label: CAPACITY_LABELS.enrolmentCapacityBasis,
      fmt: NF.num,
      vals: years.map((y) =>
        resolveEnrolmentCapacityBasis(
          a.targetEnrolment,
          bldgRefs.livePhysicalCapacityAtYear(y.year),
        ),
      ),
    },
    {
      key: "liveNetRent",
      label: "Live net rent",
      fmt: NF.currency,
      vals: years.map((y) =>
        Math.round(y.rentNet / Math.pow(1 + effRentGrowth, y.year - 1)),
      ),
    },
    {
      key: "liveOutgoings",
      label: "Live outgoings",
      fmt: NF.currency,
      vals: years.map((y) =>
        Math.round(y.outgoings / Math.pow(1 + effRentGrowth, y.year - 1)),
      ),
    },
    {
      key: "enrolment",
      label: "Enrolment",
      fmt: NF.num1,
      vals: years.map((y) => Math.round(y.enrolment)),
    },
    {
      key: "utilisation",
      label: "Utilisation",
      fmt: NF.pct,
      vals: years.map((y) => y.utilisation),
    },
    {
      key: "headlineFee",
      label: "Headline fee",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.headlineFee)),
    },
    {
      key: "discountFactor",
      label: "Discount factor",
      fmt: NF.pct2,
      vals: years.map((y) => y.effectiveFee / (y.headlineFee || 1)),
    },
    {
      key: "effectiveFee",
      label: "Effective fee",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.effectiveFee)),
    },
    {
      key: "tuitionRev",
      label: "Tuition revenue",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.tuitionRevenue)),
    },
    {
      key: "ancillaryRev",
      label: "Ancillary revenue",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.ancillaryRevenue)),
    },
    {
      key: "totalRev",
      label: "TOTAL REVENUE",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.totalRevenue)),
      total: true,
    },
    {
      key: "livePhysicalCapacity",
      label: CAPACITY_LABELS.scheduledClassroomSeats,
      fmt: NF.num,
      vals: years.map((y) => bldgRefs.livePhysicalCapacityAtYear(y.year)),
    },
    {
      key: "rentNet",
      label: "Rent (net)",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.rentNet)),
    },
    {
      key: "outgoings",
      label: "Outgoings",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.outgoings)),
    },
    {
      key: "rentGross",
      label: "Rent (gross)",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.rentGross)),
    },
    {
      key: "amenityRent",
      label: "Amenity LL rent (sports + parking + covered)",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.amenityRent)),
    },
    {
      key: "variable",
      label: "Variable costs",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.variableCosts)),
    },
    {
      key: "fixedOpex",
      label: "Fixed opex",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.fixedOpex)),
    },
    { key: "blank2", label: "", fmt: NF.text, vals: Array(ny).fill("") },
    // Cash-flow-from-operations shape (P4-R1): EBITDA (true, excl. D&A) → −amortisation → −tax →
    // net income → +amortisation → after-tax operating cash. Fit-out is a cash outflow once, at T0.
    {
      key: "ebitda",
      label: "EBITDA",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.reddamEbitda)),
      total: true,
    },
    {
      key: "fitout",
      label: "Fitout amortisation (non-cash)",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.fitoutAmortisation)),
    },
    // P6-R1 (#7): NOL carry-forward pool (end of year) = MAX(0, prior − taxable); losses shield later tax.
    {
      key: "nolPool",
      label: "NOL pool (carry-forward)",
      fmt: NF.currency,
      vals: (() => {
        let p = 0;
        return years.map((y) => {
          p = Math.max(0, p - (y.reddamEbitda - y.fitoutAmortisation));
          return Math.round(p);
        });
      })(),
    },
    {
      key: "tax",
      label: "Tax",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.tax)),
    },
    {
      key: "netIncome",
      label: "Net income",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.reddamNetIncome)),
      total: true,
    },
    {
      key: "opCash",
      label: "Operating cashflow (amort added back)",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.reddamOperatingCashflow)),
      total: true,
    },
    { key: "blank3", label: "", fmt: NF.text, vals: Array(ny).fill("") },
    {
      key: "cumulative",
      label: "Cumulative cashflow",
      fmt: NF.currency,
      vals: years.map((y) => Math.round(y.cumulativeCashflow)),
    },
    {
      key: "rentPct",
      label: "Rent % revenue",
      fmt: NF.pct,
      vals: years.map((y) => y.rentPctRevenue),
    },
  ];

  // Add each row
  rows.forEach((rowDef) => {
    const line: (string | number | { formula: string; result: number })[] = [
      rowDef.label,
      ...rowDef.vals,
    ];
    const row = ws.addRow(line);
    row.eachCell((cell, colNumber) => {
      thinBorder(cell);
      const isLabel = colNumber === 1;
      if (isLabel) {
        styleLabel(cell, !!rowDef.total);
      } else {
        cell.numFmt = rowDef.fmt;
      }
    });
  });

  // Now apply formulas to specific rows
  const baseDataRow = tableStartRow; // Excel row number of first data row

  // Build a single row-key lookup map so every formula address resolves in O(1).
  const rowIndexByKey = new Map(rows.map((r, i) => [r.key, i]));
  const rowOffsetOf = (rowKey: string) => {
    const offset = rowIndexByKey.get(rowKey);
    if (offset === undefined) {
      throw new Error(`buildOpPL: unknown rowKey "${rowKey}"`);
    }
    return offset;
  };

  // Absolute Excel row number for a data row (used by downstream sheets).
  const rowOf = (rowKey: string) => baseDataRow + rowOffsetOf(rowKey);

  // Helper to get Excel cell address for a data row
  const dataAddr = (rowKey: string, col: number) =>
    addr(baseDataRow + rowOffsetOf(rowKey) - 1, col - 1);

  // ── Formula wiring ──

  // Extract Building Schedule data row range from totals reference
  const totalRowMatch = bldgRefs.totalCapacity.match(/\$(\d+)$/);
  const bsTotalRow = totalRowMatch ? parseInt(totalRowMatch[1], 10) : 2;
  const bsLastDataRow = Math.max(2, bsTotalRow - 1);
  const bsOnlineRange = `'Building Schedule'!$L$2:$L$${bsLastDataRow}`;
  const bsCapacityRange = `'Building Schedule'!$D$2:$D$${bsLastDataRow}`;
  const bsNetRentRange = `'Building Schedule'!$I$2:$I$${bsLastDataRow}`;
  const bsOutgoingsRange = `'Building Schedule'!$K$2:$K$${bsLastDataRow}`;

  // Enrolment capacity basis = positive planning target, otherwise the staged physical seats.
  const capacityBasisRow = rowOf("capacityBasis");
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(capacityBasisRow, col);
    const physicalSeats = bldgRefs.livePhysicalCapacityAtYear(y);
    setFormula(
      cell,
      `IF(${ref("targetEnrolment")}>0,${ref("targetEnrolment")},SUMIF(${bsOnlineRange},"<="&${y},${bsCapacityRange}))`,
      resolveEnrolmentCapacityBasis(a.targetEnrolment, physicalSeats),
      { numFmt: NF.num },
    );
  }

  // Scheduled classroom seats remain visible as the staged physical input.
  const livePhysicalCapacityRow = rowOf("livePhysicalCapacity");
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    setFormula(
      ws.getCell(livePhysicalCapacityRow, col),
      `SUMIF(${bsOnlineRange},"<="&${y},${bsCapacityRange})`,
      bldgRefs.livePhysicalCapacityAtYear(y),
      { numFmt: NF.num },
    );
  }

  // Live net rent = SUMIF on Building Schedule (net rent Y1 where online <= year)
  const liveNetRentOffset = rowOffsetOf("liveNetRent");
  const liveNetRentRow = baseDataRow + liveNetRentOffset;
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(liveNetRentRow, col);
    setFormula(
      cell,
      `SUMIF(${bsOnlineRange},"<="&${y},${bsNetRentRange})`,
      Math.round(years[i].rentNet / Math.pow(1 + effRentGrowth, y - 1)),
      { numFmt: NF.currency },
    );
  }

  // Live outgoings = SUMIF on Building Schedule (outgoings Y1 where online <= year)
  const liveOutgoingsOffset = rowOffsetOf("liveOutgoings");
  const liveOutgoingsRow = baseDataRow + liveOutgoingsOffset;
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(liveOutgoingsRow, col);
    setFormula(
      cell,
      `SUMIF(${bsOnlineRange},"<="&${y},${bsOutgoingsRange})`,
      Math.round(years[i].outgoings / Math.pow(1 + effRentGrowth, y - 1)),
      { numFmt: NF.currency },
    );
  }

  // Utilisation = IF(year<=ramp, start+(steady-start)*ease, steady)
  const utilisationOffset = rowOffsetOf("utilisation");
  const utilisationRow = baseDataRow + utilisationOffset;
  const rampYearsRef = ref("rampYears") || String(a.rampYears);
  const startUtilRef =
    ref("startingUtilisation") || String(a.startingUtilisation);
  const steadyUtilRef =
    ref("steadyStateUtilisation") || String(a.steadyStateUtilisation);
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(utilisationRow, col);
    const rampProgress = `(${y}-1)/MAX(1,${rampYearsRef}-1)`;
    const utilFormula = `IF(OR(${rampYearsRef}<=1,${y}>=${rampYearsRef}),${steadyUtilRef},${startUtilRef}+(${steadyUtilRef}-${startUtilRef})*POWER(${rampProgress},2)*(3-2*${rampProgress}))`;
    setFormula(cell, utilFormula, years[i].utilisation, { numFmt: NF.pct });
  }

  // Enrolment = disclosed capacity basis * utilisation.
  const enrolmentOffset = rowOffsetOf("enrolment");
  const enrolmentRow = baseDataRow + enrolmentOffset;
  for (let i = 0; i < ny; i++) {
    const col = i + 2;
    const cell = ws.getCell(enrolmentRow, col);
    const capCell = dataAddr("capacityBasis", col);
    const utilCell = dataAddr("utilisation", col);
    setFormula(cell, `${capCell}*${utilCell}`, Math.round(years[i].enrolment), {
      numFmt: NF.num1,
    });
  }

  // Headline fee = blendedFee * (1 + growth)^(year-1)
  const headlineFeeOffset = rowOffsetOf("headlineFee");
  const headlineFeeRow = baseDataRow + headlineFeeOffset;
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(headlineFeeRow, col);
    setFormula(
      cell,
      `${blendedFeeCell}*POWER(1+${ref("tuitionGrowthPct")},${y - 1})`,
      Math.round(years[i].headlineFee),
      { numFmt: NF.currency },
    );
  }

  // Discount factor = IF(year <= decay, 1 - founding - sibling, 1)
  const discountOffset = rowOffsetOf("discountFactor");
  const discountRow = baseDataRow + discountOffset;
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(discountRow, col);
    const decay = a.discountDecayYears;
    const founding = a.foundingFamilyDiscount;
    const sibling = a.siblingDiscount;
    const result = y <= decay ? Math.max(0, 1 - founding - sibling) : 1;
    setFormula(
      cell,
      `IF(${y}<=${decay},MAX(0,1-${founding}-${sibling}),1)`,
      result,
      { numFmt: NF.pct2 },
    );
  }

  // Effective fee = headline fee * discount factor
  const effectiveFeeOffset = rowOffsetOf("effectiveFee");
  const effectiveFeeRow = baseDataRow + effectiveFeeOffset;
  for (let i = 0; i < ny; i++) {
    const col = i + 2;
    const cell = ws.getCell(effectiveFeeRow, col);
    const hfCell = dataAddr("headlineFee", col);
    const dfCell = dataAddr("discountFactor", col);
    setFormula(cell, `${hfCell}*${dfCell}`, Math.round(years[i].effectiveFee), {
      numFmt: NF.currency,
    });
  }

  // Tuition revenue = enrolment * effective fee
  const tuitionOffset = rowOffsetOf("tuitionRev");
  const tuitionRow = baseDataRow + tuitionOffset;
  for (let i = 0; i < ny; i++) {
    const col = i + 2;
    const cell = ws.getCell(tuitionRow, col);
    const enrolCell = dataAddr("enrolment", col);
    const effCell = dataAddr("effectiveFee", col);
    setFormula(
      cell,
      `${enrolCell}*${effCell}`,
      Math.round(years[i].tuitionRevenue),
      { numFmt: NF.currency },
    );
  }

  // Ancillary revenue = (sports + parking Y1) escalated at ancillaryGrowthPct (P4-R5/#13 — was flat)
  const ancillaryOffset = rowOffsetOf("ancillaryRev");
  const ancillaryRow = baseDataRow + ancillaryOffset;
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(ancillaryRow, col);
    setFormula(
      cell,
      `(${sportsIncomeCell}+${parkingIncomeCell})*POWER(1+${ref("ancillaryGrowthPct")},${y - 1})`,
      Math.round(years[i].ancillaryRevenue),
      { numFmt: NF.currency },
    );
  }

  // TOTAL REVENUE = tuition + ancillary
  const totalRevOffset = rowOffsetOf("totalRev");
  const totalRevRow = baseDataRow + totalRevOffset;
  for (let i = 0; i < ny; i++) {
    const col = i + 2;
    const cell = ws.getCell(totalRevRow, col);
    const tuitionCell = dataAddr("tuitionRev", col);
    const ancillaryCell = dataAddr("ancillaryRev", col);
    setFormula(
      cell,
      `${tuitionCell}+${ancillaryCell}`,
      Math.round(years[i].totalRevenue),
      { numFmt: NF.currency, bold: true },
    );
  }

  // Rent (net) = liveNetRent * (1+indexation)^(year-1)
  const rentNetOffset = rowOffsetOf("rentNet");
  const rentNetRowNum = baseDataRow + rentNetOffset;
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(rentNetRowNum, col);
    const liveNrCell = dataAddr("liveNetRent", col);
    setFormula(
      cell,
      `${liveNrCell}*POWER(1+${ref("effectiveRentGrowthResolved")},${y - 1})`,
      Math.round(years[i].rentNet),
      { numFmt: NF.currency },
    );
  }

  // Outgoings = liveOutgoings * (1+indexation)^(year-1)
  const outgoingsOffset = rowOffsetOf("outgoings");
  const outgoingsRowNum = baseDataRow + outgoingsOffset;
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(outgoingsRowNum, col);
    const liveOgCell = dataAddr("liveOutgoings", col);
    setFormula(
      cell,
      `${liveOgCell}*POWER(1+${ref("effectiveRentGrowthResolved")},${y - 1})`,
      Math.round(years[i].outgoings),
      { numFmt: NF.currency },
    );
  }

  // Rent (gross) = Rent (net) + Outgoings
  const rentGrossOffset = rowOffsetOf("rentGross");
  const rentGrossRow = baseDataRow + rentGrossOffset;
  for (let i = 0; i < ny; i++) {
    const col = i + 2;
    const cell = ws.getCell(rentGrossRow, col);
    const rnCell = dataAddr("rentNet", col);
    const ogCell = dataAddr("outgoings", col);
    setFormula(cell, `${rnCell}+${ogCell}`, Math.round(years[i].rentGross), {
      numFmt: NF.currency,
    });
  }

  // Amenity LL rent = (sports area × sports LL rent/m² + parking bays × parking LL rent/bay + covered play
  // area ground rent) escalated. Operator pays this to the developer (P4-R2); it sits OUTSIDE building rentGross
  // but is an operating cost.
  const amenityRentOffset = rowOffsetOf("amenityRent");
  const amenityRentRow = baseDataRow + amenityRentOffset;
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(amenityRentRow, col);
    setFormula(
      cell,
      `(${ref("sportsFieldAreaSqm")}*${ref("sportsFieldRentPerSqm")}+${ref("parkingBays")}*${ref("parkingRentPerBay")}+${ref("coveredPlayAreaGroundRent")})*POWER(1+${ref("effectiveRentGrowthResolved")},${y - 1})`,
      Math.round(years[i].amenityRent),
      { numFmt: NF.currency },
    );
  }

  // Variable costs = enrolment * variableCostPerStudent * (1+growth)^(year-1)
  const variableOffset = rowOffsetOf("variable");
  const variableRow = baseDataRow + variableOffset;
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(variableRow, col);
    const enrolCell = dataAddr("enrolment", col);
    setFormula(
      cell,
      `${enrolCell}*${ref("variableCostPerStudent")}*POWER(1+${ref("fixedOpexGrowth")},${y - 1})`,
      Math.round(years[i].variableCosts),
      { numFmt: NF.currency },
    );
  }

  // Fixed opex = fixedOpexY1 * (1+growth)^(year-1)
  const fixedOffset = rowOffsetOf("fixedOpex");
  const fixedRow = baseDataRow + fixedOffset;
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(fixedRow, col);
    setFormula(
      cell,
      `${ref("fixedOpex")}*POWER(1+${ref("fixedOpexGrowth")},${y - 1})`,
      Math.round(years[i].fixedOpex),
      { numFmt: NF.currency },
    );
  }

  // Fitout amortisation = IF(year <= amortYears, totalFitout/amortYears, 0)
  const fitoutOffset = rowOffsetOf("fitout");
  const fitoutRow = baseDataRow + fitoutOffset;
  for (let i = 0; i < ny; i++) {
    const y = years[i].year;
    const col = i + 2;
    const cell = ws.getCell(fitoutRow, col);
    const amortYears = Math.min(a.fitoutAmortisationYears, a.leaseTermYears);
    setFormula(
      cell,
      `IF(AND(${amortYears}>0,${y}<=${amortYears}),${fitoutCapexCell}/${amortYears},0)`,
      Math.round(years[i].fitoutAmortisation),
      { numFmt: NF.currency },
    );
  }

  // EBITDA = TOTAL REVENUE - Rent(gross) - Amenity rent - Variable - FixedOpex  (TRUE EBITDA — excludes
  // D&A, P4-R1; amenity LL rent is a separate operating cost subtracted here, P4-R2)
  const ebitdaOffset = rowOffsetOf("ebitda");
  const ebitdaRow = baseDataRow + ebitdaOffset;
  for (let i = 0; i < ny; i++) {
    const col = i + 2;
    const cell = ws.getCell(ebitdaRow, col);
    const revCell = dataAddr("totalRev", col);
    const rgCell = dataAddr("rentGross", col);
    const amCell = dataAddr("amenityRent", col);
    const varCell = dataAddr("variable", col);
    const fixCell = dataAddr("fixedOpex", col);
    setFormula(
      cell,
      `${revCell}-${rgCell}-${amCell}-${varCell}-${fixCell}`,
      Math.round(years[i].reddamEbitda),
      { numFmt: NF.currency, bold: true },
    );
  }

  // NOL pool (P6-R1, #7) = MAX(0, prior pool − taxable income); taxable = EBITDA − amortisation.
  // A loss (taxable < 0) accumulates into the pool; a profit draws it down. Year-1 prior pool = 0.
  const nolOffset = rowOffsetOf("nolPool");
  const nolRow = baseDataRow + nolOffset;
  for (let i = 0; i < ny; i++) {
    const col = i + 2;
    const cell = ws.getCell(nolRow, col);
    const ebitdaCell = dataAddr("ebitda", col);
    const fitCell = dataAddr("fitout", col);
    const prior = i === 0 ? "0" : dataAddr("nolPool", col - 1);
    setFormula(
      cell,
      `MAX(0,${prior}-(${ebitdaCell}-${fitCell}))`,
      Math.round(rows[nolOffset].vals[i] as number),
      { numFmt: NF.currency },
    );
  }

  // Tax = MAX(0, (EBITDA − amortisation) − prior NOL pool) × corporateTaxRate. Amortisation is
  // tax-deductible (taxable = true EBITDA less D&A), and the carried-forward losses (prior pool) shield
  // later positive taxable income before tax is charged (P6-R1; AU-standard indefinite carryforward).
  const taxOffset = rowOffsetOf("tax");
  const taxRow = baseDataRow + taxOffset;
  for (let i = 0; i < ny; i++) {
    const col = i + 2;
    const cell = ws.getCell(taxRow, col);
    const ebitdaCell = dataAddr("ebitda", col);
    const fitCell = dataAddr("fitout", col);
    const priorPool = i === 0 ? "0" : dataAddr("nolPool", col - 1);
    setFormula(
      cell,
      `MAX(0,(${ebitdaCell}-${fitCell})-${priorPool})*${ref("corporateTaxRate")}`,
      Math.round(years[i].tax),
      { numFmt: NF.currency },
    );
  }

  // Net income = EBITDA - Fitout amortisation - Tax  (accrual net income)
  const netIncomeOffset = rowOffsetOf("netIncome");
  const netIncomeRow = baseDataRow + netIncomeOffset;
  for (let i = 0; i < ny; i++) {
    const col = i + 2;
    const cell = ws.getCell(netIncomeRow, col);
    const ebitdaCell = dataAddr("ebitda", col);
    const fitCell = dataAddr("fitout", col);
    const taxCell = dataAddr("tax", col);
    setFormula(
      cell,
      `${ebitdaCell}-${fitCell}-${taxCell}`,
      Math.round(years[i].reddamNetIncome),
      { numFmt: NF.currency, bold: true },
    );
  }

  // Operating cashflow = EBITDA - Tax  (after-tax operating cash; non-cash amortisation added back) — P4-R1
  const opCashOffset = rowOffsetOf("opCash");
  const opCashRow = baseDataRow + opCashOffset;
  for (let i = 0; i < ny; i++) {
    const col = i + 2;
    const cell = ws.getCell(opCashRow, col);
    const ebitdaCell = dataAddr("ebitda", col);
    const taxCell = dataAddr("tax", col);
    setFormula(
      cell,
      `${ebitdaCell}-${taxCell}`,
      Math.round(years[i].reddamOperatingCashflow),
      { numFmt: NF.currency, bold: true },
    );
  }

  // Cumulative cashflow = prev cumulative + operating cashflow (cash basis; fit-out hits once at T0)
  const cumulativeOffset = rowOffsetOf("cumulative");
  const cumulativeRow = baseDataRow + cumulativeOffset;
  for (let i = 0; i < ny; i++) {
    const col = i + 2;
    const cell = ws.getCell(cumulativeRow, col);
    const opCashCell = dataAddr("opCash", col);
    if (i === 0) {
      // Y1 cumulative = -totalOpCapex + operating cashflow
      setFormula(
        cell,
        `-${fitoutCapexCell}+${opCashCell}`,
        Math.round(years[i].cumulativeCashflow),
        { numFmt: NF.currency },
      );
    } else {
      const prevCumCell = dataAddr("cumulative", col - 1);
      setFormula(
        cell,
        `${prevCumCell}+${opCashCell}`,
        Math.round(years[i].cumulativeCashflow),
        { numFmt: NF.currency },
      );
    }
  }

  // Rent % revenue = (building Rent gross + Amenity rent) / TOTAL REVENUE — total occupancy cost (P4-R2)
  const rentPctOffset = rowOffsetOf("rentPct");
  const rentPctRow = baseDataRow + rentPctOffset;
  for (let i = 0; i < ny; i++) {
    const col = i + 2;
    const cell = ws.getCell(rentPctRow, col);
    const rgCell = dataAddr("rentGross", col);
    const amCell = dataAddr("amenityRent", col);
    const revCell = dataAddr("totalRev", col);
    setFormula(
      cell,
      `(${rgCell}+${amCell})/${revCell}`,
      years[i].rentPctRevenue,
      { numFmt: NF.pct },
    );
  }

  // ── NPV & IRR rows ──
  r++;
  let opIrrRow = 0;
  if (ny > 0) {
    const discountRate = a.discountRate;
    // Operator NPV/IRR discount AFTER-TAX OPERATING CASH (not accrual net income) — the fit-out is a
    // cash outflow once at T0, never again, so amortisation is added back (P4-R1). Matches model.ts.
    const opCashExcelRow = opCashRow;
    const firstDataCol = "B";
    const lastDataCol = addr(opCashExcelRow - 1, ny).replace(/\d+/, "");
    const opCashRange = `${firstDataCol}${opCashExcelRow}:${lastDataCol}${opCashExcelRow}`;

    const npvLabelRow = ws.addRow(["NPV (@ discount rate)"]);
    styleLabel(npvLabelRow.getCell(1), true);
    thinBorder(npvLabelRow.getCell(1));
    const npvCell = npvLabelRow.getCell(2);
    setFormula(
      npvCell,
      `-${fitoutCapexCell}+NPV(${discountRate},${opCashRange})`,
      kpis.reddamNpv,
      { numFmt: NF.currency, bold: true },
    );

    // Operator cashflow incl. T0 fitout-capex outflow (period 0 = col B; operating cash Y1..Yn = cols C..).
    // IRR() needs the leading -capex BOTH to guarantee a sign change (else #NUM!) AND to equal the
    // JS operator IRR, which discounts the same [-capex, ...operatingCash] series (model.ts). Mirrors the NPV cell.
    const opCfLabelRow = ws.addRow(["Operator cashflow (incl. T0 capex)"]);
    styleLabel(opCfLabelRow.getCell(1));
    thinBorder(opCfLabelRow.getCell(1));
    const opCfRowNum = opCfLabelRow.number;
    setFormula(
      ws.getCell(opCfRowNum, 2),
      `-${fitoutCapexCell}`,
      -totalReddamCapex,
      {
        numFmt: NF.currency,
      },
    );
    for (let i = 0; i < ny; i++) {
      const ocCell = addr(opCashExcelRow - 1, i + 1); // Op P&L operating-cash cell for Year i+1 (col B.. = idx 1..)
      setFormula(
        ws.getCell(opCfRowNum, i + 3),
        `${ocCell}`,
        Math.round(years[i].reddamOperatingCashflow),
        {
          numFmt: NF.currency,
        },
      );
    }
    const opCfLastCol = addr(opCfRowNum - 1, ny + 1).replace(/\d+/, ""); // ny=25 -> 'AA'
    const opCfRange = `${firstDataCol}${opCfRowNum}:${opCfLastCol}${opCfRowNum}`;

    const irrLabelRow = ws.addRow(["IRR (incl. T0 capex)"]);
    styleLabel(irrLabelRow.getCell(1), true);
    thinBorder(irrLabelRow.getCell(1));
    const irrCell = irrLabelRow.getCell(2);
    // When the operator never recovers its capex (e.g. Downside: every-year-negative cashflow), the
    // IRR() formula recalcs to #NUM! and reddamIrr is null. Wrap in IFERROR → "n/a" so the exported workbook shows a
    // self-explaining label instead of a spreadsheet error, and cache the same: the IRR number when one
    // exists, else the string "n/a". When an IRR exists this is unchanged (the number).
    const irrCached: number | string = reddamIrr == null ? "n/a" : reddamIrr;
    setFormula(irrCell, `IFERROR(IRR(${opCfRange}),"n/a")`, irrCached, {
      numFmt: NF.pct2,
      bold: true,
    });
    opIrrRow = irrLabelRow.number;
  } else {
    const npvLabelRow = ws.addRow(["NPV (@ discount rate)"]);
    styleLabel(npvLabelRow.getCell(1), true);
    thinBorder(npvLabelRow.getCell(1));
    npvLabelRow.getCell(2).value = "—";
    npvLabelRow.getCell(2).font = { italic: true, name: "Arial", size: 10 };
    thinBorder(npvLabelRow.getCell(2));

    const irrLabelRow = ws.addRow(["IRR (incl. T0 capex)"]);
    styleLabel(irrLabelRow.getCell(1), true);
    thinBorder(irrLabelRow.getCell(1));
    irrLabelRow.getCell(2).value = "—";
    irrLabelRow.getCell(2).font = { italic: true, name: "Arial", size: 10 };
    thinBorder(irrLabelRow.getCell(2));
    opIrrRow = irrLabelRow.number;
  }

  // Column widths
  ws.columns = [{ width: 24 }, ...Array(ny).fill({ width: 14 })];

  return {
    tableStartRow: baseDataRow,
    netIncomeRow,
    opCashRow,
    ebitdaRow,
    totalRevRow,
    cumulativeRow,
    fitoutCapexCell,
    blendedFeeCell,
    opIrrRow,
    rows,
    rowOf,
  };
}

/* ── Headline KPIs sheet (all formulas) ──
   Every metric is a formula referencing Op P&L or Dev Cost. */
export function buildKPIs(
  wb: Workbook,
  k: KPIs,
  a: Assumptions,
  refs: Map<string, string>,
  opPl: ReturnType<typeof buildOpPL>,
  devCost: ReturnType<typeof buildDevCost>,
  yearOneAmenityRent = 0,
) {
  const ws = wb.addWorksheet("Headline KPIs");
  ws.views = [{ state: "frozen", ySplit: 1 }];

  const ref = (key: string) => refs.get(key) ?? "";
  let r = 1;

  const section = (title: string) => {
    ws.getCell(r, 1).value = title;
    ws.getCell(r, 1).font = {
      bold: true,
      color: { argb: C.headerText },
      name: "Arial",
      size: 10,
    };
    ws.getCell(r, 1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.headerBg },
    };
    ws.getCell(r, 2).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.headerBg },
    };
    thinBorderRow(ws, r, 1, 2);
    r++;
  };

  const rowMap = new Map<string, number>();

  const kvf = (
    label: string,
    formula: string,
    result: number | string,
    numFmt?: string,
  ) => {
    ws.getCell(r, 1).value = label;
    styleLabel(ws.getCell(r, 1));
    setFormula(ws.getCell(r, 2), formula, result, { numFmt, isLink: true });
    thinBorderRow(ws, r, 1, 2);
    rowMap.set(label, r);
    r++;
    return r - 1;
  };

  const kv = (label: string, value: string | number, numFmt?: string) => {
    ws.getCell(r, 1).value = label;
    styleLabel(ws.getCell(r, 1));
    ws.getCell(r, 2).value = value;
    styleFormula(ws.getCell(r, 2));
    if (numFmt) ws.getCell(r, 2).numFmt = numFmt;
    thinBorderRow(ws, r, 1, 2);
    rowMap.set(label, r);
    r++;
  };

  // Helper to build range references to Op P&L rows
  const ny = a.modelYears;
  const firstDataCol = "B";
  const lastDataCol = addr(opPl.netIncomeRow - 1, ny).replace(/\d+/, "");

  const ebitdaRange = `'Op P&L'!${firstDataCol}${opPl.ebitdaRow}:${lastDataCol}${opPl.ebitdaRow}`;
  // Operator NPV discounts after-tax operating cash (amort added back), matching model.ts (P4-R1).
  const opCashRange = `'Op P&L'!${firstDataCol}${opPl.opCashRow}:${lastDataCol}${opPl.opCashRow}`;
  // totalRevRange reserved for future expansion
  const cumulativeRange = `'Op P&L'!${firstDataCol}${opPl.cumulativeRow}:${lastDataCol}${opPl.cumulativeRow}`;

  section("DEVELOPER");
  const { exitYear, earlyExit: useExitYear } = exitBasisFor(a);
  const primaryHorizon = useExitYear ? `Y${exitYear}` : "PC";
  kvf(
    `Primary IRR (ungeared, ${primaryHorizon})`,
    `='Dev Cost'!B${devCost.primaryIrrRow}`,
    k.developerPrimaryIrr,
    NF.pct2,
  );
  kvf(
    "IRR (ungeared, sale at completion)",
    devCost.irrRow > 0 ? `='Dev Cost'!B${devCost.irrRow}` : "",
    k.developerIrr,
    NF.pct2,
  );
  if (useExitYear) {
    kvf(
      `IRR (ungeared, project Y${exitYear} hold-and-sell)`,
      devCost.ungearedEarlyExitIrrRow > 0
        ? `='Dev Cost'!B${devCost.ungearedEarlyExitIrrRow}`
        : "",
      k.developerPrimaryIrr,
      NF.pct2,
    );
    kvf(
      `IRR (geared, project Y${exitYear} early exit)`,
      devCost.gearedEarlyExitIrrRow > 0
        ? `='Dev Cost'!B${devCost.gearedEarlyExitIrrRow}`
        : "",
      k.developerPrimaryGearedIrr,
      NF.pct2,
    );
  }
  kvf(
    "IRR (geared, sale at completion)",
    devCost.gearedIrrRow > 0 ? `='Dev Cost'!B${devCost.gearedIrrRow}` : "",
    k.developerGearedIrr,
    NF.pct2,
  );
  kvf(
    gearedRefinanceExitLabel(a.termLoanHoldYears),
    devCost.gearedWholeLifeIrrRow > 0
      ? `='Dev Cost'!B${devCost.gearedWholeLifeIrrRow}`
      : "",
    k.developerGearedWholeOfLifeIrr,
    NF.pct2,
  );
  kvf(
    MARGIN_ON_COST_LABEL,
    `='Dev Cost'!B${devCost.marginRow}`,
    k.developerMarginOnCost,
    NF.pct2,
  );
  kvf(
    YIELD_ON_COST_LABEL,
    `='Dev Cost'!B${devCost.yieldOnCostRow}`,
    k.developerYieldOnCost,
    NF.pct2,
  );
  kvf(
    NOI_ON_DEVELOPER_EQUITY_LABEL,
    `='Dev Cost'!B${devCost.yieldCashRow}`,
    k.developerYieldOnCash,
    NF.pct2,
  );
  kvf(
    EQUITY_REQUIRED_LABEL,
    `='Dev Cost'!B${devCost.equityReqRow}`,
    k.developerEquityRequired,
    NF.currency,
  );
  kvf(
    "Money multiple",
    `='Dev Cost'!B${devCost.moneyMultipleRow}`,
    k.developerMoneyMultiple,
    NF.multiple,
  );
  kv(
    `Primary hurdle met (${primaryHorizon})`,
    k.developerPrimaryHurdleMet ? "Yes" : "No",
  );
  kv(
    `Primary hurdle status (${primaryHorizon})`,
    k.developerPrimaryHurdleStatus,
  );
  r++;

  section("OPERATOR");
  // NPV = NPV(discountRate, operating-cash range) - initial capex
  kvf(
    OPERATOR_NPV_LABEL,
    `-'Op P&L'!${opPl.fitoutCapexCell}+NPV(${ref("discountRate")},${opCashRange})`,
    k.reddamNpv,
    NF.currency,
  );
  // Operator IRR: pass-through to the Op P&L IRR cell (which now discounts the capex-inclusive cashflow),
  // so Headline == Op P&L == Cover and the recalc equals k.reddamIrr. Mirrors how Developer IRRs ref Dev Cost.
  // When the operator never recovers capex (k.reddamIrr null), the Op P&L cell is IFERROR→"n/a", so this
  // pass-through returns the text "n/a"; cache the same string (not 0) so the displayed value matches.
  kvf(
    OPERATOR_IRR_LABEL,
    `='Op P&L'!B${opPl.opIrrRow}`,
    k.reddamIrr ?? "n/a",
    NF.pct2,
  );
  // P8-R4 (#24): payback = FIRST year cumulative cashflow ≥ 0, robust to the ramp dip (the old
  // MATCH(0,…,1) assumed an ascending series and returned the last ≤0 row — off-by-one, and on a
  // never-recovering operator it returned the final year instead of "never"). MATCH(TRUE,…,0) over the
  // ≥0 mask returns the first crossing; IFERROR → "—" when the operator never recovers.
  kvf(
    OPERATOR_PAYBACK_LABEL,
    `IFERROR(MATCH(1,INDEX(--(${cumulativeRange}>=0),0),0),"—")`,
    k.paybackYear ?? "—",
    NF.num,
  );
  // Steady-state revenue = last year total revenue
  kvf(
    "Steady-state revenue",
    `='Op P&L'!${addr(opPl.totalRevRow - 1, ny)}`,
    k.steadyStateRevenue,
    NF.currency,
  );
  // Total tax = SUM(tax row)
  const taxRowNum = opPl.rowOf("tax");
  kvf(
    "Total tax (term)",
    `SUM('Op P&L'!${firstDataCol}${taxRowNum}:${lastDataCol}${taxRowNum})`,
    k.totalTax,
    NF.currency,
  );
  // Avg EBITDA = AVERAGE(ebitdaRange)
  kvf(AVG_EBITDA_LABEL, `AVERAGE(${ebitdaRange})`, k.avgEbitda, NF.currency);
  // Year 1 EBITDA = first column of EBITDA row
  kvf(
    Y1_EBITDA_LABEL,
    `='Op P&L'!${addr(opPl.ebitdaRow - 1, 1)}`,
    k.yearOneEbitda,
    NF.currency,
  );
  // Rent % revenue Y1
  kvf(
    RENT_PCT_REVENUE_Y1_LABEL,
    `='Op P&L'!${addr(opPl.rowOf("rentPct") - 1, 1)}`,
    k.rentPctRevenueYear1,
    NF.pct,
  );
  kvf(
    RENT_PCT_REVENUE_AVG_LABEL,
    `AVERAGE('Op P&L'!${firstDataCol}${opPl.rowOf("rentPct")}:${lastDataCol}${opPl.rowOf("rentPct")})`,
    k.rentPctRevenueAvg,
    NF.pct,
  );
  kvf(
    RENT_PCT_REVENUE_STEADY_LABEL,
    `='Op P&L'!${addr(opPl.rowOf("rentPct") - 1, ny)}`,
    k.rentPctRevenueSteadyState,
    NF.pct,
  );
  // Break-even building rent and enrolment — model outputs (bisection), shown as values.
  // Amenity rent remains outside the solved building-rent variable, so disclose the
  // corresponding total occupancy cost separately. A non-converged result is unavailable.
  const breakEvenBuildingRentRow = r;
  kv(
    BREAK_EVEN_RENT_Y1_LABEL,
    k.breakEvenRentConverged ? Math.round(k.breakEvenRentY1) : "—",
    NF.currency,
  );
  kvf(
    "Total occupancy cost at Y1 break-even",
    `IF(ISNUMBER(B${breakEvenBuildingRentRow}),B${breakEvenBuildingRentRow}+'Op P&L'!B${opPl.rowOf("amenityRent")},"—")`,
    k.breakEvenRentConverged
      ? Math.round(k.breakEvenRentY1 + yearOneAmenityRent)
      : "—",
    NF.currency,
  );
  kv(
    "Break-even enrolment",
    k.breakEvenEnrolmentConverged ? Math.round(k.breakEvenEnrolment) : "—",
    NF.num,
  );

  // W5 — Ramp survivability KPIs
  // COUNTIF EBITDA range < 0: operator years in the red
  kvf(
    "EBITDA-negative years",
    `COUNTIF(${ebitdaRange},"<0")`,
    k.negativeEbitdaYears,
    NF.num,
  );
  // MIN cumulative cashflow: deepest trough. Mirrors the engine (model.ts W5):
  // the T0 instant (−total operator capex) is part of the series, and the cumulative
  // ROW starts at end-of-Y1 (seed + Y1 cash) — so the seed must be wrapped into the
  // MIN operand or positive-Y1-cash scenarios understate the trough on every recalc.
  kvf(
    "Peak cash trough",
    `MIN(-'Op P&L'!${opPl.fitoutCapexCell},${cumulativeRange})`,
    k.maxCumulativeDrawdown,
    NF.currency,
  );
  // Reserves required: if trough is negative, negate it; else 0
  kvf(
    RAMP_RESERVES_REQUIRED_LABEL,
    `MAX(0,-MIN(-'Op P&L'!${opPl.fitoutCapexCell},${cumulativeRange}))`,
    k.reservesRequiredToSurvive,
    NF.currency,
  );

  ws.columns = [{ width: 32 }, { width: 20 }];

  return rowMap;
}

/* ── Model Checks sheet (NEW — formula-driven audit) ── */
const MODEL_CHECK_RENT_REVENUE_TARGET = 0.35;

export function buildModelChecks(
  wb: Workbook,
  scenario: Scenario,
  result: ModelResult,
  refs: Map<string, string>,
  opPl: ReturnType<typeof buildOpPL>,
  devCost: ReturnType<typeof buildDevCost>,
) {
  const ws = wb.addWorksheet("Model Checks");
  ws.views = [{ state: "frozen", ySplit: 4 }];
  const a = scenario.assumptions;
  const checks = Model.modelChecks(scenario, result);

  const ref = (key: string) => refs.get(key) ?? "";

  // Title
  ws.getCell(1, 1).value = "MODEL INTEGRITY CHECKS";
  ws.getCell(1, 1).font = {
    bold: true,
    size: 14,
    color: { argb: C.headerBg },
    name: "Arial",
  };
  ws.mergeCells("A1:E1");

  ws.getCell(2, 1).value =
    `${checks.length}-check workbook control panel. Identifiers and cached statuses match the PDF control population; PASS = green, REVIEW = amber, FAIL = red.`;
  styleNote(ws.getCell(2, 1));
  ws.mergeCells("A2:E2");

  // Headers
  const checkHeaders = [
    "ID",
    "Area",
    "Check",
    "Evidence / tolerance",
    "Status",
  ];
  const headerRow = ws.getRow(4);
  checkHeaders.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = h;
    styleHeader(cell);
  });

  let r = 5;

  const statusLabel = (level: CheckResult["level"]) =>
    level === "warn" ? "REVIEW" : level.toUpperCase();
  const helperCode = (level: CheckResult["level"]) =>
    level === "pass" ? 0 : level === "warn" ? 1 : 2;

  const addCheck = (check: CheckResult, formula: string) => {
    const row = ws.getRow(r);
    row.getCell(1).value = check.id;
    styleLabel(row.getCell(1));
    thinBorder(row.getCell(1));

    row.getCell(2).value = check.area;
    styleLabel(row.getCell(2));
    thinBorder(row.getCell(2));

    row.getCell(3).value = check.label;
    styleLabel(row.getCell(3));
    thinBorder(row.getCell(3));

    row.getCell(4).value = check.detail;
    styleNote(row.getCell(4));
    thinBorder(row.getCell(4));

    // Some engine-level invariants have no stable closed-form workbook
    // equivalent. Their exact model-output result is held in a hidden helper
    // cell, and the visible status remains a formula over that disclosed
    // control input rather than a pasted status label.
    const code = helperCode(check.level);
    setFormula(row.getCell(6), String(code), code, {
      numFmt: NF.num,
    });

    // Status cell with formula
    const statusCell = row.getCell(5);
    setFormula(statusCell, formula, statusLabel(check.level), {});
    // Apply conditional formatting via fill based on result
    if (check.level === "pass") {
      statusCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: C.passGreen },
      };
      statusCell.font = {
        bold: true,
        color: { argb: C.passGreenText },
        name: "Arial",
        size: 10,
      };
    } else if (check.level === "warn") {
      statusCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: C.warnAmber },
      };
      statusCell.font = {
        bold: true,
        color: { argb: C.warnAmberText },
        name: "Arial",
        size: 10,
      };
    } else {
      statusCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: C.failRed },
      };
      statusCell.font = {
        bold: true,
        color: { argb: C.failRedText },
        name: "Arial",
        size: 10,
      };
    }
    statusCell.alignment = { horizontal: "center" };
    thinBorder(statusCell);

    r++;
  };

  const rentPctCell = `'Op P&L'!${addr(opPl.rowOf("rentPct") - 1, 1)}`;
  const market = Model.constructionBenchmark(a.schoolSpecTier);
  const stageMixFormula = [
    ref("earlyLearningShare"),
    ref("primaryShare"),
    ref("secondaryShare"),
    ref("seniorShare"),
  ].join("+");
  const parkingFormula = [
    ref("weekdayParentParkingBays"),
    ref("weekendSportsParkingBays"),
    ref("staffReservedParkingBays"),
  ].join("+");

  const statusFormula = (check: CheckResult, row: number): string => {
    const modelOutputFormula = `IF($F${row}=0,"PASS",IF($F${row}=1,"REVIEW","FAIL"))`;
    switch (check.id) {
      case "cost-recalc":
        return `IF(ABS('Dev Cost'!B${devCost.totalCostRow}-('Dev Cost'!B${devCost.preMgmtRow}+'Dev Cost'!B${devCost.devMgmtFeeRow}))<1,"PASS","FAIL")`;
      case "funding-sources-uses":
        return `IF(ABS('Dev Cost'!B${devCost.totalCostRow}-('Dev Cost'!B${devCost.equityReqRow}+'Dev Cost'!B${devCost.facilityLimitRow}+'Dev Cost'!B${devCost.contributionAppliedRow}))<=0.01,"PASS","FAIL")`;
      case "return-bounds":
        return `IF(AND('Dev Cost'!B${devCost.irrRow}>=-1,'Dev Cost'!B${devCost.irrRow}<=5,'Dev Cost'!B${devCost.gearedIrrRow}>=-1,'Dev Cost'!B${devCost.gearedIrrRow}<=5,'Dev Cost'!B${devCost.gearedWholeLifeIrrRow}>=-1,'Dev Cost'!B${devCost.gearedWholeLifeIrrRow}<=5,'Dev Cost'!B${devCost.yieldOnCostRow}>=-0.5,'Dev Cost'!B${devCost.yieldOnCostRow}<=1,'Dev Cost'!B${devCost.marginRow}>=-1,'Dev Cost'!B${devCost.marginRow}<=5,'Dev Cost'!B${devCost.moneyMultipleRow}>=-5,'Dev Cost'!B${devCost.moneyMultipleRow}<=100),"PASS","REVIEW")`;
      case "noi-cost-backed": {
        // Per line item, mirroring modelChecks(): each amenity rent is backed by ITS OWN construction
        // input; under the loaded-aggregate basis the loaded SITE/OUTDOOR works back all amenities (the
        // scope register maps every amenity there, never to building works).
        const loadedBacking = `AND(${ref("developerCostBasisMode")}="loaded_aggregate",'Dev Cost'!B${devCost.siteWorksRow}>0)`;
        const legacy = `${ref("developerCostBasisMode")}<>"loaded_aggregate"`;
        return `IF(AND(OR(${ref("sportsFieldRentPerSqm")}<=0,${ref("sportsFieldAreaSqm")}<=0,${loadedBacking},AND(${legacy},${ref("sportsFieldAreaSqm")}*${ref("sportsFieldConstructionPerSqm")}>0)),OR(${ref("parkingRentPerBay")}<=0,${ref("parkingBays")}<=0,${loadedBacking},AND(${legacy},${ref("parkingBays")}*${ref("parkingConstructionCostPerBay")}>0)),OR(${ref("coveredPlayAreaGroundRent")}<=0,${loadedBacking},AND(${legacy},${ref("coveredPlayAreaConstructionCost")}>0)),OR('Dev Cost'!B${devCost.netRentY1Row}<=0,'Dev Cost'!B${devCost.hardConstrRow}>0)),"PASS","REVIEW")`;
      }
      case "construction-vs-market":
        return `IF(AND(('Dev Cost'!B${devCost.hardConstrRow}+'Dev Cost'!B${devCost.prelimRow}+'Dev Cost'!B${devCost.builderMarginRow})/'Building Schedule'!C${scenario.buildings.length + 2}>=${market.low},('Dev Cost'!B${devCost.hardConstrRow}+'Dev Cost'!B${devCost.prelimRow}+'Dev Cost'!B${devCost.builderMarginRow})/'Building Schedule'!C${scenario.buildings.length + 2}<=${market.high}),"PASS","REVIEW")`;
      case "noi-recalc":
        return `IF(ABS('Dev Cost'!B${devCost.noiRow}-('Dev Cost'!B${devCost.netRentY1Row}+'Dev Cost'!B${devCost.sportsRentRow}+'Dev Cost'!B${devCost.parkingRentRow}+'Dev Cost'!B${devCost.coveredPlayAreaRentRow}))<1,"PASS","FAIL")`;
      case "profit-recalc":
        return `IF(ABS('Dev Cost'!B${devCost.profitRow}-('Dev Cost'!B${devCost.netRealRow}-'Dev Cost'!B${devCost.totalCostRow}))<1,"PASS","FAIL")`;
      case "rent-pct-revenue":
        return `IF(${rentPctCell}<=${MODEL_CHECK_RENT_REVENUE_TARGET},"PASS","REVIEW")`;
      case "mix":
        return `IF(ABS((${stageMixFormula})-1)<=0.02,"PASS","REVIEW")`;
      case "parking":
        return `IF((${parkingFormula})<=${ref("parkingBays")},"PASS","REVIEW")`;
      case "facility-lvr":
        return `IF('Dev Cost'!B${devCost.grossFacilityLimitRow}<='Dev Cost'!B${devCost.totalCostRow}*(${ref("constructionLvrRate")}+0.005),"PASS","REVIEW")`;
      case "equity-positive":
        return `IF('Dev Cost'!B${devCost.equityReqRow}>0,"PASS","REVIEW")`;
      case "term-loan-sufficiency":
        return `IF('Dev Cost'!B${devCost.termLoanLimitRow}>=('Dev Cost'!B${devCost.facilityLimitRow}+'Dev Cost'!B${devCost.lineFeeRow}+'Dev Cost'!B${devCost.constructionInterestRow}),"PASS","REVIEW")`;
      case "dscr":
        return `IF('Dev Cost'!B${devCost.termLoanAnnualServiceRow}<=0,"PASS",IF('Dev Cost'!B${devCost.noiRow}/'Dev Cost'!B${devCost.termLoanAnnualServiceRow}>=1.25,"PASS",IF('Dev Cost'!B${devCost.noiRow}/'Dev Cost'!B${devCost.termLoanAnnualServiceRow}>=1,"REVIEW","FAIL")))`;
      default:
        return modelOutputFormula;
    }
  };

  checks.forEach((check) => {
    // Formula-backed controls cache the same result that the workbook formula
    // will produce on open. This is important for consumers that inspect the
    // workbook before Excel has recalculated it.
    const workbookCheck =
      check.id === "rent-pct-revenue"
        ? {
            ...check,
            level:
              result.kpis.rentPctRevenueYear1 <= MODEL_CHECK_RENT_REVENUE_TARGET
                ? ("pass" as const)
                : ("warn" as const),
          }
        : check;
    addCheck(workbookCheck, statusFormula(workbookCheck, r));
  });

  for (let column = 1; column <= 5; column += 1) {
    ws.getColumn(column).eachCell((cell) => {
      cell.alignment = {
        horizontal: column === 5 ? "center" : "left",
        vertical: "top",
        wrapText: true,
      };
    });
  }
  ws.getColumn(6).hidden = true;
  ws.columns = [
    { width: 24 },
    { width: 18 },
    { width: 42 },
    { width: 76 },
    { width: 12 },
    { width: 4, hidden: true },
  ];
}

/* ── Rent Bridge sheet — cost BUILD-UP + financing-equivalent reference + occupancy reconciliation ──
   Surfaces Model.computeRentBuildUp(): the developer's cost-recovery rent built UP from 6 cost lines
   (land, construction debt+holding, soft, approval, opex, equity@hurdle). FIVE are live formulas
   (land, soft, approval, opex, equity@hurdle); the construction-debt + holding-interest line is a
   labelled model-output VALUE — the drawdown-profile integral has no clean closed form for phased
   per-building construction, so it is rendered exact for BOTH single-phase and phased scenarios.
   The building-rent ask is a live ='Op P&L'! link; the geared-IRR solver output is a labelled
   model-output reference, not the stated ungeared hurdle decision. Amenity rent is then added to
   both building-rent figures so the workbook visibly reconciles to the SPA's total-occupancy basis. */
export function buildRentBridge(
  wb: Workbook,
  scenario: Scenario,
  result: ModelResult,
  refs: Map<string, string>,
  opPl: ReturnType<typeof buildOpPL>,
  devCost: ReturnType<typeof buildDevCost>,
) {
  const ws = wb.addWorksheet("Rent Bridge");
  ws.views = [{ state: "frozen", ySplit: 2 }];
  const { exitYear: exitYr, earlyExit } = exitBasisFor(scenario.assumptions);

  const buildUp = Model.computeRentBuildUp(scenario);
  const comp = (i: number) =>
    Math.round(buildUp.components[i]?.annualAmount ?? 0);

  // Cross-sheet refs.
  const termRef = refs.get("leaseTermYears") ?? ""; // 'Assumptions'!$B$N (lease term, years)
  const hurdleRef = refs.get("developerHurdleIRR") ?? ""; // 'Assumptions'!$B$N (developer hurdle IRR)
  // W2 incentive refs (added to Assumptions sheet in buildAssumptions)
  const lumpSumRef = refs.get("leaseIncentiveLumpSum") ?? "";
  const rentFreeRef = refs.get("rentFreeMonths") ?? "";
  const amortYrsRef = refs.get("incentiveAmortisationYears") ?? "";
  // (Construction-debt line is now a model-output value — the base/margin/line-fee/dev-months refs
  // that fed its old closed-form formula are no longer needed.)
  const opexY1Ref = `'Op P&L'!${addr(opPl.rowOf("outgoings") - 1, 1)}`;
  const rentGrossY1Ref = `'Op P&L'!${addr(opPl.rowOf("rentGross") - 1, 1)}`;
  const amenityRentY1Ref = `'Op P&L'!${addr(opPl.rowOf("amenityRent") - 1, 1)}`;
  const totalRevY1Ref = `'Op P&L'!${addr(opPl.rowOf("totalRev") - 1, 1)}`;
  const dc = (row: number) => `'Dev Cost'!B${row}`;

  const matchedUngearedMetric = earlyExit
    ? "developerUngearedEarlyExitIrr"
    : "developerIrr";
  const ungearedThreshold = Model.solveRentForHurdle(
    scenario,
    matchedUngearedMetric,
    scenario.assumptions.developerHurdleIRR,
  );
  const gearedReferenceAvailable =
    result.developer.gearedHurdleConverged &&
    Number.isFinite(result.developer.gearedHurdle) &&
    buildUp.calibratedSolverConverged;
  const ungearedThresholdScenario = structuredClone(scenario);
  if (ungearedThreshold.converged) {
    for (const building of ungearedThresholdScenario.buildings) {
      if (Number.isFinite(building.netRentPerSqm)) {
        building.netRentPerSqm *= ungearedThreshold.rentScale;
      }
    }
  }
  const ungearedThresholdRent = ungearedThreshold.converged
    ? (Model.computeCore(ungearedThresholdScenario).years[0]?.rentGross ?? 0)
    : Number.NaN;
  const gearedThresholdRent = gearedReferenceAvailable
    ? buildUp.calibratedRentY1
    : Number.NaN;
  const operatorRecommendation = getOperatorDealRecommendation(
    scenario,
    result,
  );

  const equityAmortised =
    (scenario.assumptions.equityReturnFormulation ?? "perpetuity") ===
    "amortised";

  // Title
  ws.getCell(1, 1).value = "RENT BRIDGE — COST BUILD-UP";
  ws.getCell(1, 1).font = {
    bold: true,
    size: 14,
    color: { argb: C.headerBg },
    name: "Arial",
  };
  ws.mergeCells("A1:C1");

  // Subtitle. A1: the reference and margin move with developerExitYear — name the exit basis.
  const exitBasisLabel = ` ${rentBridgeExitBasisLabel(exitYr, scenario.assumptions.modelYears)}.`;
  ws.getCell(2, 1).value = buildUp.calibratedSolverConverged
    ? `The financing-equivalent rent reference reconciles to the stated ungeared project threshold. Building-rent margin / overreach = ask − financing-equivalent reference; amenity rent is added separately to reconcile total occupancy cost. The cost build-up is illustrative, not a break-even.${exitBasisLabel}`
    : `Financing-equivalent rent calibration did not converge — treat the financing reference and over/under-asking read as unavailable. Amenity rent and total occupancy ask remain fully reconciled.${exitBasisLabel}`;
  styleNote(ws.getCell(2, 1));
  ws.mergeCells("A2:C2");

  let r = 4;

  // ── RETURN REFERENCE AUDIT ──
  ws.getCell(r, 1).value = "RETURN REFERENCE AUDIT";
  ws.getCell(r, 1).font = {
    bold: true,
    color: { argb: C.headerText },
    name: "Arial",
    size: 10,
  };
  ws.getCell(r, 1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  ws.getCell(r, 2).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  thinBorderRow(ws, r, 1, 2);
  r++;

  const auditValue = (
    label: string,
    value: string | number,
    numFmt?: string,
    note?: string,
  ) => {
    ws.getCell(r, 1).value = label;
    styleLabel(ws.getCell(r, 1));
    ws.getCell(r, 2).value = value;
    styleFormula(ws.getCell(r, 2));
    if (numFmt) ws.getCell(r, 2).numFmt = numFmt;
    if (note) {
      ws.getCell(r, 3).value = note;
      styleNote(ws.getCell(r, 3));
      if (typeof value === "string") {
        ws.getCell(r, 2).alignment = {
          ...ws.getCell(r, 2).alignment,
          vertical: "top",
          wrapText: true,
        };
        if (value.length > 20) {
          ws.getRow(r).height = Math.min(
            60,
            Math.max(24, Math.ceil(value.length / 32) * 15),
          );
        }
      }
    }
    thinBorderRow(ws, r, 1, 2);
    const row = r;
    r++;
    return row;
  };

  auditValue(
    "Selected developer horizon",
    earlyExit
      ? `project Y${exitYr} early exit`
      : "Sale at practical completion (PC)",
  );
  {
    const row = r;
    ws.getCell(row, 1).value = "Stated ungeared project hurdle";
    styleLabel(ws.getCell(row, 1), true);
    setFormula(
      ws.getCell(row, 2),
      hurdleRef,
      scenario.assumptions.developerHurdleIRR,
      { numFmt: NF.pct2, isLink: true },
    );
    ws.getCell(row, 3).value =
      "Primary developer-economics test at the selected horizon.";
    styleNote(ws.getCell(row, 3));
    thinBorderRow(ws, row, 1, 2);
    r++;
  }
  const ungearedThresholdRow = auditValue(
    "Calibrated ungeared threshold rent (Y1, building)",
    ungearedThreshold.converged ? Math.round(ungearedThresholdRent) : "—",
    NF.currency,
    "Model output from uniform building-rent calibration to the stated ungeared project hurdle.",
  );
  auditValue(
    "Cashflow-equivalent geared reference",
    gearedReferenceAvailable ? result.developer.gearedHurdle : "—",
    NF.pct2,
    "Model output from the same calibrated rent and matched-horizon financing cashflows; not an Excel solver formula.",
  );
  {
    const achievedRow = earlyExit
      ? devCost.gearedEarlyExitIrrRow
      : devCost.gearedIrrRow;
    const row = r;
    ws.getCell(row, 1).value =
      "Achieved matched-horizon geared IRR at current ask";
    styleLabel(ws.getCell(row, 1));
    setFormula(
      ws.getCell(row, 2),
      dc(achievedRow),
      result.kpis.developerPrimaryGearedIrr,
      { numFmt: NF.pct2, isLink: true },
    );
    ws.getCell(row, 3).value =
      "Live link to the selected Dev Cost IRR() result at the current asking rent.";
    styleNote(ws.getCell(row, 3));
    thinBorderRow(ws, row, 1, 2);
    r++;
  }
  auditValue(
    "Geared reference availability",
    gearedReferenceAvailable
      ? "Available (converged)"
      : "Unavailable (calibration did not converge)",
  );
  auditValue(
    "Matched cashflow provenance",
    earlyExit
      ? `project Y${exitYr}: Ungeared hold-and-sell CF (column F) → Early-exit geared CF (column E)`
      : "PC: Ungeared CF (column B) → Geared sale-at-completion CF (column C)",
    undefined,
    "The Dev Cost IRR() rows read these monthly model-output vectors from Dev Cashflow.",
  );
  const financingEquivalentRow = auditValue(
    "Financing-equivalent threshold rent (Y1, building)",
    gearedReferenceAvailable ? Math.round(gearedThresholdRent) : "—",
    NF.currency,
    "Model-output rent solving the matched geared cashflow to the cashflow-equivalent geared reference.",
  );
  {
    const thresholdRentsAvailable =
      ungearedThreshold.converged && gearedReferenceAvailable;
    const row = r;
    ws.getCell(row, 1).value = "Threshold-rent reconciliation gap";
    styleLabel(ws.getCell(row, 1), true);
    if (thresholdRentsAvailable) {
      setFormula(
        ws.getCell(row, 2),
        `B${financingEquivalentRow}-B${ungearedThresholdRow}`,
        Math.round(gearedThresholdRent) - Math.round(ungearedThresholdRent),
        { numFmt: NF.currency, bold: true },
      );
      ws.getCell(row, 3).value =
        "Expected within model tolerance (≤ $10/year); confirms leverage creates no incremental rent threshold.";
    } else {
      ws.getCell(row, 2).value = "—";
      ws.getCell(row, 3).value =
        "Unavailable until both matched-horizon rent calibrations converge.";
    }
    styleNote(ws.getCell(row, 3));
    thinBorderRow(ws, row, 1, 2);
    r++;
  }
  auditValue("Geared reference interpretation", GEARED_REFERENCE_DISCLOSURE);
  r++;

  // ── OPERATOR ASSESSMENT AUDIT ──
  ws.getCell(r, 1).value = "OPERATOR ASSESSMENT AUDIT";
  ws.getCell(r, 1).font = {
    bold: true,
    color: { argb: C.headerText },
    name: "Arial",
    size: 10,
  };
  ws.getCell(r, 1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  ws.getCell(r, 2).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  thinBorderRow(ws, r, 1, 2);
  r++;
  auditValue(
    "Operator affordability screening threshold",
    OPERATOR_AFFORDABILITY_CEILING,
    NF.pct2,
    "Internal screening threshold used by the shared operator assessment; not a sourced industry covenant or automatic decision rule. Separate 35% target and caution audit checks remain in Model Checks.",
  );
  auditValue(
    "Current Y1 rent burden",
    result.kpis.rentPctRevenueYear1,
    NF.pct2,
    "Opening-year total rent as a share of operator revenue; the shared assessment compares this with the internal screening threshold.",
  );
  auditValue(
    "Operator assessment status",
    operatorRecommendation.verdict,
    undefined,
    "Shared model-output verdict used by the SPA and PDF; not an Excel formula.",
  );
  auditValue(
    "Operator assessment evidence",
    operatorRecommendation.reason,
    undefined,
    "Shared model-output reason; preserves the exact governing decision evidence.",
  );
  r++;

  // ── COST BUILD-UP ──
  ws.getCell(r, 1).value = "COST BUILD-UP";
  ws.getCell(r, 1).font = {
    bold: true,
    color: { argb: C.headerText },
    name: "Arial",
    size: 10,
  };
  ws.getCell(r, 1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  ws.getCell(r, 2).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  thinBorderRow(ws, r, 1, 2);
  r++;

  // Each cost line is a LIVE formula, annualised over the lease term (÷ lease-term ref), referencing
  // the Dev Cost / Op P&L / Assumptions cells. Stored results equal computeRentBuildUp().components[i].
  const line = (label: string, formula: string, result: number) => {
    ws.getCell(r, 1).value = label;
    styleLabel(ws.getCell(r, 1));
    setFormula(ws.getCell(r, 2), formula, result, { numFmt: NF.currency });
    thinBorderRow(ws, r, 1, 2);
    r++;
  };

  // 1. Land acquisition recovery
  line(
    "Land acquisition recovery",
    `${dc(devCost.landAcqRow)}/${termRef}`,
    comp(0),
  );

  // 2. Construction debt + holding interest — MODEL-OUTPUT VALUE (the only build-up line that is not a
  //    live formula). debtCostDev + holdingInterest = constructionInterest + lineFee + holdingInterest,
  //    where constructionInterest is the integral of the (per-building, S-curve) drawdown profile over
  //    lastCompletionMonth. The old closed form facility × 0.5 × (base+margin) × (developmentMonths/12)
  //    was exact ONLY for a single-phase smoothstep drawdown (the 4 default scenarios). For a CUSTOM
  //    phased scenario (per-building constructionStartMonth/Months pushing lastCompletionMonth ≠
  //    developmentMonths, or the S-curve drawdown), that 0.5-average closed form DRIFTS from the model
  //    integral on recalc — there is no clean cell-by-cell Excel formula for a phased drawdown integral.
  //    HARDENED: render the exact model debt basis as a plain rounded VALUE, correct for BOTH single-
  //    phase and phased construction (like the financing-reference reconciliation row), with an adjacent
  //    note so it is not misread as a stray hardcode.
  ws.getCell(r, 1).value = "Construction debt + holding interest";
  styleLabel(ws.getCell(r, 1));
  ws.getCell(r, 2).value = comp(1);
  styleFormula(ws.getCell(r, 2));
  ws.getCell(r, 2).numFmt = NF.currency;
  thinBorderRow(ws, r, 1, 2);
  ws.getCell(r, 3).value =
    "Model output — drawdown-profile integral (no clean closed form for phased drawdowns; exact for single-phase AND phased construction).";
  styleNote(ws.getCell(r, 3));
  r++;

  // 3. Soft costs (consultants, statutory, mgmt fee) — every term carries the 'Dev Cost'! prefix
  //    so the validator resolves them cross-sheet (a bare B-ref would resolve to this sheet).
  line(
    "Soft costs (consultants, statutory, mgmt fee)",
    `(${dc(devCost.consultantRow)}+${dc(devCost.statutoryRow)}+${dc(devCost.planningRow)}+${dc(devCost.contingencyRow)}` +
      `+${dc(devCost.devMgmtFeeRow)}+${dc(devCost.otherDdRow)}+${dc(devCost.tenantRow)})/${termRef}`,
    comp(2),
  );

  // 4. Approval cost recovery (developer share)
  line(
    "Approval cost recovery (developer share)",
    `${dc(devCost.approvalShareRow)}/${termRef}`,
    comp(3),
  );

  // 5. Outgoings / opex pass-through — live Op P&L Y1 outgoings cell (already annual, no ÷ term)
  line("Outgoings / opex pass-through", opexY1Ref, comp(4));

  // 6. Developer equity return @ hurdle IRR — perpetuity (default): equity × hurdle. If the
  //    scenario uses the amortised formulation, Excel can't reproduce the annuity factor cleanly
  //    from these cells, so fall back to the C1 model-output value with the equity ref shown.
  if (equityAmortised) {
    // Model-output fallback (amortised annuity factor not reproducible as a single Dev Cost cell).
    ws.getCell(r, 1).value = "Developer equity return @ hurdle IRR";
    styleLabel(ws.getCell(r, 1));
    ws.getCell(r, 2).value = comp(5);
    styleFormula(ws.getCell(r, 2));
    ws.getCell(r, 2).numFmt = NF.currency;
    thinBorderRow(ws, r, 1, 2);
    ws.getCell(r, 3).value =
      "Model output — amortised annuity (perpetuity default would be a live formula).";
    styleNote(ws.getCell(r, 3));
    r++;
  } else {
    line(
      "Developer equity return @ hurdle IRR",
      `${dc(devCost.equityReqRow)}*${hurdleRef}`,
      comp(5),
    );
  }

  const firstLineRow = r - 6;
  const lastLineRow = r - 1;

  // 7. Σ cost components (ILLUSTRATIVE) — live SUM of the six lines. NOT the break-even: it amortises
  //    capital recovered at exit and omits a construction-principal line, so it can diverge materially
  //    from the financing-equivalent rent reference below.
  ws.getCell(r, 1).value =
    "Σ cost components (illustrative — not the break-even)";
  styleTotal(ws.getCell(r, 1));
  setFormula(
    ws.getCell(r, 2),
    `SUM(B${firstLineRow}:B${lastLineRow})`,
    Math.round(buildUp.costRecoveryRent),
    { numFmt: NF.currency, bold: true },
  );
  ws.getCell(r, 2).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.subtotalGrey },
  };
  thinBorderRow(ws, r, 1, 2);
  const costRecoveryRow = r;
  r++;

  // 8. Developer ask — live Op P&L Y1 gross-rent cell (the traceable anchor)
  ws.getCell(r, 1).value = "Developer ask — scenario rent (Y1, gross)";
  styleLabel(ws.getCell(r, 1));
  setFormula(
    ws.getCell(r, 2),
    rentGrossY1Ref,
    Math.round(buildUp.scenarioRentY1),
    { numFmt: NF.currency },
  );
  thinBorderRow(ws, r, 1, 2);
  const askRow = r;
  r++;

  // 9. Amenity rent — separate landlord rent paid for sports, parking and covered play area.
  ws.getCell(r, 1).value =
    "Amenity rent — sports, parking & covered play area (Y1)";
  styleLabel(ws.getCell(r, 1));
  setFormula(
    ws.getCell(r, 2),
    amenityRentY1Ref,
    Math.round(result.years[0]?.amenityRent ?? 0),
    { numFmt: NF.currency },
  );
  thinBorderRow(ws, r, 1, 2);
  const amenityRentRow = r;
  r++;

  // 10. Total occupancy ask — matches the SPA affordability/ZOPA basis.
  ws.getCell(r, 1).value = "Total occupancy ask (Y1)";
  styleLabel(ws.getCell(r, 1), true);
  setFormula(
    ws.getCell(r, 2),
    `B${askRow}+B${amenityRentRow}`,
    Math.round(buildUp.scenarioRentY1) +
      Math.round(result.years[0]?.amenityRent ?? 0),
    { numFmt: NF.currency, bold: true },
  );
  ws.getCell(r, 3).value =
    "Building gross rent + sports, parking and covered-play-area landlord rent.";
  styleNote(ws.getCell(r, 3));
  thinBorderRow(ws, r, 1, 2);
  r++;

  const floorIndicative = !buildUp.calibratedSolverConverged;

  // 11. Financing-equivalent building-rent reference — cashflow-derived IRR-bisection output.
  //     This is not the stated ungeared hurdle decision and Excel cannot reproduce the solve.
  ws.getCell(r, 1).value = earlyExit
    ? `Financing-equivalent building rent (Y${exitYr} model output)`
    : "Financing-equivalent building rent (model output)";
  styleLabel(ws.getCell(r, 1), true);
  if (floorIndicative) {
    ws.getCell(r, 2).value = "—";
    ws.getCell(r, 2).alignment = { horizontal: "center" };
    ws.getCell(r, 3).value =
      "Calibration did not converge — financing reference unavailable.";
  } else {
    ws.getCell(r, 2).value = Math.round(buildUp.calibratedRentY1);
    styleFormula(ws.getCell(r, 2));
    ws.getCell(r, 2).numFmt = NF.currency;
    ws.getCell(r, 3).value =
      "Cashflow-derived financing reference (model output, not an Excel formula); it does not determine whether the stated ungeared hurdle is met.";
  }
  styleNote(ws.getCell(r, 3));
  thinBorderRow(ws, r, 1, 2);
  const exactFloorRow = r;
  ws.getRow(r).height = 30;
  r++;

  // 12. Total-occupancy equivalent of the financing-equivalent building-rent reference.
  ws.getCell(r, 1).value = "Financing-equivalent total occupancy rent (Y1)";
  styleLabel(ws.getCell(r, 1), true);
  if (floorIndicative) {
    ws.getCell(r, 2).value = "—";
    ws.getCell(r, 2).alignment = { horizontal: "center" };
    ws.getCell(r, 3).value =
      "Unavailable until the financing-equivalent rent calibration converges.";
  } else {
    setFormula(
      ws.getCell(r, 2),
      `B${exactFloorRow}+B${amenityRentRow}`,
      Math.round(buildUp.calibratedRentY1) +
        Math.round(result.years[0]?.amenityRent ?? 0),
      { numFmt: NF.currency, bold: true },
    );
    ws.getCell(r, 3).value =
      "Financing-equivalent building-rent reference + amenity landlord rent; directly comparable with total occupancy ask.";
  }
  styleNote(ws.getCell(r, 3));
  thinBorderRow(ws, r, 1, 2);
  ws.getRow(r).height = 30;
  r++;

  // 13. Developer margin / overreach — building ask minus financing-equivalent building-rent reference.
  ws.getCell(r, 1).value =
    "Developer margin / overreach vs financing reference";
  styleLabel(ws.getCell(r, 1), true);
  if (floorIndicative) {
    ws.getCell(r, 2).value = "—";
    ws.getCell(r, 2).alignment = { horizontal: "center" };
    ws.getCell(r, 3).value =
      "Unavailable until the financing-equivalent rent calibration converges.";
  } else {
    setFormula(
      ws.getCell(r, 2),
      `B${askRow}-B${exactFloorRow}`,
      Math.round(buildUp.scenarioRentY1 - buildUp.calibratedRentY1),
      { numFmt: NF.currency, bold: true },
    );
    ws.getCell(r, 3).value =
      "> 0 = building-rent ask above the financing-equivalent reference; < 0 = below it. This is not the ungeared hurdle decision.";
  }
  styleNote(ws.getCell(r, 3));
  thinBorderRow(ws, r, 1, 2);
  ws.getRow(r).height = 30;
  r += 2;

  // W2 — Lease incentive / effective rent block
  const k = result.kpis;
  ws.getCell(r, 1).value = "EFFECTIVE RENT — LEASE INCENTIVE PACKAGE";
  ws.getCell(r, 1).font = {
    bold: true,
    color: { argb: C.headerText },
    name: "Arial",
    size: 10,
  };
  ws.getCell(r, 1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  ws.getCell(r, 2).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  thinBorderRow(ws, r, 1, 2);
  r++;

  ws.getCell(r, 1).value = "Incentive (PV) — lump sum + rent-free equivalent";
  styleLabel(ws.getCell(r, 1));
  // Formula: lump sum + (Y1 gross rent / 12) × rent-free months. References Assumptions + Op P&L.
  setFormula(
    ws.getCell(r, 2),
    `${lumpSumRef}+${rentGrossY1Ref}/12*${rentFreeRef}`,
    Math.round(k.leaseIncentivePvTotal),
    { numFmt: NF.currency },
  );
  const incentivePvRow = r;
  ws.getCell(r, 3).value =
    "Lump-sum contribution + rent-free equivalent. Recalculates with rent and incentive inputs.";
  styleNote(ws.getCell(r, 3));
  thinBorderRow(ws, r, 1, 2);
  r++;

  ws.getCell(r, 1).value = "Incentive amortised per year";
  styleLabel(ws.getCell(r, 1));
  setFormula(
    ws.getCell(r, 2),
    `B${incentivePvRow}/${amortYrsRef}`,
    Math.round(k.incentiveAmortisedPerYear),
    { numFmt: NF.currency },
  );
  const incentiveAmortRow = r;
  ws.getCell(r, 3).value =
    'Incentive PV spread over amortisation period. Edit "Incentive amortisation (years)" in Assumptions.';
  styleNote(ws.getCell(r, 3));
  thinBorderRow(ws, r, 1, 2);
  r++;

  ws.getCell(r, 1).value =
    "Effective building rent Y1 (face − amortised incentive)";
  styleLabel(ws.getCell(r, 1), true);
  setFormula(
    ws.getCell(r, 2),
    `${rentGrossY1Ref}-B${incentiveAmortRow}`,
    Math.round(k.effectiveRentY1),
    { numFmt: NF.currency, bold: true },
  );
  const effectiveRentRow = r;
  thinBorderRow(ws, r, 1, 2);
  r++;

  ws.getCell(r, 1).value = "Effective building rent / revenue (Y1)";
  styleLabel(ws.getCell(r, 1));
  setFormula(
    ws.getCell(r, 2),
    `IFERROR(B${effectiveRentRow}/${totalRevY1Ref},0)`,
    k.effectiveRentPctRevenueY1,
    { numFmt: NF.pct2 },
  );
  ws.getCell(r, 3).value =
    "Building-rent ratio only (face building rent − amortised incentive). Amenity landlord rent is shown separately above. Not graded against the affordability ceilings.";
  styleNote(ws.getCell(r, 3));
  thinBorderRow(ws, r, 1, 2);
  r++;

  ws.getCell(r, 1).value = EFFECTIVE_OCCUPANCY_COST_Y1_ROW_LABEL;
  styleLabel(ws.getCell(r, 1), true);
  setFormula(
    ws.getCell(r, 2),
    `${rentGrossY1Ref}+${amenityRentY1Ref}-B${incentiveAmortRow}`,
    Math.round(k.effectiveOccupancyCostY1),
    { numFmt: NF.currency, bold: true },
  );
  const effectiveOccupancyCostRow = r;
  thinBorderRow(ws, r, 1, 2);
  r++;

  ws.getCell(r, 1).value = EFFECTIVE_OCCUPANCY_COST_RATIO_EXCEL_LABEL;
  styleLabel(ws.getCell(r, 1));
  setFormula(
    ws.getCell(r, 2),
    `IFERROR(B${effectiveOccupancyCostRow}/${totalRevY1Ref},0)`,
    k.effectiveOccupancyCostPctRevenueY1,
    { numFmt: NF.pct2 },
  );
  ws.getCell(r, 3).value = EFFECTIVE_OCCUPANCY_COST_BASIS_NOTE;
  styleNote(ws.getCell(r, 3));
  thinBorderRow(ws, r, 1, 2);
  ws.getRow(r).height = 30;
  r += 2;

  // Reconciliation — how far the illustrative cost build-up sits from the financing reference (live)
  ws.getCell(r, 1).value =
    "Illustrative build-up less financing-equivalent rent";
  styleLabel(ws.getCell(r, 1), true);
  if (floorIndicative) {
    ws.getCell(r, 2).value = "—";
    ws.getCell(r, 2).alignment = { horizontal: "center" };
    ws.getCell(r, 3).value =
      "Unavailable until the financing-equivalent rent calibration converges.";
  } else {
    setFormula(
      ws.getCell(r, 2),
      `B${costRecoveryRow}-B${exactFloorRow}`,
      Math.round(buildUp.costRecoveryRent - buildUp.calibratedRentY1),
      { numFmt: NF.currency, bold: true },
    );
  }
  styleNote(ws.getCell(r, 3));
  thinBorderRow(ws, r, 1, 2);
  ws.getRow(r).height = 30;
  r += 2;

  // Methodology note — what is a live formula vs an intentional model output, to distinguish live formulas
  // from intentional model outputs. F4: explicitly call out the pro-rata nature.
  ws.getCell(r, 1).value =
    "How to read this sheet: the component table is a pro-rata allocation of the asked building rent, NOT a cost-justified break-even floor. Five cost lines, the cost-recovery SUM, building-rent ask, total-occupancy reconciliation, margin/overreach residual and reconciliation gap are LIVE formulas wired to Dev Cost / Op P&L / Assumptions. Construction debt + holding interest is an exact model-output VALUE because the phased drawdown integral has no clean cell-by-cell formula. The financing-equivalent rent reference is likewise a model output from the matched-horizon cashflow calibration: it is a financing diagnostic and does not determine whether the stated ungeared developer hurdle is met.";
  styleNote(ws.getCell(r, 1));
  ws.mergeCells(`A${r}:C${r}`);
  const methodologyRow = r;

  ws.columns = [{ width: 52 }, { width: 24 }, { width: 50 }];
  ws.eachRow({ includeEmpty: false }, (row) => {
    row.eachCell({ includeEmpty: false }, (cell) => {
      if (cell.col === "A" || cell.col === "C" || cell.font?.italic) {
        cell.alignment = {
          ...cell.alignment,
          vertical: "top",
          wrapText: true,
        };
      }
    });
  });
  ws.getRow(2).height = 36;
  ws.getRow(methodologyRow).height = 48;
}

/* ── Debt Schedule sheet ── */
export function buildDebtSchedule(
  wb: Workbook,
  a: Assumptions,
  d: Developer,
  refs: Map<string, string>,
  devCost: ReturnType<typeof buildDevCost>,
) {
  const ws = wb.addWorksheet("Debt Schedule");
  ws.views = [{ state: "frozen", ySplit: 2 }];

  const ref = (key: string) => refs.get(key) ?? "";

  const devMonths = Math.max(1, Math.round(d.lastCompletionMonth));
  const baseRate = a.holdingInterestRate;
  const margin = a.constructionMarginRate;
  const useScurve = a.useScurveDrawdown;
  const facilityLimit = d.facilityLimitDev;
  const grossLimit = d.grossFacilityLimitDev;
  const contributionApplied = d.operatorContributionApplied;
  const contributionTiming = a.contributionTiming ?? "progressive";
  const totalCost = d.totalCost;
  const totalRate = baseRate + margin;

  const baseRateRef = ref("holdingInterestRate") || String(baseRate);
  const marginRef = ref("constructionMarginRate") || String(margin);
  const totalCostRef = `'Dev Cost'!B${devCost.totalCostRow}`;
  const facilityLimitRef = `'Dev Cost'!B${devCost.facilityLimitRow}`;
  const grossLimitRef = `'Dev Cost'!B${devCost.grossFacilityLimitRow}`;
  const contributionAppliedRef = `'Dev Cost'!B${devCost.contributionAppliedRow}`;

  // Title
  ws.getCell(1, 1).value = "DEBT SCHEDULE";
  ws.getCell(1, 1).font = {
    bold: true,
    size: 12,
    color: { argb: C.headerBg },
    name: "Arial",
  };
  ws.mergeCells("A1:J1");

  // Subtitle with cross-sheet refs
  const subtitleFormula = `="Facility limit: "&TEXT('Dev Cost'!B${devCost.facilityLimitRow},"$#,##0")&" | Total development cost: "&TEXT('Dev Cost'!B${devCost.totalCostRow},"$#,##0")&" | Base rate: "&TEXT(${baseRateRef},"0.00%")&" | Margin: "&TEXT(${marginRef},"0.00%")&" | Contribution timing: "&${ref("contributionTiming")}`;
  ws.getCell(2, 1).value = {
    formula: subtitleFormula,
    result: `Facility limit: ${formatSubtitleCurrency(facilityLimit)} | Total development cost: ${formatSubtitleCurrency(totalCost)} | Base rate: ${formatSubtitlePercent(baseRate)} | Margin: ${formatSubtitlePercent(margin)} | Contribution timing: ${contributionTiming}`,
  };
  styleNote(ws.getCell(2, 1));
  ws.mergeCells("A2:J2");

  // Headers
  const headers = [
    "Month",
    "S-curve %",
    "Cumulative drawn",
    "Draw this month",
    "Avg drawn",
    "Interest",
    "Cum. interest",
    "Total debt",
    "Non-debt funding",
    "LVR",
  ];
  const headerRow = ws.getRow(4);
  headers.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = h;
    styleHeader(cell);
  });

  // Data rows
  let previousDrawn = 0;
  let cumulativeInterest = 0;
  for (let m = 1; m <= devMonths; m++) {
    const row = ws.getRow(4 + m);
    const r = 4 + m;
    const progress = useScurve
      ? 3 * (m / devMonths) ** 2 - 2 * (m / devMonths) ** 3
      : m / devMonths;
    // Mirrors the engine's monthly target loop (model.ts): completion draws the
    // GROSS capacity across the curve, upfront nets the operator contribution off
    // the gross curve, progressive draws the net facility.
    const cumulativeDrawn =
      contributionTiming === "completion"
        ? grossLimit * progress
        : contributionTiming === "upfront"
          ? Math.max(0, grossLimit * progress - contributionApplied)
          : facilityLimit * progress;
    const drawThisMonth = cumulativeDrawn - previousDrawn;
    const averageDrawn = (previousDrawn + cumulativeDrawn) / 2;
    const interest = (averageDrawn * totalRate) / 12;
    cumulativeInterest += interest;
    const totalDebt = cumulativeDrawn + cumulativeInterest;
    const nonDebtFunding = totalCost - cumulativeDrawn;
    const lvr = totalCost > 0 ? cumulativeDrawn / totalCost : 0;

    row.getCell(1).value = m;
    styleLabel(row.getCell(1));
    thinBorder(row.getCell(1));

    // S-curve %
    if (useScurve) {
      row.getCell(2).value = {
        formula: `3*POWER(A${r}/${devMonths},2)-2*POWER(A${r}/${devMonths},3)`,
        result: progress,
      };
    } else {
      row.getCell(2).value = m / devMonths;
    }
    styleFormula(row.getCell(2));
    row.getCell(2).numFmt = NF.pct2;
    thinBorder(row.getCell(2));

    // Cumulative drawn: gross×curve (completion), gross×curve − contribution (upfront),
    // facility×curve (progressive) — branched on the contributionTiming input cell.
    row.getCell(3).value = {
      formula:
        contributionTiming === "completion"
          ? `${grossLimitRef}*B${r}`
          : contributionTiming === "upfront"
            ? `MAX(0,${grossLimitRef}*B${r}-${contributionAppliedRef})`
            : `${facilityLimitRef}*B${r}`,
      result: cumulativeDrawn,
    };
    styleFormula(row.getCell(3));
    row.getCell(3).numFmt = NF.currency;
    thinBorder(row.getCell(3));

    // Draw this month
    if (m === 1) {
      row.getCell(4).value = { formula: `C${r}`, result: drawThisMonth };
    } else {
      row.getCell(4).value = {
        formula: `C${r}-C${r - 1}`,
        result: drawThisMonth,
      };
    }
    styleFormula(row.getCell(4));
    row.getCell(4).numFmt = NF.currency;
    thinBorder(row.getCell(4));

    // Avg drawn
    if (m === 1) {
      row.getCell(5).value = { formula: `C${r}/2`, result: averageDrawn };
    } else {
      row.getCell(5).value = {
        formula: `(C${r}+C${r - 1})/2`,
        result: averageDrawn,
      };
    }
    styleFormula(row.getCell(5));
    row.getCell(5).numFmt = NF.currency;
    thinBorder(row.getCell(5));

    // Interest = avg drawn * (baseRate + margin) / 12
    row.getCell(6).value = {
      formula: `E${r}*(${baseRateRef}+${marginRef})/12`,
      result: interest,
    };
    styleFormula(row.getCell(6));
    row.getCell(6).numFmt = NF.currency;
    thinBorder(row.getCell(6));

    // Cumulative interest
    if (m === 1) {
      row.getCell(7).value = { formula: `F${r}`, result: cumulativeInterest };
    } else {
      row.getCell(7).value = {
        formula: `G${r - 1}+F${r}`,
        result: cumulativeInterest,
      };
    }
    styleFormula(row.getCell(7));
    row.getCell(7).numFmt = NF.currency;
    thinBorder(row.getCell(7));

    // Total debt = cumulative drawn + cumulative interest
    row.getCell(8).value = {
      formula: `C${r}+G${r}`,
      result: totalDebt,
    };
    styleFormula(row.getCell(8));
    row.getCell(8).numFmt = NF.currency;
    thinBorder(row.getCell(8));

    // Non-debt funding includes both developer equity and the operator contribution.
    row.getCell(9).value = {
      formula: `${totalCostRef}-C${r}`,
      result: nonDebtFunding,
    };
    styleFormula(row.getCell(9));
    row.getCell(9).numFmt = NF.currency;
    thinBorder(row.getCell(9));

    // LVR = cumulative drawn / totalCost
    row.getCell(10).value = {
      formula: `IFERROR(C${r}/${totalCostRef},0)`,
      result: lvr,
    };
    styleFormula(row.getCell(10));
    row.getCell(10).numFmt = NF.pct2;
    thinBorder(row.getCell(10));

    previousDrawn = cumulativeDrawn;
  }

  // Summary row
  const lastDataRow = 4 + devMonths;
  const sumRow = lastDataRow + 1;
  ws.getCell(sumRow, 1).value = "TOTAL";
  ws.getCell(sumRow, 1).font = { bold: true, name: "Arial", size: 10 };
  thinBorder(ws.getCell(sumRow, 1));

  ws.getCell(sumRow, 3).value = {
    formula: `C${lastDataRow}`,
    result: previousDrawn,
  };
  ws.getCell(sumRow, 3).numFmt = NF.currency;
  ws.getCell(sumRow, 3).font = { bold: true, name: "Arial", size: 10 };
  thinBorder(ws.getCell(sumRow, 3));

  ws.getCell(sumRow, 6).value = {
    formula: `SUM(F5:F${lastDataRow})`,
    result: cumulativeInterest,
  };
  ws.getCell(sumRow, 6).numFmt = NF.currency;
  ws.getCell(sumRow, 6).font = { bold: true, name: "Arial", size: 10 };
  thinBorder(ws.getCell(sumRow, 6));

  // The line fee is sized by Dev Cost's timing-aware row (gross base under
  // completion, net facility otherwise) — never recompute it here off the net limit.
  ws.getCell(sumRow, 7).value = {
    formula: `'Dev Cost'!B${devCost.lineFeeRow}`,
    result: d.lineFee,
  };
  ws.getCell(sumRow, 7).numFmt = NF.currency;
  ws.getCell(sumRow, 7).font = { bold: true, name: "Arial", size: 10 };
  thinBorder(ws.getCell(sumRow, 7));

  ws.getCell(sumRow, 8).value = {
    formula: `H${lastDataRow}+'Dev Cost'!B${devCost.lineFeeRow}`,
    result: previousDrawn + cumulativeInterest + d.lineFee,
  };
  ws.getCell(sumRow, 8).numFmt = NF.currency;
  ws.getCell(sumRow, 8).font = { bold: true, name: "Arial", size: 10 };
  thinBorder(ws.getCell(sumRow, 8));

  ws.getCell(sumRow, 9).value = {
    formula: `${totalCostRef}-C${lastDataRow}`,
    result: totalCost - previousDrawn,
  };
  ws.getCell(sumRow, 9).numFmt = NF.currency;
  ws.getCell(sumRow, 9).font = { bold: true, name: "Arial", size: 10 };
  thinBorder(ws.getCell(sumRow, 9));

  ws.getCell(sumRow, 10).value = {
    formula: `J${lastDataRow}`,
    result: totalCost > 0 ? previousDrawn / totalCost : 0,
  };
  ws.getCell(sumRow, 10).numFmt = NF.pct2;
  ws.getCell(sumRow, 10).font = { bold: true, name: "Arial", size: 10 };
  thinBorder(ws.getCell(sumRow, 10));

  for (let c = 1; c <= 10; c++) {
    if (![1, 3, 6, 7, 8, 9, 10].includes(c)) {
      thinBorder(ws.getCell(sumRow, c));
    }
  }

  ws.columns = [
    { width: 8 },
    { width: 12 },
    { width: 16 },
    { width: 16 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 16 },
    { width: 14 },
    { width: 10 },
  ];
}

/* ── Interest Rate Sensitivity sheet ── */
export function buildInterestSensitivity(
  wb: Workbook,
  a: Assumptions,
  d: Developer,
  refs: Map<string, string>,
  devCost: ReturnType<typeof buildDevCost>,
) {
  const ws = wb.addWorksheet("Interest Sensitivity");
  ws.views = [{ state: "frozen", ySplit: 2 }];

  const ref = (key: string) => refs.get(key) ?? "";

  const baseRate = a.holdingInterestRate;
  const margin = a.constructionMarginRate;
  const facilityLimit = d.facilityLimitDev;
  const devMonths = Math.max(1, Math.round(d.lastCompletionMonth));

  const baseRateRef = ref("holdingInterestRate") || String(baseRate);
  const marginRef = ref("constructionMarginRate") || String(margin);

  ws.getCell(1, 1).value = "INTEREST RATE SENSITIVITY";
  ws.getCell(1, 1).font = {
    bold: true,
    size: 12,
    color: { argb: C.headerBg },
    name: "Arial",
  };
  ws.mergeCells("A1:F1");

  const grossLimit = d.grossFacilityLimitDev;
  const contributionApplied = d.operatorContributionApplied;
  const contributionTiming = a.contributionTiming ?? "progressive";
  // Peak construction debt (not the net facility) is the figure the interest
  // integrates against under every timing — gross under completion, net otherwise.
  const peakDebtRef = `'Dev Cost'!B${devCost.peakDebtRow}`;
  const subtitleFormula = `="Base rate: "&TEXT(${baseRateRef},"0.00%")&" | Construction margin: "&TEXT(${marginRef},"0.00%")&" | Peak construction debt: "&TEXT(${peakDebtRef},"$#,##0")&" | Contribution timing: "&${ref("contributionTiming")}`;
  ws.getCell(2, 1).value = {
    formula: subtitleFormula,
    result: `Base rate: ${formatSubtitlePercent(baseRate)} | Construction margin: ${formatSubtitlePercent(margin)} | Peak construction debt: ${formatSubtitleCurrency(d.peakDebtDev)} | Contribution timing: ${contributionTiming}`,
  };
  styleNote(ws.getCell(2, 1));
  ws.mergeCells("A2:F2");

  // Headers
  const headers = [
    "Scenario",
    "Total rate",
    "Annual interest",
    "Total interest (dev)",
    "Equity reqd",
    "Impact on profit",
  ];
  const headerRow = ws.getRow(4);
  headers.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = h;
    styleHeader(cell);
  });

  const scenarios = [
    { label: "Base case", delta: 0 },
    { label: "Rate -100 bps", delta: -0.01 },
    { label: "Rate -50 bps", delta: -0.005 },
    { label: "Rate +50 bps", delta: 0.005 },
    { label: "Rate +100 bps", delta: 0.01 },
  ];

  // Timing-aware construction interest at an arbitrary total rate — mirrors the
  // engine's monthly target loop (model.ts): completion draws the GROSS capacity,
  // upfront nets the operator contribution off the gross curve, progressive draws
  // the net facility. The drawn SHAPE is rate-independent, so interest(R) =
  // Σ(avg drawn) × R / 12 for every timing.
  function constructionInterestAt(totalRate: number): number {
    const M = Math.max(1, devMonths);
    let interest = 0;
    let drawn = 0;
    for (let m = 1; m <= M; m++) {
      const t = m / M;
      const curve = a.useScurveDrawdown ? 3 * t * t - 2 * t * t * t : t;
      const grossTarget = grossLimit * curve;
      const target =
        contributionTiming === "completion"
          ? grossTarget
          : contributionTiming === "upfront"
            ? Math.max(0, grossTarget - contributionApplied)
            : facilityLimit * curve;
      const avgDrawn = (drawn + target) / 2;
      interest += (avgDrawn * totalRate) / 12;
      drawn = target;
    }
    return interest;
  }

  const baseInterest = constructionInterestAt(baseRate + margin);

  scenarios.forEach((s, idx) => {
    const row = ws.getRow(5 + idx);
    const r = 5 + idx;
    const totalRate = baseRate + margin + s.delta;
    const totalInterest = constructionInterestAt(totalRate);
    const annualInterest = devMonths > 0 ? (totalInterest * 12) / devMonths : 0;
    const impact = baseInterest - totalInterest;

    row.getCell(1).value = s.label;
    styleLabel(row.getCell(1));
    thinBorder(row.getCell(1));

    row.getCell(2).value = {
      formula: `=${baseRateRef}+${marginRef}+${s.delta}`,
      result: totalRate,
    };
    styleFormula(row.getCell(2));
    row.getCell(2).numFmt = NF.pct2;
    thinBorder(row.getCell(2));

    row.getCell(3).value = {
      formula: `=D${r}*12/${Math.max(1, devMonths)}`,
      result: annualInterest,
    };
    styleFormula(row.getCell(3));
    row.getCell(3).numFmt = NF.currency;
    thinBorder(row.getCell(3));

    // The drawn series is rate-independent, so interest at ANY total rate =
    // Σ(avg drawn) × rate / 12 — and Debt Schedule column E is exactly Σ(avg drawn),
    // already contribution-timing aware (loop#015). Cached `result` mirrors the
    // engine's monthly target loop via constructionInterestAt().
    row.getCell(4).value = {
      formula: `=SUM('Debt Schedule'!E5:E${4 + Math.max(1, devMonths)})*B${r}/12`,
      result: totalInterest,
    };
    styleFormula(row.getCell(4));
    row.getCell(4).numFmt = NF.currency;
    thinBorder(row.getCell(4));

    // Equity required ties to Dev Cost's row (gross-capacity based, timing-aware cached)
    // rather than totalCost − net facility, which diverges under completion timing.
    row.getCell(5).value = {
      formula: `='Dev Cost'!B${devCost.equityReqRow}`,
      result: d.equityRequired,
    };
    styleFormula(row.getCell(5));
    row.getCell(5).numFmt = NF.currency;
    thinBorder(row.getCell(5));

    row.getCell(6).value = { formula: `=$D$5-D${r}`, result: impact };
    styleFormula(row.getCell(6));
    row.getCell(6).numFmt = NF.currency;
    thinBorder(row.getCell(6));
  });

  ws.columns = [
    { width: 16 },
    { width: 12 },
    { width: 16 },
    { width: 18 },
    { width: 14 },
    { width: 18 },
  ];
}

/* ── LVR Sensitivity sheet ── */
export function buildLVRSensitivity(
  wb: Workbook,
  a: Assumptions,
  d: Developer,
  refs: Map<string, string>,
  devCost: ReturnType<typeof buildDevCost>,
) {
  const ws = wb.addWorksheet("LVR Sensitivity");
  ws.views = [{ state: "frozen", ySplit: 2 }];

  const ref = (key: string) => refs.get(key) ?? "";

  const baseLvr = a.constructionLvrRate;
  const totalCost = d.totalCost;

  const lvrRef = ref("constructionLvrRate") || String(baseLvr);
  const totalCostRef = `'Dev Cost'!B${devCost.totalCostRow}`;
  const grossFacilityRef = `'Dev Cost'!B${devCost.grossFacilityLimitRow}`;
  const eligibleCost = baseLvr > 0 ? d.grossFacilityLimitDev / baseLvr : 0;

  ws.getCell(1, 1).value = "CONSTRUCTION LVR SENSITIVITY";
  ws.getCell(1, 1).font = {
    bold: true,
    size: 12,
    color: { argb: C.headerBg },
    name: "Arial",
  };
  ws.mergeCells("A1:E1");

  ws.getCell(2, 1).value =
    `Base LVR: ${fmtPct(baseLvr)} | Total development cost: ${fmtCompactMoney(totalCost)}`;
  styleNote(ws.getCell(2, 1));
  ws.mergeCells("A2:E2");

  // Headers
  const headers = [
    "Scenario",
    "LVR",
    "Facility limit",
    EQUITY_REQUIRED_LABEL,
    "Equity %",
  ];
  const headerRow = ws.getRow(4);
  headers.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = h;
    styleHeader(cell);
  });

  const scenarios = [
    { label: "Base case", lvr: baseLvr },
    { label: "LVR -10%", lvr: Math.max(0, baseLvr - 0.1) },
    { label: "LVR -5%", lvr: Math.max(0, baseLvr - 0.05) },
    { label: "LVR +5%", lvr: Math.min(1, baseLvr + 0.05) },
    { label: "LVR +10%", lvr: Math.min(1, baseLvr + 0.1) },
  ];

  scenarios.forEach((s, idx) => {
    const row = ws.getRow(5 + idx);
    const r = 5 + idx;
    const facility = eligibleCost * s.lvr;
    const equity = totalCost - facility;
    const equityPct = totalCost > 0 ? equity / totalCost : 0;

    row.getCell(1).value = s.label;
    styleLabel(row.getCell(1));
    thinBorder(row.getCell(1));

    // All LVR scenarios are formulas referencing base LVR with delta
    const delta = s.lvr - baseLvr;
    const lvrFormula =
      delta === 0
        ? lvrRef
        : `MAX(0,MIN(1,${lvrRef}${delta >= 0 ? "+" : ""}${delta}))`;
    row.getCell(2).value = { formula: "=" + lvrFormula, result: s.lvr };
    styleFormula(row.getCell(2));
    row.getCell(2).numFmt = NF.pct2;
    thinBorder(row.getCell(2));

    row.getCell(3).value = {
      formula:
        delta === 0
          ? `=${grossFacilityRef}`
          : `=IF(${lvrRef}=0,0,${grossFacilityRef}/${lvrRef}*B${r})`,
      result: facility,
    };
    styleFormula(row.getCell(3));
    row.getCell(3).numFmt = NF.currency;
    thinBorder(row.getCell(3));

    row.getCell(4).value = {
      formula: `=${totalCostRef}-C${r}`,
      result: equity,
    };
    styleFormula(row.getCell(4));
    row.getCell(4).numFmt = NF.currency;
    thinBorder(row.getCell(4));

    row.getCell(5).value = {
      formula: `=D${r}/${totalCostRef}`,
      result: equityPct,
    };
    styleFormula(row.getCell(5));
    row.getCell(5).numFmt = NF.pct2;
    thinBorder(row.getCell(5));
  });

  ws.columns = [
    { width: 14 },
    { width: 10 },
    { width: 16 },
    { width: 16 },
    { width: 12 },
  ];
}

/* ── Scenarios comparison sheet ── */
export async function buildScenarios(
  wb: Workbook,
  activeScenario: Scenario,
  scenarios: Scenario[],
  results: Record<string, ModelResult>,
) {
  const comparableScenarios = sameProjectScenarios(activeScenario, scenarios);
  const ws = wb.addWorksheet("Scenarios");
  ws.views = [{ state: "frozen", ySplit: 1 }];

  // Header note
  ws.getCell(1, 1).value =
    comparableScenarios.length === 1
      ? "Active-project scenario comparison — no documented same-site comparator is loaded; unrelated saved-site scenarios are excluded."
      : "Model-computed scenario comparison for the active project only — unrelated saved-site scenarios are excluded.";
  styleNote(ws.getCell(1, 1));
  ws.mergeCells("A1:M1");

  const headers = [
    "Scenario",
    "Developer horizon",
    "Primary ungeared IRR",
    "Primary hurdle status",
    "Margin",
    "Profit",
    "Money multiple",
    "Op EBITDA margin",
    "Rent / student",
    OPERATOR_NPV_LABEL,
    OPERATOR_IRR_LABEL,
    OPERATOR_PAYBACK_LABEL,
    "Cashflow trajectory",
  ];
  const headerRow = ws.addRow(headers);
  headerRow.eachCell((cell) => styleHeader(cell));

  for (let si = 0; si < comparableScenarios.length; si++) {
    const s = comparableScenarios[si];
    const r = results[s.id];
    const hStat = r.kpis.developerPrimaryHurdleStatus;
    const exitYear =
      s.assumptions.developerExitYear ?? s.assumptions.modelYears;
    const horizon = exitYear < s.assumptions.modelYears ? `Y${exitYear}` : "PC";
    const yEnd = r.years[r.years.length - 1];
    const y1 = r.years[0];
    const ebitdaMargin =
      yEnd && yEnd.totalRevenue > 0
        ? yEnd.reddamEbitda / yEnd.totalRevenue
        : null;
    const rentPerStudent =
      y1 && y1.enrolment > 0 ? y1.rentGross / y1.enrolment : null;

    const row = ws.addRow([
      s.name,
      horizon,
      r.kpis.developerPrimaryIrr,
      hStat === "met" ? "PASS" : hStat === "marginal" ? "MARGINAL" : "FAIL",
      r.kpis.developerMarginOnCost,
      Math.round(r.developer.profit),
      Number.isFinite(r.kpis.developerMoneyMultiple)
        ? r.kpis.developerMoneyMultiple
        : null,
      ebitdaMargin,
      rentPerStudent,
      Math.round(r.kpis.reddamNpv),
      r.kpis.reddamIrr ?? null,
      r.kpis.paybackYear ? `Y${r.kpis.paybackYear}` : "—",
      null, // placeholder for sparkline image
    ]);

    row.eachCell((cell, colNumber) => {
      thinBorder(cell);
      if (colNumber === 1) {
        cell.font = { bold: true, name: "Arial", size: 10 };
      } else if (colNumber === 4) {
        const val = cell.value as string;
        if (val === "PASS") {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: C.passGreen },
          };
          cell.font = {
            bold: true,
            color: { argb: C.passGreenText },
            name: "Arial",
            size: 10,
          };
        } else if (val === "FAIL") {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: C.failRed },
          };
          cell.font = {
            bold: true,
            color: { argb: C.failRedText },
            name: "Arial",
            size: 10,
          };
        } else {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: C.warnAmber },
          };
          cell.font = {
            bold: true,
            color: { argb: C.warnAmberText },
            name: "Arial",
            size: 10,
          };
        }
        cell.alignment = { horizontal: "center" };
      } else if ([3, 5, 8, 11].includes(colNumber)) {
        cell.numFmt = NF.pct2;
      } else if ([6, 9, 10].includes(colNumber)) {
        cell.numFmt = NF.currency;
      } else if (colNumber === 7) {
        cell.numFmt = NF.num;
      }
    });

    // Generate and embed sparkline for cumulative cashflow
    const cashflowValues = r.years.map((y) => y.cumulativeCashflow);
    if (cashflowValues.length > 1) {
      try {
        const svg = renderSparklineSvg(cashflowValues, 100, 24);
        const pngDataUrl = await svgElementToPngDataUrl(svg, 2);
        if (pngDataUrl && pngDataUrl.startsWith("data:image/png;base64,")) {
          const base64 = pngDataUrl.replace("data:image/png;base64,", "");
          const imageId = wb.addImage({ base64, extension: "png" });
          // Zero-based exceljs anchor. Cashflow trajectory is col M (zero-based 12)
          // after adding the explicit selected-horizon column; data row for scenario si
          // is 1-based si+3 (note row1, header row2, data row3) = zero-based si+2.
          ws.addImage(imageId, {
            tl: {
              col: 12,
              row: si + 2,
              nativeCol: 12,
              nativeColOff: 0,
              nativeRow: si + 2,
              nativeRowOff: 0,
            } as any,
            br: {
              col: 13,
              row: si + 3,
              nativeCol: 13,
              nativeColOff: 0,
              nativeRow: si + 3,
              nativeRowOff: 0,
            } as any,
          });
        }
      } catch {
        // Sparkline generation failed — leave cell empty
      }
    }
  }

  ws.columns = [
    { width: 14 },
    { width: 12 },
    { width: 10 },
    { width: 12 },
    { width: 10 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 10 },
    { width: 10 },
    { width: 12 },
    { width: 18 },
  ];
}

/* ── Monthly cashflow sheet ── */
export function buildMonthly(wb: Workbook, mr: MonthlyResult) {
  const ws = wb.addWorksheet("Monthly");
  ws.views = [{ state: "frozen", ySplit: 1 }];

  // Header note
  ws.getCell(1, 1).value = "Data table — model-generated monthly breakdown";
  styleNote(ws.getCell(1, 1));
  ws.mergeCells("A1:J1");

  const headers = [
    "Month",
    "Phase",
    "Dev outflow",
    "Enrolment",
    "Utilisation",
    "Revenue",
    "Rent + opex",
    "Net income",
    "Cumulative",
    "Notes",
  ];
  const headerRow = ws.addRow(headers);
  headerRow.eachCell((cell) => styleHeader(cell));

  mr.months.forEach((m) => {
    const row = ws.addRow([
      m.label,
      m.phase,
      m.developerOutflow > 0 ? Math.round(m.developerOutflow) : null,
      m.enrolment == null ? null : Number(m.enrolment.toFixed(1)),
      m.utilisation == null ? null : m.utilisation,
      m.reddamRevenue > 0 ? Math.round(m.reddamRevenue) : null,
      m.reddamOutflow > 0 ? Math.round(m.reddamOutflow) : null,
      Math.round(m.reddamNetIncome),
      Math.round(m.cumulativeCashflow),
      m.notes || "",
    ]);

    row.eachCell((cell, colNumber) => {
      thinBorder(cell);
      if (colNumber === 2) {
        const phase = cell.value as string;
        if (phase === "development") {
          cell.font = { color: { argb: "666666" }, name: "Arial", size: 10 };
        } else if (phase === "ramp") {
          cell.font = {
            color: { argb: C.headerBg },
            bold: true,
            name: "Arial",
            size: 10,
          };
        } else if (phase === "steady") {
          cell.font = {
            color: { argb: C.passGreenText },
            bold: true,
            name: "Arial",
            size: 10,
          };
        }
        cell.alignment = { horizontal: "center" };
      } else if (colNumber === 5) {
        cell.numFmt = NF.pct;
      } else if ([3, 6, 7, 8, 9].includes(colNumber)) {
        cell.numFmt = NF.currency;
      }
    });
  });

  ws.columns = [
    { width: 12 },
    { width: 12 },
    { width: 14 },
    { width: 10 },
    { width: 10 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 28 },
  ];
}

/* ── Dev Drawdown sheet ── */
export function buildMonthlyDev(wb: Workbook, mr: MonthlyResult) {
  const ws = wb.addWorksheet("Dev Drawdown");
  ws.views = [{ state: "frozen", ySplit: 1 }];

  // Header note
  ws.getCell(1, 1).value = "Data table — model-generated development drawdown";
  styleNote(ws.getCell(1, 1));
  ws.mergeCells("A1:C1");

  const devMonths = mr.months.filter((m) => m.phase === "development");
  const headers = ["Month", "Label", "Developer outflow"];
  const headerRow = ws.addRow(headers);
  headerRow.eachCell((cell) => styleHeader(cell));

  devMonths.forEach((m) => {
    const row = ws.addRow([m.month, m.label, Math.round(m.developerOutflow)]);
    row.eachCell((cell, colNumber) => {
      thinBorder(cell);
      if (colNumber === 3) cell.numFmt = NF.currency;
    });
  });

  // Total row with SUM formula
  const n = devMonths.length;
  const sumFormula =
    n > 0
      ? {
          formula: `SUM(C3:C${n + 2})`,
          result: devMonths.reduce((s, m) => s + m.developerOutflow, 0),
        }
      : 0;
  const totalRow = ws.addRow(["", "Total development spend", sumFormula]);
  totalRow.eachCell((cell, colNumber) => {
    styleTotal(cell);
    if (colNumber === 3) {
      cell.numFmt = NF.currency;
      styleFormula(cell);
    }
  });

  ws.columns = [{ width: 10 }, { width: 16 }, { width: 18 }];
}

/* ── Two-Way Sensitivity sheet ── */
export function buildTwoWaySensitivity(
  wb: Workbook,
  scenario: Scenario,
  result: ModelResult,
) {
  const ws = wb.addWorksheet("Two-Way Sensitivity");
  ws.views = [{ state: "frozen", ySplit: 3 }];

  // Default drivers: capRate (exit yield) vs developer capex/sqm
  const rowDriver = Model.DRIVERS.find((d) => d.key === "capRate")!;
  const colDriver = Model.DRIVERS.find((d) => d.key === "devCapex")!;
  const hurdle = scenario.assumptions.developerHurdleIRR;
  const exitYear =
    scenario.assumptions.developerExitYear ?? scenario.assumptions.modelYears;
  const primaryHorizon =
    exitYear < scenario.assumptions.modelYears ? `Y${exitYear}` : "PC";

  // Title
  ws.getCell(1, 1).value = "TWO-WAY SENSITIVITY";
  ws.getCell(1, 1).font = {
    bold: true,
    size: 12,
    color: { argb: C.headerBg },
    name: "Arial",
  };
  ws.mergeCells("A1:F1");

  // Subtitle
  ws.getCell(2, 1).value =
    `${rowDriver.label} × ${colDriver.label} → matched-horizon ungeared IRR (${primaryHorizon})`;
  styleNote(ws.getCell(2, 1), C.mutedGrey);
  ws.mergeCells("A2:F2");

  // Data table note
  ws.getCell(3, 1).value =
    "Data table — model recomputed for each assumption combination";
  styleNote(ws.getCell(3, 1));
  ws.mergeCells("A3:F3");

  // Flex steps: ±20% in 10% increments
  const steps = [-0.2, -0.1, 0, 0.1, 0.2];

  // Header row (column driver values)
  const headerRow = ws.getRow(4);
  headerRow.getCell(1).value = `${colDriver.label} →`;
  styleHeader(headerRow.getCell(1));
  steps.forEach((step, i) => {
    const cell = headerRow.getCell(i + 2);
    cell.value =
      step === 0
        ? "Base"
        : `${step >= 0 ? "+" : ""}${(step * 100).toFixed(0)}%`;
    styleHeader(cell);
  });

  // Data rows
  steps.forEach((rowStep, ri) => {
    const row = ws.getRow(5 + ri);
    const labelCell = row.getCell(1);
    labelCell.value =
      rowStep === 0
        ? `${rowDriver.label} (base)`
        : `${rowDriver.label} ${rowStep >= 0 ? "+" : ""}${(rowStep * 100).toFixed(0)}%`;
    styleLabel(labelCell, true);
    thinBorder(labelCell);

    steps.forEach((colStep, ci) => {
      const cell = row.getCell(ci + 2);
      // Clone scenario and apply both drivers
      const cloned: Scenario = JSON.parse(JSON.stringify(scenario));
      rowDriver.apply(cloned, rowStep);
      colDriver.apply(cloned, colStep);
      // The selected-horizon primary IRR is a core KPI — skip break-even solvers for this hot loop.
      const r = Model.computeCore(cloned);
      const val = r.kpis.developerPrimaryIrr;

      cell.value = val;
      cell.note =
        "Model-recomputed sensitivity output for the stated two-driver case; not a user override or editable assumption.";
      cell.numFmt = NF.pct2;
      thinBorder(cell);

      // Heat-map conditional formatting
      if (val >= hurdle) {
        stylePass(cell);
      } else if (val >= hurdle * 0.9) {
        styleWarn(cell);
      } else {
        styleFail(cell);
      }
      cell.alignment = { horizontal: "center" };
    });
  });

  // Base case reference row
  const baseRow = ws.getRow(11);
  baseRow.getCell(1).value = "Base case";
  styleLabel(baseRow.getCell(1), true);
  baseRow.getCell(2).value = result.kpis.developerPrimaryIrr;
  baseRow.getCell(2).numFmt = NF.pct2;
  baseRow.getCell(3).value = `Hurdle: ${(hurdle * 100).toFixed(1)}%`;
  styleNote(baseRow.getCell(3), C.mutedGrey);

  ws.columns = [
    { width: 22 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
  ];
}

/* ── Census Demographics sheet ── */
export function buildCensus(wb: Workbook, scenario: Scenario) {
  const ws = wb.addWorksheet("Census");
  ws.views = [{ state: "frozen", ySplit: 2 }];

  const c = Census.catchmentFor(scenario);
  // P8-R3 (#22): use the scenario's enrolmentCaptureRate (was hardcoded 0.15) with a dynamic label.
  const captureRate = Number.isFinite(scenario.assumptions.enrolmentCaptureRate)
    ? (scenario.assumptions.enrolmentCaptureRate as number)
    : 0.15;
  const capturePct = Math.round(captureRate * 100);

  // Title
  ws.getCell(1, 1).value = "CATCHMENT DEMOGRAPHICS";
  ws.getCell(1, 1).font = {
    bold: true,
    size: 12,
    color: { argb: C.headerBg },
    name: "Arial",
  };
  ws.mergeCells("A1:D1");

  // Headers
  const headers = ["Metric", "Value", "Unit", "Source"];
  headers.forEach((h, i) => {
    const cell = ws.getCell(2, i + 1);
    cell.value = h;
    styleHeader(cell);
  });

  const rows = [
    [
      "Area name",
      c.area_name,
      "",
      `ABS Census ${c.census_year} ${c.area_type} ${c.area_code}`,
    ],
    ["Total population", c.total_population, "people", "QuickStats"],
    ["Median age", c.median_age, "years", "QuickStats"],
    ["Total families", c.total_families, "families", "QuickStats"],
    [
      "Avg children per family",
      c.avg_children_per_family,
      "children",
      "QuickStats",
    ],
    ["Total dwellings", c.total_dwellings, "dwellings", "QuickStats"],
    ["Occupied dwellings", c.occupied_dwellings, "dwellings", "QuickStats"],
    [
      "Median weekly household income",
      c.median_weekly_household_income,
      "$/week",
      "QuickStats",
    ],
    [
      "Annual median household income",
      c.annual_median_household_income,
      "$/year",
      "Derived",
    ],
    ["Median weekly rent", c.median_weekly_rent, "$/week", "QuickStats"],
    [
      "Median monthly mortgage",
      c.median_monthly_mortgage,
      "$/month",
      "QuickStats",
    ],
    ["Family households", c.family_households, "households", "QuickStats"],
    [
      "Single person households",
      c.single_person_households,
      "households",
      "QuickStats",
    ],
    ["Owned outright", c.owned_outright, "dwellings", "QuickStats"],
    ["Owned with mortgage", c.owned_with_mortgage, "dwellings", "QuickStats"],
    ["Rented", c.rented, "dwellings", "QuickStats"],
    ["Preschool attendees", c.preschool_attendees, "children", "QuickStats"],
    ["Primary school attendees", c.primary_total, "children", "QuickStats"],
    ["Secondary school attendees", c.secondary_total, "children", "QuickStats"],
    ["Tertiary attendees", c.tertiary_total, "students", "QuickStats"],
    [
      "School-age children (5-17)",
      c.school_age_children_estimate,
      "children",
      "Derived (Primary + Secondary)",
    ],
    // W6c — demand analytics (heuristic-labelled, not measurements)
    [
      `Est. enrolment pool (${capturePct}% capture)`,
      Census.estimatedEnrolmentPool(c, captureRate),
      "students",
      `${capturePct}% capture rate of school-age children — competitive-market heuristic; adjust via catchment assumptions`,
    ],
    [
      "Market demand index",
      Census.marketDemandIndex(c) ?? 0,
      "index",
      "School-age children × independent-school propensity; relative measure only",
    ],
  ];

  rows.forEach((row, ri) => {
    const r = ws.getRow(3 + ri);
    r.getCell(1).value = row[0];
    r.getCell(2).value = row[1];
    r.getCell(3).value = row[2];
    r.getCell(4).value = row[3];
    for (let cidx = 1; cidx <= 4; cidx++) {
      thinBorder(r.getCell(cidx));
    }
    if (typeof row[1] === "number") {
      r.getCell(2).alignment = { horizontal: "right" };
      r.getCell(2).numFmt = "#,##0";
    }
  });

  ws.columns = [{ width: 40 }, { width: 16 }, { width: 12 }, { width: 30 }];
}

/* ── Lease vs Own sheet (W3) ── */
export function buildLeaseVsOwnSheet(
  wb: Workbook,
  scenario: Scenario,
  result: ModelResult,
  refs: Map<string, string>,
  opPl: ReturnType<typeof buildOpPL>,
  devCost: ReturnType<typeof buildDevCost>,
) {
  const ws = wb.addWorksheet("Lease vs Own");
  ws.views = [{ state: "frozen", ySplit: 2 }];

  const lvo = Model.computeLeaseVsOwn(scenario, result);
  const a = scenario.assumptions;

  // Title
  ws.getCell(1, 1).value = "LEASE VS OWN — ILLUSTRATIVE NPV COMPARISON";
  ws.getCell(1, 1).font = {
    bold: true,
    size: 12,
    color: { argb: C.headerBg },
    name: "Arial",
  };
  ws.mergeCells("A1:D1");

  ws.getCell(2, 1).value =
    "All values are illustrative. See mandatory caveats at the bottom of this sheet. This is a simplified NPV comparison — it does not substitute for a formal lease-vs-own analysis by a qualified valuer.";
  styleNote(ws.getCell(2, 1));
  ws.getCell(2, 1).alignment = { vertical: "middle", wrapText: true };
  ws.getRow(2).height = 32;
  ws.mergeCells("A2:D2");

  let r = 4;

  const section = (title: string) => {
    ws.getCell(r, 1).value = title;
    ws.getCell(r, 1).font = {
      bold: true,
      color: { argb: C.headerText },
      name: "Arial",
      size: 10,
    };
    ["A", "B", "C", "D"].forEach((col) => {
      ws.getCell(r, ["A", "B", "C", "D"].indexOf(col) + 1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: C.headerBg },
      };
    });
    thinBorderRow(ws, r, 1, 4);
    r++;
  };

  const writeLeaseVsOwnNote = (row: number, note: string) => {
    const cell = ws.getCell(row, 4);
    cell.value = note;
    styleNote(cell);
    cell.alignment = { vertical: "middle", wrapText: true };
    ws.getRow(row).height = Math.max(20, Math.ceil(note.length / 62) * 15);
  };

  // Helpers for static-value and formula rows
  const row3 = (
    label: string,
    lease: string | number,
    own: string | number,
    note = "",
  ) => {
    ws.getCell(r, 1).value = label;
    styleLabel(ws.getCell(r, 1));
    ws.getCell(r, 2).value = lease;
    styleFormula(ws.getCell(r, 2));
    ws.getCell(r, 2).numFmt = typeof lease === "number" ? NF.currency : "@";
    ws.getCell(r, 3).value = own;
    styleFormula(ws.getCell(r, 3));
    ws.getCell(r, 3).numFmt = typeof own === "number" ? NF.currency : "@";
    if (note) {
      writeLeaseVsOwnNote(r, note);
    }
    thinBorderRow(ws, r, 1, 4);
    r++;
  };
  const row3F = (
    label: string,
    leaseF: string | null,
    leaseV: number | string,
    ownF: string | null,
    ownV: number | string,
    note = "",
  ) => {
    ws.getCell(r, 1).value = label;
    styleLabel(ws.getCell(r, 1));
    if (leaseF) {
      setFormula(ws.getCell(r, 2), leaseF, leaseV as number, {
        numFmt: typeof leaseV === "number" ? NF.currency : "@",
      });
    } else {
      ws.getCell(r, 2).value = leaseV;
      ws.getCell(r, 2).numFmt = typeof leaseV === "number" ? NF.currency : "@";
    }
    styleFormula(ws.getCell(r, 2));
    if (ownF) {
      setFormula(ws.getCell(r, 3), ownF, ownV as number, {
        numFmt: typeof ownV === "number" ? NF.currency : "@",
      });
    } else {
      ws.getCell(r, 3).value = ownV;
      ws.getCell(r, 3).numFmt = typeof ownV === "number" ? NF.currency : "@";
    }
    styleFormula(ws.getCell(r, 3));
    if (note) {
      writeLeaseVsOwnNote(r, note);
    }
    thinBorderRow(ws, r, 1, 4);
    r++;
    return r - 1; // return the row that was just written
  };

  // Cross-sheet formula building blocks for NPV comparison
  const schoolWaccRef = refs.get("schoolWacc") ?? "";
  const rentGrossRowNum = opPl.rowOf("rentGross");
  const rentGrossY1CellW3 = addr(rentGrossRowNum - 1, 1);
  const rentGrossLastCell = addr(rentGrossRowNum - 1, result.years.length);
  const rentGrossRange = `'Op P&L'!${rentGrossY1CellW3}:'Op P&L'!${rentGrossLastCell}`;
  const amenityRentRowNum = opPl.rowOf("amenityRent");
  const amenityRentY1Cell = addr(amenityRentRowNum - 1, 1);
  const amenityRentLastCell = addr(amenityRentRowNum - 1, result.years.length);
  const amenityRentRange = `'Op P&L'!${amenityRentY1Cell}:'Op P&L'!${amenityRentLastCell}`;
  // Lease PV includes both building gross rent and amenity landlord rent, matching the model.
  const leasePvFormula = `-NPV(${schoolWaccRef},${rentGrossRange})-NPV(${schoolWaccRef},${amenityRentRange})`;
  // ownPathCapex = total developer cost
  const capexFormula = `='Dev Cost'!B${devCost.totalCostRow}`;
  // terminalValue = net realisation from Dev Cost
  const terminalValFormula = `='Dev Cost'!B${devCost.netRealRow}`;
  // terminalPv = terminal / (1+wacc)^n
  const terminalPvFormula = `='Dev Cost'!B${devCost.netRealRow}/POWER(1+${schoolWaccRef},${result.years.length})`;

  // Column headers
  ws.getCell(3, 1).value = "Item";
  ws.getCell(3, 2).value = "Lease path";
  ws.getCell(3, 3).value = "Own path";
  ws.getCell(3, 4).value = "Notes";
  [1, 2, 3, 4].forEach((col) => styleHeader(ws.getCell(3, col)));
  r = 4;

  section("INPUTS");
  row3(
    "School WACC",
    `${(lvo.schoolWacc * 100).toFixed(1)}%`,
    `${(lvo.schoolWacc * 100).toFixed(1)}%`,
    "RBA cash rate + ~200 bps education spread. Edit in Assumptions → Lease Incentive & School WACC.",
  );
  row3(
    "Lease term",
    `${a.leaseTermYears} years`,
    `${a.leaseTermYears} years`,
    "Own path: terminal value recovered at end of lease term.",
  );
  row3F(
    "Developer TDC (proxy capex)",
    null,
    "—",
    capexFormula,
    lvo.ownPathCapex,
    "Own path capex = developer total cost (simplified — paid at Y0).",
  );
  row3F(
    "NOI / cap rate (terminal value, net)",
    null,
    "—",
    terminalValFormula,
    lvo.ownPathTerminalValue,
    `Exit cap rate ${(a.capRate * 100).toFixed(2)}% applied to Y1 NOI, net of selling costs.`,
  );
  r++;

  section("NPV COMPARISON");
  const leasePvRow = row3F(
    "PV of rent outflows at WACC",
    leasePvFormula,
    lvo.leasePvAtWacc,
    null,
    "—",
    "Negative — cash outflow to operator over lease term. Recalculates with rent and WACC.",
  );
  row3F(
    "Capex (at Y0)",
    null,
    "—",
    `=-${capexFormula.slice(1)}`,
    -lvo.ownPathCapex,
    "Negative — one-off outlay at commencement.",
  );
  const terminalPvRow = row3F(
    "Terminal value PV at WACC",
    null,
    "—",
    terminalPvFormula,
    lvo.ownPathTerminalValuePv,
    `Discounted at ${(lvo.schoolWacc * 100).toFixed(1)}% over ${result.years.length} years (modelled horizon).`,
  );
  // ownPathNpv = -capex + terminalPv; use row references
  const capexRowNum = r - 2; // capex is 2 rows back
  const ownNpvRow = row3F(
    "NPV",
    leasePvFormula,
    lvo.leasePvAtWacc,
    `-'Dev Cost'!B${devCost.totalCostRow}+C${terminalPvRow}`,
    lvo.ownPathNpv,
    "",
  );
  row3F(
    "Delta (own path − lease path)",
    null,
    "—",
    `C${ownNpvRow}-B${leasePvRow}`,
    lvo.deltaOwningVsLeasing,
    lvo.deltaOwningVsLeasing > 0
      ? "Positive = owning appears cheaper at this WACC."
      : "Negative = leasing appears cheaper at this WACC.",
  );
  row3F(
    "Break-even capex (delta = 0)",
    null,
    "—",
    `C${terminalPvRow}-B${leasePvRow}`,
    lvo.impliedBreakEvenCapex,
    "Capex at which own and lease paths have equal NPV.",
  );
  r++;
  // suppress unused variable warning
  void capexRowNum;

  section("MANDATORY CAVEATS");
  for (const caveat of lvo.caveats) {
    const boardCaveat = caveat.replace("schoolWacc", "School WACC");
    ws.getCell(r, 1).value = `• ${boardCaveat}`;
    styleNote(ws.getCell(r, 1));
    ws.mergeCells(`A${r}:D${r}`);
    thinBorderRow(ws, r, 1, 4);
    r++;
  }

  ws.columns = [{ width: 40 }, { width: 18 }, { width: 18 }, { width: 62 }];
}

/* ── Builder Quotes sheet ── */
export function buildBuilderQuotes(wb: Workbook, scenario: Scenario) {
  const ws = wb.addWorksheet("Builder Quotes");
  ws.views = [{ state: "frozen", ySplit: 2 }];

  const quotes = scenario.builderQuotes ?? [];

  // Title
  ws.getCell(1, 1).value = "BUILDER QUOTES";
  ws.getCell(1, 1).font = {
    bold: true,
    size: 12,
    color: { argb: C.headerBg },
    name: "Arial",
  };
  ws.mergeCells("A1:J1");

  // Bid-dispersion caveat note
  ws.getCell(2, 1).value =
    "Project-specific class measures inter-bid dispersion only; it is not an AACE estimate class or absolute market-accuracy measure. D7 = rate alignment vs published benchmarks (0-5).";
  styleNote(ws.getCell(2, 1));
  ws.mergeCells("A2:J2");
  thinBorderRow(ws, 2, 1, 10);

  if (quotes.length === 0) {
    ws.getCell(2, 1).value =
      "No builder quotes loaded. Project-specific dispersion classes are not AACE estimate classes or absolute market-accuracy measures. D7 = rate alignment vs published benchmarks (0-5).";
    styleNote(ws.getCell(2, 1), C.mutedGrey);
    // NOTE: row 2 (A2:J2) is already merged unconditionally above (line ~4486)
    // for both branches. Re-merging here makes ExcelJS throw
    // "Cannot merge already merged cells" on the empty-quotes path.
    ws.columns = [
      { width: 22 },
      { width: 12 },
      { width: 16 },
      { width: 16 },
      { width: 14 },
      { width: 18 },
      { width: 14 },
      { width: 14 },
      { width: 14 },
      { width: 40 },
    ];
    return;
  }

  // Headers
  const headers = [
    "Builder",
    "Bid date",
    "Reported TPC",
    "LFL TPC",
    "Design excluded",
    "Contingency excluded",
    "Exclusions count",
    "D7 benchmark",
    "Flag",
    "Notes",
  ];
  headers.forEach((h, i) => {
    const cell = ws.getCell(3, i + 1);
    cell.value = h;
    styleHeader(cell);
  });

  quotes.forEach((q, ri) => {
    const exclusions = q.exclusions ?? [];
    const row = ws.getRow(4 + ri);
    row.getCell(1).value = q.builderName;
    row.getCell(2).value = q.bidDate;
    row.getCell(3).value = q.reportedTpc;
    row.getCell(3).numFmt = NF.currency;
    // LFL TPC computed — must match builder-quotes.ts:computeLflTpc()
    let lfl = q.reportedTpc;
    if (q.excludedDesignFees) {
      lfl += q.reportedTpc * (q.excludedDesignFeesPct ?? 0.045);
    }
    if (q.excludedContingencyPct) {
      lfl += q.reportedTpc * q.excludedContingencyPct;
    }
    for (const ex of exclusions) {
      if (ex.status === "excluded" && Number.isFinite(ex.dollarAmount ?? 0)) {
        // Skip dollar-amount exclusions already covered by percentage adjustments to avoid double-counting.
        if (q.excludedDesignFees && ex.itemKey === "design_fees") continue;
        if (q.excludedContingencyPct && ex.itemKey === "contingency") continue;
        lfl += ex.dollarAmount ?? 0;
      }
    }
    row.getCell(4).value = lfl;
    row.getCell(4).numFmt = NF.currency;
    row.getCell(5).value = q.excludedDesignFees ? "Yes" : "No";
    row.getCell(6).value = q.excludedContingencyPct
      ? `${(q.excludedContingencyPct * 100).toFixed(1)}%`
      : "—";
    row.getCell(7).value = exclusions.filter(
      (e) => e.status === "excluded",
    ).length;
    const documentation = q.scoreDocumentationDepth;
    row.getCell(8).value =
      documentation?.D7_rateAlignment == null
        ? "Not scored"
        : documentation.D7_rateAlignment;
    row.getCell(9).value =
      documentation?.preliminaryFlag === "no_rlb_benchmark"
        ? "No benchmark"
        : documentation?.preliminaryFlag === "no_audit"
          ? "No audit"
          : "—";
    row.getCell(10).value = q.notes ?? "";
    row.getCell(10).alignment = { wrapText: true };

    for (let c = 1; c <= 10; c++) {
      thinBorder(row.getCell(c));
      if (c >= 3 && c <= 4) row.getCell(c).alignment = { horizontal: "right" };
    }
  });

  ws.columns = [
    { width: 22 },
    { width: 12 },
    { width: 16 },
    { width: 16 },
    { width: 14 },
    { width: 18 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 40 },
  ];
}

/* ── Action Items sheet ── */
export function buildActionItems(
  wb: Workbook,
  scenario: Scenario,
  result: ModelResult,
) {
  const ws = wb.addWorksheet("Action Items");
  ws.views = [{ state: "frozen", ySplit: 2 }];

  const items = scenario.actionItems ?? [];
  const risks = buildDealRisks(scenario, result);

  // Title
  ws.getCell(1, 1).value = "BOARD RISK AND ACTION REGISTER";
  ws.getCell(1, 1).font = {
    bold: true,
    size: 12,
    color: { argb: C.headerBg },
    name: "Arial",
  };
  ws.mergeCells("A1:I1");

  // Headers
  const headers = [
    "Risk ID",
    "Risk / action",
    "Proposed owner",
    "Target date / decision gate",
    "Current control / evidence",
    "Provisional residual",
    "Escalation trigger",
    "Proposed action",
    "Status",
  ];
  headers.forEach((h, i) => {
    const cell = ws.getCell(2, i + 1);
    cell.value = h;
    styleHeader(cell);
  });

  risks.forEach((risk, ri) => {
    const row = ws.getRow(3 + ri);
    row.getCell(1).value = risk.id;
    row.getCell(2).value = risk.name;
    row.getCell(3).value = risk.proposedOwner;
    row.getCell(4).value = risk.dueGate;
    row.getCell(5).value = risk.currentControl;
    row.getCell(6).value = risk.residualRating;
    row.getCell(7).value = risk.escalationTrigger;
    row.getCell(8).value = risk.mitigation;
    row.getCell(9).value = risk.status;

    for (let c = 1; c <= 9; c++) {
      thinBorder(row.getCell(c));
      row.getCell(c).alignment = { wrapText: true, vertical: "top" };
    }
  });

  let nextRow = 3 + risks.length + 1;
  ws.getCell(nextRow, 1).value =
    "MODEL-GENERATED DILIGENCE PROMPTS — NOT SITE FINDINGS";
  styleNote(ws.getCell(nextRow, 1), C.warnAmberText);
  ws.mergeCells(`A${nextRow}:I${nextRow}`);
  nextRow++;

  const promptHeaders = [
    "Prompt ID",
    "Severity",
    "Prompt",
    "Evidence status",
    "Cost treatment",
    "Cost band low",
    "Cost band high",
    "Affected buildings",
    "Source",
  ];
  promptHeaders.forEach((header, index) => {
    const cell = ws.getCell(nextRow, index + 1);
    cell.value = header;
    styleHeader(cell);
  });
  nextRow++;

  if (items.length === 0) {
    ws.getCell(nextRow, 1).value =
      "No model-generated diligence prompts are stored for this scenario.";
    styleNote(ws.getCell(nextRow, 1), C.mutedGrey);
    ws.mergeCells(`A${nextRow}:I${nextRow}`);
  } else {
    items.forEach((item, index) => {
      const row = ws.getRow(nextRow + index);
      row.getCell(1).value = item.id;
      row.getCell(2).value = item.severity;
      row.getCell(3).value = `${item.title}: ${item.description}`;
      row.getCell(4).value =
        "Evidence status: unverified — no site-specific report is linked.";
      row.getCell(5).value =
        "Cost treatment: unverified — not separately added unless reconciled; may be covered only by contingency.";
      row.getCell(6).value = item.estimatedCostBandLow;
      row.getCell(6).numFmt = NF.currency;
      row.getCell(7).value = item.estimatedCostBandHigh;
      row.getCell(7).numFmt = NF.currency;
      row.getCell(8).value = (item.affectsBuildings ?? []).join(", ") || "All";
      row.getCell(9).value = item.source ?? "";
      for (let c = 1; c <= 9; c++) {
        thinBorder(row.getCell(c));
        row.getCell(c).alignment = { wrapText: true, vertical: "top" };
      }
    });
  }

  ws.columns = [
    { width: 10 },
    { width: 26 },
    { width: 24 },
    { width: 34 },
    { width: 42 },
    { width: 14 },
    { width: 34 },
    { width: 42 },
    { width: 24 },
  ];
}

/* ── Charts sheet (PNG images) ── */
export async function buildChartsSheet(
  wb: Workbook,
  result: ModelResult,
  scenario: Scenario,
) {
  const ws = wb.addWorksheet("Charts");
  // Freeze the title row so the chart captions stay visible when scrolling.
  ws.views = [{ state: "frozen", xSplit: 0, ySplit: 1 }];

  let currentRow = 0;

  // Helper to embed an SVG as PNG image
  const embedSvg = async (svg: SVGSVGElement, title: string, row: number) => {
    try {
      const pngDataUrl = await svgElementToPngDataUrl(svg, 2);
      if (pngDataUrl && pngDataUrl.startsWith("data:image/png;base64,")) {
        const base64 = pngDataUrl.replace("data:image/png;base64,", "");
        const imageId = wb.addImage({ base64, extension: "png" });
        ws.addImage(imageId, {
          tl: {
            col: 0,
            row,
            nativeCol: 0,
            nativeColOff: 0,
            nativeRow: row,
            nativeRowOff: 0,
          } as any,
          ext: { width: 640, height: 320 },
        });
        return 18; // image occupies ~18 rows at default row height
      }
    } catch {
      // PNG generation failed — add text placeholder
    }
    ws.getCell(row + 1, 1).value = `[${title} — image generation unavailable]`;
    ws.getCell(row + 1, 1).font = {
      italic: true,
      color: { argb: "8a94a6" },
      name: "Arial",
      size: 10,
    };
    return 2;
  };

  // Title
  ws.getCell(1, 1).value = "CHARTS";
  ws.getCell(1, 1).font = {
    bold: true,
    size: 14,
    color: { argb: C.headerBg },
    name: "Arial",
  };
  ws.mergeCells("A1:J1");
  currentRow = 2;

  // 1. Cashflow chart
  ws.getCell(currentRow + 1, 1).value =
    "Cashflow — EBITDA bars + cumulative line";
  ws.getCell(currentRow + 1, 1).font = {
    bold: true,
    size: 11,
    color: { argb: C.formulaBlack },
    name: "Arial",
  };
  currentRow += 2;
  const cfSvg = renderCashflowSvg(result.years, 640, 320);
  currentRow += await embedSvg(cfSvg, "Cashflow chart", currentRow);

  // 2. P&L chart
  currentRow += 1;
  ws.getCell(currentRow + 1, 1).value = "P&L bridge — Revenue, Rent, EBITDA";
  ws.getCell(currentRow + 1, 1).font = {
    bold: true,
    size: 11,
    color: { argb: C.formulaBlack },
    name: "Arial",
  };
  currentRow += 2;
  const plSvg = renderPnLSvg(result.years, 640, 320);
  currentRow += await embedSvg(plSvg, "P&L chart", currentRow);

  // 3a. Tornado chart — GEARED Developer IRR at the selected primary horizon.
  currentRow += 1;
  const exitYear =
    scenario.assumptions.developerExitYear ?? scenario.assumptions.modelYears;
  const earlyExit = exitYear < scenario.assumptions.modelYears;
  const primaryGearedMetric = earlyExit
    ? "developerGearedEarlyExitIrr"
    : "developerGearedIrr";
  ws.getCell(currentRow + 1, 1).value = earlyExit
    ? `Tornado sensitivity — Developer IRR (geared, project Y${exitYear} early exit)`
    : "Tornado sensitivity — Developer IRR (geared, sale at completion)";
  ws.getCell(currentRow + 1, 1).font = {
    bold: true,
    size: 11,
    color: { argb: C.formulaBlack },
    name: "Arial",
  };
  currentRow += 2;
  const gearedRows = Model.tornado(scenario, primaryGearedMetric);
  const gearedBase = result.kpis.developerPrimaryGearedIrr;
  const pctFormat = (value: number) =>
    Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : "—";
  const ppFormat = (value: number) =>
    Number.isFinite(value) ? `${(value * 100).toFixed(1)} pp` : "—";
  if (gearedRows.length > 0 && Number.isFinite(gearedBase)) {
    const rowHeight = Math.max(
      20,
      Math.min(36, Math.floor(280 / gearedRows.length)),
    );
    const gearedSvg = renderTornadoSvg(
      gearedRows,
      gearedBase,
      640,
      rowHeight,
      pctFormat,
      undefined,
      ppFormat,
    );
    currentRow += await embedSvg(
      gearedSvg,
      "Tornado chart (geared)",
      currentRow,
    );
  } else {
    ws.getCell(currentRow + 1, 1).value =
      "[Geared tornado chart — insufficient data]";
    ws.getCell(currentRow + 1, 1).font = {
      italic: true,
      color: { argb: "8a94a6" },
      name: "Arial",
      size: 10,
    };
    currentRow += 1;
  }

  // 3b. Tornado chart — UNGEARED / project Developer IRR (carries an artifact caveat).
  currentRow += 1;
  ws.getCell(currentRow + 1, 1).value =
    "Tornado sensitivity — Developer IRR (ungeared / project)";
  ws.getCell(currentRow + 1, 1).font = {
    bold: true,
    size: 11,
    color: { argb: C.formulaBlack },
    name: "Arial",
  };
  currentRow += 2;
  const ungearedRows = Model.tornado(scenario, "developerIrr");
  const ungearedBase = result.kpis.developerIrr;
  if (ungearedRows.length > 0 && Number.isFinite(ungearedBase)) {
    const rowHeight = Math.max(
      20,
      Math.min(36, Math.floor(280 / ungearedRows.length)),
    );
    const ungearedSvg = renderTornadoSvg(
      ungearedRows,
      ungearedBase,
      640,
      rowHeight,
      pctFormat,
      undefined,
      ppFormat,
    );
    currentRow += await embedSvg(
      ungearedSvg,
      "Tornado chart (ungeared)",
      currentRow,
    );
  } else {
    ws.getCell(currentRow + 1, 1).value =
      "[Ungeared tornado chart — insufficient data]";
    ws.getCell(currentRow + 1, 1).font = {
      italic: true,
      color: { argb: "8a94a6" },
      name: "Arial",
      size: 10,
    };
    currentRow += 1;
  }
  // Caveat under the ungeared chart.
  currentRow += 1;
  ws.getCell(currentRow + 1, 1).value =
    "Project IRR books the full sale at practical completion and ignores the modelled exit year — read as relative driver sensitivity, not an absolute return.";
  ws.getCell(currentRow + 1, 1).font = {
    italic: true,
    size: 9,
    color: { argb: C.formulaBlack },
    name: "Arial",
  };

  ws.columns = [{ width: 80 }];
}

export { buildWorkbook, exportExcel } from "./excel/workbook";
